import { Shield, Droplets, Bot, EyeOff, Server, CandlestickChart, ShieldCheck, Vault, KeyRound, Network, FileCode2, Cpu, Coins, Image, Landmark, Repeat, Shuffle, MessageSquareText, Radar, ArrowLeftRight, Workflow, Layers, Scale, Ship, Milestone, Wind, BadgeCheck, UserCog, FileSignature, Waves, Cake, Route, Cog, Radio, Waypoints } from 'lucide-react';
import { protocolDisplayName, protocolCategory, humanizeFieldName } from '@authe/fe-commons/metadata-analytics';
export { humanizeFieldName } from '@authe/fe-commons/metadata-analytics';
import { publicAssetUrl } from '@authe/fe-commons/assets';
import { resolveScalar } from '@authe/fe-commons/labels';
import * as React from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';
import { cn, ReadConfidenceBoundary, DecodedCall, RawToggle, DecodedValue, DecodedParamList, ClaimEvidence } from '@authe/ui-core';
import { decodedOf, rawOf, isEnriched, parseDecodedCallValue, kindOf } from '@authe/fe-commons/storage';
import { chainDecodeVocabulary } from '@authe/fe-commons/chains';

// src/registry/registry.ts
var ProtocolUiRegistry = class {
  valueComponents = /* @__PURE__ */ new Map();
  eventComponents = /* @__PURE__ */ new Map();
  callComponents = /* @__PURE__ */ new Map();
  callOwnsReturnsIds = /* @__PURE__ */ new Set();
  valueHints = [];
  eventHints = [];
  callHints = [];
  /** Register a value component under its `componentId` (from a `DisplayHint`). */
  registerValue(componentId, component) {
    this.valueComponents.set(componentId, component);
    return this;
  }
  /** Register an event component under its `componentId` (from an `EventViewHint`). */
  registerEvent(componentId, component) {
    this.eventComponents.set(componentId, component);
    return this;
  }
  /** Register a call component under its `componentId` (from a `CallViewHint`). */
  registerCall(componentId, component, opts) {
    this.callComponents.set(componentId, component);
    if (opts?.ownsReturns) this.callOwnsReturnsIds.add(componentId);
    else this.callOwnsReturnsIds.delete(componentId);
    return this;
  }
  /**
   * Register a bundled protocol's DEFAULT render hints — the FE-side mirror of
   * its backend `display_hints` / `event_views` / `call_views`. Used as a
   * fallback (merged under the body's hints) so bundled cards resolve even when
   * a body carries no `extras.render_hints`.
   */
  registerHints(hints) {
    if (hints.valueHints) this.valueHints.push(...hints.valueHints);
    if (hints.eventHints) this.eventHints.push(...hints.eventHints);
    if (hints.callHints) this.callHints.push(...hints.callHints);
    return this;
  }
  /** The accumulated default hints for every registered bundled protocol. */
  defaultHints() {
    return {
      valueHints: [...this.valueHints],
      eventHints: [...this.eventHints],
      callHints: [...this.callHints]
    };
  }
  /** The value component for a `componentId`, or `undefined` when unregistered. */
  valueComponent(componentId, kind) {
    if (componentId === "GenericStruct" && kind?.includes(".")) {
      const owner = kind.split(".")[0];
      const bundled = this.valueHints.find((h) => h.kind === kind && h.protocolId === owner && h.component !== "GenericStruct");
      if (bundled && this.valueComponents.has(bundled.component)) return this.valueComponents.get(bundled.component);
    }
    return componentId ? this.valueComponents.get(componentId) : void 0;
  }
  /** The event component for a `componentId`, or `undefined` when unregistered. */
  eventComponent(componentId) {
    return componentId ? this.eventComponents.get(componentId) : void 0;
  }
  /** The call component for a `componentId`, or `undefined` when unregistered. */
  callComponent(componentId) {
    return componentId ? this.callComponents.get(componentId) : void 0;
  }
  /**
   * True when the call card for `componentId` renders the decoded return value
   * itself (registered with `{ ownsReturns: true }`) — the view layer skips the
   * shared "Returns" footer for it to avoid showing the return twice.
   */
  callOwnsReturns(componentId) {
    return !!componentId && this.callOwnsReturnsIds.has(componentId);
  }
  /** True when a value component is registered for `componentId`. */
  hasValue(componentId) {
    return !!componentId && this.valueComponents.has(componentId);
  }
  /** True when an event component is registered for `componentId`. */
  hasEvent(componentId) {
    return !!componentId && this.eventComponents.has(componentId);
  }
  /** True when a call component is registered for `componentId`. */
  hasCall(componentId) {
    return !!componentId && this.callComponents.has(componentId);
  }
};
function createProtocolUiRegistry() {
  return new ProtocolUiRegistry();
}

// src/theme/tokens.ts
var PROTOCOL_CARD = "rounded-lg border border-border bg-card";
var PROTOCOL_CARD_HEADER = "flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border/60 px-3 py-2";
var PROTOCOL_CARD_BODY = "px-3 py-2.5";
var PROTOCOL_TAG = "inline-flex items-center gap-1 rounded-sm bg-muted px-1.5 py-px text-2xs font-medium text-muted-foreground";
var FACT_LABEL = "text-2xs font-medium uppercase tracking-wide text-muted-foreground";
var FACT_VALUE = "min-w-0 break-words text-sm";
var ACCENT_TEXT = {
  neutral: "text-foreground",
  bridge: "text-category-blue",
  oracle: "text-category-violet",
  danger: "text-risk-high"
};

// src/theme/brand/accents.ts
var ACCENTS = {
  blue: {
    text: "text-category-blue",
    badge: "bg-category-blue/10 text-category-blue",
    border: "border-category-blue/60",
    bar: "border-l-category-blue",
    dot: "bg-category-blue"
  },
  violet: {
    text: "text-category-violet",
    badge: "bg-category-violet/10 text-category-violet",
    border: "border-category-violet/60",
    bar: "border-l-category-violet",
    dot: "bg-category-violet"
  },
  indigo: {
    text: "text-category-indigo",
    badge: "bg-category-indigo/10 text-category-indigo",
    border: "border-category-indigo/60",
    bar: "border-l-category-indigo",
    dot: "bg-category-indigo"
  },
  cyan: {
    text: "text-category-cyan",
    badge: "bg-category-cyan/10 text-category-cyan",
    border: "border-category-cyan/60",
    bar: "border-l-category-cyan",
    dot: "bg-category-cyan"
  },
  teal: {
    text: "text-category-teal",
    badge: "bg-category-teal/10 text-category-teal",
    border: "border-category-teal/60",
    bar: "border-l-category-teal",
    dot: "bg-category-teal"
  },
  amber: {
    text: "text-category-amber",
    badge: "bg-category-amber/10 text-category-amber",
    border: "border-category-amber/60",
    bar: "border-l-category-amber",
    dot: "bg-category-amber"
  },
  emerald: {
    text: "text-category-emerald",
    badge: "bg-category-emerald/10 text-category-emerald",
    border: "border-category-emerald/60",
    bar: "border-l-category-emerald",
    dot: "bg-category-emerald"
  },
  rose: {
    text: "text-category-rose",
    badge: "bg-category-rose/10 text-category-rose",
    border: "border-category-rose/60",
    bar: "border-l-category-rose",
    dot: "bg-category-rose"
  },
  pink: {
    text: "text-category-pink",
    badge: "bg-category-pink/10 text-category-pink",
    border: "border-category-pink/60",
    bar: "border-l-category-pink",
    dot: "bg-category-pink"
  },
  orange: {
    text: "text-category-orange",
    badge: "bg-category-orange/10 text-category-orange",
    border: "border-category-orange/60",
    bar: "border-l-category-orange",
    dot: "bg-category-orange"
  },
  neutral: {
    text: "text-muted-foreground",
    badge: "bg-muted text-muted-foreground",
    border: "border-border",
    bar: "border-l-border",
    dot: "bg-muted-foreground"
  }
};
var CATEGORY_ACCENT = {
  bridge: "blue",
  oracle: "violet",
  messaging: "cyan",
  aggregator: "teal",
  // DEX / AMM swaps + liquidity (Uniswap & friends) — a hot pink reads as the
  // canonical "swap" colour (Uniswap's own brand) and stays distinct from the
  // teal aggregator routers that wrap them.
  exchange: "pink",
  // Token standards: fungible value reads green; NFTs read amber; the native
  // coin gets its own teal so it stays distinct from an ERC20 (emerald) on a
  // tx that moves both.
  token: "emerald",
  nft: "amber",
  native: "teal",
  // EVM precompiles read as low-level infra — a cool indigo keeps them distinct
  // from the warm token cards without competing for "protocol" colour weight.
  precompile: "indigo",
  // A deployment is low-level infra like a precompile — it puts code on the chain
  // rather than performing a protocol action — so it shares that cool indigo
  // instead of competing with the protocol-action palette.
  deployment: "indigo",
  // Proxy plumbing is transparent infrastructure (it delegates to the real
  // implementation) — neutral grey reads as "structure", not a protocol action,
  // and keeps these frequent cards visually quiet.
  proxy: "neutral",
  // Ownership / access-control is a governance concern, not plumbing — a royal
  // violet reads as "authority" and lifts an `owner()`/`OwnershipTransferred`
  // out of the neutral proxy greys it used to share.
  ownership: "violet",
  // Role-based access control + pausable circuit-breakers — adjacent to ownership
  // but distinct: a warm rose reads as "permissioned action" without colliding
  // with single-owner violet or the warm-amber NFT cards.
  access: "rose",
  // ERC4626 vaults move underlying assets in/out for shares — a cyan keeps the
  // vault distinct from the ERC20 (emerald) it custodies on the same tx.
  vault: "cyan",
  // Lending shares the vault cyan, deliberately. Supplying to Aave is the same
  // reading as depositing to a vault — an asset goes in and a claim on it comes
  // back — and the aToken IS that claim. The debt side is what a vault lacks,
  // but it is the same position seen from the other end, so splitting the hue
  // would separate a story that belongs together. Cyan also keeps a lending
  // card clear of the two things it most often sits beside: the pink of the
  // swap that funded it and the emerald of the ERC20 transfers beneath it.
  lending: "cyan",
  // Smart accounts (Safe & friends): the same authority violet as `ownership`, on
  // purpose. Both answer "who controls this" — a multisig account and an
  // `OwnershipTransferred` are one concern seen from two sides — and giving the
  // account its own hue would split a reading that belongs together.
  safe: "violet",
  // A position whose value derives from something else — prediction-market
  // outcome shares today. Amber, shared with NFTs on purpose: both are holdings
  // whose worth is a claim rather than a balance, and a prediction position is
  // nearer that reading than it is to the pink of a spot venue.
  derivatives: "amber",
  // The chain's own programs and plumbing (Solana's System and Compute Budget
  // programs, a HyperEVM system contract): the cool indigo of the precompile and
  // deployment cards, which are the same kind of thing on EVM.
  infrastructure: "indigo",
  // Shielded pools and ring signatures (Zcash, Monero): what the transaction does
  // NOT show is the finding, so it gets a hue nothing else uses.
  privacy: "orange",
  // A searcher's own program (an arbitrage bot) — not the venue it trades
  // against, so deliberately NOT the exchange pink: rose reads as "someone
  // else's automation acted here", the same attention the access cards ask for.
  mev: "rose",
  neutral: "neutral"
};
var CATEGORY_ICON = {
  bridge: ArrowLeftRight,
  oracle: Radar,
  messaging: MessageSquareText,
  aggregator: Shuffle,
  exchange: Repeat,
  lending: Landmark,
  token: Coins,
  nft: Image,
  native: Coins,
  precompile: Cpu,
  deployment: FileCode2,
  proxy: Network,
  ownership: KeyRound,
  access: Shield,
  vault: Vault,
  safe: ShieldCheck,
  derivatives: CandlestickChart,
  infrastructure: Server,
  privacy: EyeOff,
  mev: Bot,
  neutral: void 0
};
var CATALOG_BRAND_CATEGORY = {
  bridge: "bridge",
  exchange: "exchange",
  aggregator: "aggregator",
  lending: "lending",
  flashloan: "lending",
  derivatives: "derivatives",
  safe: "safe",
  account_abstraction: "safe",
  governance: "ownership",
  oracle: "oracle",
  infrastructure: "infrastructure",
  erc20: "token",
  nft: "nft",
  staking: "vault",
  // A searcher's own program: it CALLS the venues rather than being one, so it
  // reads as itself and never as the exchange it traded against.
  mev: "mev",
  // An exploit is a finding about a transaction, not a venue's colour.
  exploit: "neutral",
  privacy: "privacy",
  // A casino's game (Ape Church): the house is the counterparty and the outcome
  // is random, which no venue colour describes — neutral rather than borrowing
  // the exchange pink of a market it is not.
  gambling: "neutral",
  other: "neutral"
};
var DERIVED = /* @__PURE__ */ new Map();
function cataloguedBrand(protocolId) {
  const cached = DERIVED.get(protocolId);
  if (cached !== void 0) return cached ?? void 0;
  const displayName = protocolDisplayName(protocolId);
  const catalogued = protocolCategory(protocolId);
  const category = catalogued ? CATALOG_BRAND_CATEGORY[catalogued] : void 0;
  const brand = displayName && category ? { id: protocolId, displayName, category, icon: CATEGORY_ICON[category] } : null;
  DERIVED.set(protocolId, brand);
  return brand ?? void 0;
}

// src/protocols/chainlink_ccip/labels.ts
var CHAINLINK_CCIP = {
  protocolId: "chainlink_ccip",
  displayName: "Chainlink CCIP",
  docs: "https://docs.chain.link/ccip"
};
var CCIP_COMPONENTS = {
  /** Value component for the namespaced `chainlink_ccip.curse_subject` kind. */
  curseSubject: "CcipCurseSubject",
  /** Event card for `CCIPSendRequested` / `CCIPMessageSent`. */
  messageSent: "CcipMessageSent",
  /** Call card for `ccipSend` (the OnRamp send entrypoint). */
  send: "CcipSend",
  /** Call card for the OffRamp receive entrypoints (`execute` / `transmit`). */
  executionReport: "CcipExecutionReport",
  /** Value component for a CCV `receipts[]` issuer role badge. */
  receiptRole: "CcipReceiptRole",
  /** Value component for the CCV verification roll-up (verifiers + executor). */
  verification: "CcipVerification",
  /** Value component for the `token_amounts` transfer-list highlight. */
  tokenAmounts: "CcipTokenAmounts"
};
var CCIP_CURSE_SUBJECT_KIND = "chainlink_ccip.curse_subject";
var CCIP_RECEIPT_ROLE_KIND = "chainlink_ccip.receipt_role";
var CCIP_VERIFICATION_KIND = "chainlink_ccip.verification";
var CCIP_TOKEN_TRANSFERS_KIND = "token_transfers";
var CCIP_MESSAGE_ID_FACT = "message_id";
var CCIP_FIELD_LABELS = {
  messageId: "Message ID",
  sender: "Sender",
  receiver: "Receiver",
  recipient: "Receiver",
  sourceChainSelector: "Source chain",
  destChainSelector: "Destination chain",
  destinationChainSelector: "Destination chain",
  feeToken: "Fee token",
  feeTokenAmount: "Fee",
  gasLimit: "Gas limit",
  sequenceNumber: "Sequence",
  nonce: "Nonce",
  tokenAmounts: "Token transfers",
  data: "Payload",
  contract: "Router",
  extraArgs: "Extra args",
  allowOutOfOrderExecution: "Out-of-order execution",
  verification: "Verification",
  receipts: "Fee receipts",
  verifierBlobs: "Verifier attestations"
};
var CCIP_RECEIPT_ROLE_LABELS = {
  verifier: "Verifier",
  executor: "Executor",
  onramp: "OnRamp / Router fee",
  token_pool: "Token pool fee",
  participant: "Participant"
};

// src/protocols/chainlink_automation/labels.ts
var CHAINLINK_AUTOMATION = {
  protocolId: "chainlink_automation",
  displayName: "Chainlink Automation",
  docs: "https://docs.chain.link/chainlink-automation"
};
var AUTOMATION_COMPONENTS = {
  /** Call card for the OCR `transmit` that delivers an `AutomationReport`. */
  transmit: "ChainlinkAutomationTransmit"
};
var AUTOMATION_FIELD_LABELS = {
  registry: "Registry",
  fastGasWei: "Fast gas (wei)",
  linkNative: "LINK / native",
  upkeepId: "Upkeep ID",
  gasLimit: "Gas limit",
  trigger: "Trigger",
  performData: "Perform data"
};

// src/protocols/cowswap/labels.ts
var COWSWAP = {
  protocolId: "cowswap",
  displayName: "CoW Swap",
  docs: "https://docs.cow.fi"
};
var COWSWAP_COMPONENTS = {
  /** The packed 56-byte order uid, as a CoW Explorer deep link. */
  orderUid: "CowOrderUid",
  /** A trade's unpacked flag bitfield, as chips. */
  tradeFlags: "CowTradeFlags",
  /** An expanded order struct. */
  order: "CowOrder",
  /** The off-chain app-data document resolved from its on-chain digest. */
  appData: "CowAppData",
  /** Call cards. */
  settle: "CowSettle",
  createOrder: "CowCreateOrder",
  invalidateOrders: "CowInvalidateOrders",
  /** Event cards. */
  trade: "CowTrade",
  settlement: "CowSettlement",
  interactionLog: "CowInteractionLog",
  orderPlacement: "CowOrderPlacement",
  orderRefund: "CowOrderRefund"
};
var COWSWAP_KINDS = {
  orderUid: "cowswap.order_uid",
  tradeFlags: "cowswap.trade_flags",
  order: "cowswap.order",
  appData: "cowswap.app_data"
};
var COWSWAP_CALLS = {
  settle: "settle",
  createOrder: "createOrder",
  invalidateOrders: "invalidateOrdersIgnoringNotAllowed"
};
var COWSWAP_EVENTS = {
  trade: "Trade",
  settlement: "Settlement",
  interaction: "Interaction",
  orderPlacement: "OrderPlacement",
  orderRefund: "OrderRefund"
};
var COWSWAP_FIELD_LABELS = {
  appCode: "Placed via",
  orderClass: "Order class",
  partnerFeeBps: "Partner fee (bps)",
  slippageBps: "Slippage (bps)",
  digest: "App-data digest",
  source: "Document source"
};

// src/protocols/kyberswap/labels.ts
var KYBERSWAP = {
  protocolId: "kyberswap_meta_aggregator",
  displayName: "KyberSwap",
  docs: "https://docs.kyberswap.com"
};
var KYBERSWAP_COMPONENTS = {
  /** The integrator's own JSON, carried on-chain beside the trade. */
  clientData: "KyberSwapClientData",
  swap: "KyberSwapSwap",
  swapped: "KyberSwapSwapped",
  exchange: "KyberSwapExchange",
  fee: "KyberSwapFee",
  error: "KyberSwapError"
};
var KYBERSWAP_KINDS = {
  clientData: "kyberswap.client_data"
};
var KYBERSWAP_CALLS = {
  swap: "swap"
};
var KYBERSWAP_EVENTS = {
  swapped: "Swapped",
  exchange: "Exchange",
  fee: "Fee",
  error: "Error"
};
var KYBERSWAP_CLIENT_DATA_FIELDS = {
  source: "source",
  amountInUsd: "amountInUsd",
  amountOutUsd: "amountOutUsd",
  quotedAmountOut: "quotedAmountOut",
  routeId: "routeId"
};
var KYBERSWAP_FIELD_LABELS = {
  source: "Integrator",
  amountInUsd: "Value in (USD)",
  amountOutUsd: "Value out (USD)",
  quotedAmountOut: "Quoted output",
  routeId: "Route ID"
};

// src/protocols/lifi/labels.ts
var LIFI = {
  protocolId: "lifi",
  displayName: "LI.FI",
  docs: "https://docs.li.fi"
};
var LIFI_COMPONENTS = {
  /** Call card for the LI.FI Diamond Mayan-facet bridge entrypoints (swap + no-swap). */
  bridge: "LiFiBridge"
};
var LIFI_BRIDGE_CALLS = [
  "swapAndStartBridgeTokensViaMayan",
  "startBridgeTokensViaMayan"
];
var LIFI_FIELD_LABELS = {
  // BridgeData envelope.
  bridge: "Bridge",
  bridgedToken: "Bridged token",
  minAmount: "Bridged amount",
  recipient: "Recipient",
  destChain: "Destination chain",
  transactionId: "Transfer ID",
  // Source DEX swap (SwapData[0]).
  fromToken: "From token",
  fromAmount: "From amount",
  toToken: "To token",
  swapRouter: "Swap router",
  // MayanData (the wrapped bridge).
  mayanProtocol: "Mayan protocol"
};

// src/protocols/mayan/labels.ts
var MAYAN = {
  protocolId: "mayan",
  displayName: "Mayan",
  docs: "https://docs.mayan.finance"
};
var MAYAN_COMPONENTS = {
  /** Call card for the shared Forwarder front door (`forward*` / `swapAndForward*`). */
  forwarder: "MayanForwarder",
  /** Call card for a Swift order verb (`createOrderWith*` / `fulfill*` / `unlock*`). */
  swiftOrder: "MayanSwiftOrder",
  /** Call card for the Swift v2 driver batch executor (`executeAndRescue`). */
  executor: "MayanExecuteAndRescue",
  /** Event card for a Swift order-lifecycle event (`OrderCreated` / `OrderFulfilled` / …). */
  orderEvent: "MayanOrderEvent",
  /** Value card for the namespaced `mayan.packed_payload` kind. */
  packedPayload: "MayanPackedPayload",
  /** Value card for the namespaced `mayan.swift_message` kind (Swift VAA payload). */
  swiftMessage: "MayanSwiftMessage"
};
var MAYAN_PACKED_PAYLOAD_KIND = "mayan.packed_payload";
var MAYAN_SWIFT_MESSAGE_KIND = "mayan.swift_message";
var MAYAN_FIELD_LABELS = {
  contract: "Contract",
  // Swift VAA message (the `encodedVm` payload an unlock / fulfill / refund consumes).
  action: "Action",
  driver: "Driver",
  referrerBps: "Referrer fee (bps)",
  protocolBps: "Protocol fee (bps)",
  fulfillTime: "Fulfilled at",
  // Forwarder envelope.
  tokenIn: "Input token",
  amountIn: "Input amount",
  middleToken: "Bridged token",
  minMiddleAmount: "Min bridged amount",
  mayanProtocol: "Mayan protocol",
  swapProtocol: "Swap router",
  // Swift order intent.
  trader: "Trader",
  recipient: "Recipient",
  token: "Token",
  tokenOut: "Delivered token",
  amount: "Amount",
  minAmountOut: "Min received",
  destChain: "Destination chain",
  sourceChain: "Source chain",
  deadline: "Deadline",
  referrer: "Referrer",
  gasDrop: "Gas drop",
  orderHash: "Order hash",
  // Swift v2 driver `executeAndRescue` batch.
  calls: "Calls",
  target: "Target",
  value: "Value",
  callData: "Call",
  rescueReceiver: "Rescue receiver",
  rescueTokens: "Rescued tokens"
};
var MAYAN_FORWARDER_CALLS = [
  "forwardEth",
  "forwardERC20",
  "swapAndForwardEth",
  "swapAndForwardERC20"
];
var MAYAN_SWIFT_CALLS = [
  "createOrderWithEth",
  "createOrderWithToken",
  "fulfillOrder",
  "fulfillSimple",
  "unlockSingle",
  "unlockBatch"
];
var MAYAN_EXECUTOR_CALLS = ["executeAndRescue"];
var MAYAN_ORDER_EVENTS = [
  "OrderCreated",
  "OrderFulfilled",
  "OrderUnlocked",
  "OrderCanceled",
  "OrderRefunded"
];

// src/protocols/okx_xbridge/labels.ts
var OKX_XBRIDGE = {
  protocolId: "okx_xbridge",
  displayName: "OKX XBridge",
  docs: "https://www.okx.com/web3/build/docs/waas/dex-crosschain-api"
};
var OKX_COMPONENTS = {
  /** Call card for the XBridge swap-and-bridge entrypoints (single + batch). */
  swap: "OkxXBridgeSwap",
  /** Value card for the namespaced `okx_xbridge.packed_payload` kind. */
  packedPayload: "OkxPackedPayload"
};
var OKX_PACKED_PAYLOAD_KIND = "okx_xbridge.packed_payload";
var OKX_FIELD_LABELS = {
  // Top-level entrypoint.
  refId: "Reference ID",
  // BridgeRequest leg.
  token: "Bridged token",
  amount: "Input amount",
  minReturnAmount: "Bridged amount",
  recipient: "Recipient",
  destChain: "Destination chain",
  fee: "Fee recipient",
  // Inner OKX DexRouter swap.
  swapMethod: "Swap method",
  fromToken: "From token",
  fromTokenAmount: "From amount",
  toToken: "To token",
  receiver: "Swap receiver"
};

// src/protocols/oneinch/labels.ts
var ONEINCH = {
  protocolId: "oneinch",
  displayName: "1inch",
  docs: "https://docs.1inch.io"
};
var ONEINCH_COMPONENTS = {
  /** An address unpacked from the low 160 bits of a `uint256` argument. */
  address: "OneInchAddress",
  /** One route hop: pool + AMM family + direction, from a single word. */
  pool: "OneInchPool",
  makerTraits: "OneInchMakerTraits",
  takerTraits: "OneInchTakerTraits",
  fillArgs: "OneInchFillArgs",
  extension: "OneInchExtension",
  permit: "OneInchPermit",
  amountGetter: "OneInchAmountGetter",
  swap: "OneInchSwap",
  unoswap: "OneInchUnoswap",
  fillOrder: "OneInchFillOrder",
  cancelOrder: "OneInchCancelOrder",
  orderFilled: "OneInchOrderFilled",
  orderCancelled: "OneInchOrderCancelled",
  epochIncreased: "OneInchEpochIncreased",
  // --- The pre-V6 generations. V2 through V5 are still live contracts with
  // live liquidity, and they answer DIFFERENT verbs than V6 does, so each needs
  // its own id rather than being folded into the V6 cards above.
  /** `uniswapV3Swap` / `unoswap` on V3-V5: a route given as packed pool words. */
  legacyRoute: "OneInchLegacyRoute",
  /** `clipperSwap`: a single-venue RFQ-style swap, not a route. */
  clipperSwap: "OneInchClipperSwap",
  /** `fillOrderRFQ` on Limit Order Protocol V1/V2. */
  rfqFill: "OneInchRfqFill",
  /** `settleOrders` on the Fusion settlement contract. */
  fusionSettle: "OneInchFusionSettle",
  /** The `Swapped` event — V2/V3 routers, and the shape KyberSwap forked. */
  swapped: "OneInchSwapped",
  rfqOrderFilled: "OneInchOrderFilledRfq",
  orderCanceled: "OneInchOrderCanceled",
  nonceIncreased: "OneInchNonceIncreased"
};
var ONEINCH_KINDS = {
  address: "oneinch.address",
  dex: "oneinch.dex",
  makerTraits: "oneinch.maker_traits",
  takerTraits: "oneinch.taker_traits",
  fillArgs: "oneinch.fill_args",
  extension: "oneinch.extension",
  permit: "oneinch.permit"
};
var ONEINCH_CALLS = {
  swap: "swap",
  unoswap: "unoswap",
  fillOrder: "fillOrder",
  cancelOrder: "cancelOrder",
  uniswapV3Swap: "uniswapV3Swap",
  clipperSwap: "clipperSwap",
  fillOrderRfq: "fillOrderRFQ",
  settleOrders: "settleOrders"
};
var ONEINCH_EVENTS = {
  orderFilled: "OrderFilled",
  orderCancelled: "OrderCancelled",
  epochIncreased: "EpochIncreased",
  /**
   * The V2/V3 router settlement event. NOTE the spelling of the two cancel
   * events: the Limit Order Protocol spells it `OrderCancelled` (two Ls) and the
   * RFQ contract spells it `OrderCanceled` (one). They are different events on
   * different contracts, not a typo to normalise away.
   */
  swapped: "Swapped",
  rfqOrderFilled: "OrderFilledRFQ",
  orderCanceled: "OrderCanceled",
  nonceIncreased: "NonceIncreased"
};
var ONEINCH_FIELDS = {
  address: "address",
  poolProtocol: "poolProtocol",
  zeroForOne: "zeroForOne"
};

// src/protocols/conditional_tokens/labels.ts
var CONDITIONAL_TOKENS = {
  protocolId: "conditional_tokens",
  displayName: "Conditional Tokens",
  docs: "https://docs.gnosis.io/conditionaltokens/"
};
var CONDITIONAL_TOKENS_COMPONENTS = {
  position: "ConditionalTokensPosition"
};
var CONDITIONAL_TOKENS_EVENTS = {
  positionSplit: "PositionSplit",
  positionsMerge: "PositionsMerge",
  payoutRedemption: "PayoutRedemption"
};
var CONDITIONAL_TOKENS_FIELDS = {
  stakeholder: "stakeholder",
  collateralToken: "collateralToken",
  conditionId: "conditionId",
  parentCollectionId: "parentCollectionId",
  /** `PositionSplit` / `PositionsMerge` — which outcomes the stake was split across. */
  partition: "partition",
  /**
   * `PayoutRedemption` — which outcomes were CLAIMED. The standard's own name,
   * and a different concept from `partition`, which is why the redemption does
   * not carry one. The card reads both so a redemption still shows its
   * outcome count.
   */
  indexSets: "indexSets",
  amount: "amount",
  payout: "payout"
};
var CONDITIONAL_TOKENS_LABELS = {
  trader: "Trader",
  market: "Market",
  collateral: "Collateral",
  staked: "Staked",
  returned: "Returned",
  paidOut: "Paid out",
  outcomes: "Outcomes"
};
var CONDITIONAL_TOKENS_VERBS = {
  [CONDITIONAL_TOKENS_EVENTS.positionSplit]: {
    title: "Open prediction-market position",
    amountLabel: CONDITIONAL_TOKENS_LABELS.staked,
    note: "Collateral in, one outcome share out per outcome."
  },
  [CONDITIONAL_TOKENS_EVENTS.positionsMerge]: {
    title: "Close prediction-market position",
    amountLabel: CONDITIONAL_TOKENS_LABELS.returned,
    note: "A complete set of outcome shares back, collateral out."
  },
  [CONDITIONAL_TOKENS_EVENTS.payoutRedemption]: {
    title: "Redeem resolved market",
    amountLabel: CONDITIONAL_TOKENS_LABELS.paidOut,
    note: "The market RESOLVED and this holder claimed. May be zero."
  }
};

// src/protocols/polymarket_ctf_exchange/labels.ts
var POLYMARKET_CTF_EXCHANGE = {
  protocolId: "polymarket_ctf_exchange",
  displayName: "Polymarket CTF Exchange",
  docs: "https://docs.polymarket.com/"
};
var POLYMARKET_COMPONENTS = {
  fill: "PolymarketOrderFill"
};
var POLYMARKET_EVENTS = {
  orderFilled: "OrderFilled",
  ordersMatched: "OrdersMatched",
  tokenRegistered: "TokenRegistered"
};
var POLYMARKET_FIELDS = {
  orderHash: "orderHash",
  maker: "maker",
  taker: "taker",
  side: "side",
  tokenId: "tokenId",
  makerAssetId: "makerAssetId",
  takerAssetId: "takerAssetId",
  makerAmountFilled: "makerAmountFilled",
  takerAmountFilled: "takerAmountFilled",
  fee: "fee",
  takerOrderHash: "takerOrderHash",
  takerOrderMaker: "takerOrderMaker",
  token0: "token0",
  token1: "token1",
  conditionId: "conditionId"
};
var POLYMARKET_LABELS = {
  trader: "Trader",
  counterparty: "Counterparty",
  market: "Market",
  outcome: "Outcome share",
  order: "Order",
  shares: "Shares",
  paid: "Paid",
  received: "Received",
  price: "Price per share",
  fee: "Fee"
};
var POLYMARKET_SIDE = {
  buy: 0,
  sell: 1
};
var POLYMARKET_SIDE_VERBS = {
  buy: {
    title: "Buy prediction-market shares",
    makerGave: POLYMARKET_LABELS.paid,
    makerGot: POLYMARKET_LABELS.shares
  },
  sell: {
    title: "Sell prediction-market shares",
    makerGave: POLYMARKET_LABELS.shares,
    makerGot: POLYMARKET_LABELS.received
  }
};
function polymarketSide(value) {
  const n = typeof value === "number" ? value : typeof value === "string" && value.trim() !== "" ? Number(value) : NaN;
  if (n === POLYMARKET_SIDE.buy) return "buy";
  if (n === POLYMARKET_SIDE.sell) return "sell";
  return void 0;
}
function polymarketPrice(side, makerAmountFilled, takerAmountFilled) {
  const maker = _toBig(makerAmountFilled);
  const taker = _toBig(takerAmountFilled);
  if (side === void 0 || maker === void 0 || taker === void 0) return void 0;
  const [collateral, shares] = side === "buy" ? [maker, taker] : [taker, maker];
  if (shares === 0n || collateral === 0n) return void 0;
  const scaled = collateral * 10000n / shares;
  return `${scaled / 10000n}.${String(scaled % 10000n).padStart(4, "0")}`;
}
function _toBig(value) {
  if (typeof value === "bigint") return value;
  if (typeof value === "number" && Number.isFinite(value)) return BigInt(Math.trunc(value));
  if (typeof value === "string" && value.trim() !== "") {
    try {
      return BigInt(value.trim());
    } catch {
      return void 0;
    }
  }
  return void 0;
}

// src/protocols/erc4337/labels.ts
var ERC4337 = {
  protocolId: "erc4337",
  displayName: "ERC-4337",
  docs: "https://eips.ethereum.org/EIPS/eip-4337"
};
var ERC4337_COMPONENTS = {
  userOperation: "Erc4337UserOperation",
  handleOps: "Erc4337HandleOps"
};
var ERC4337_EVENTS = { userOperationEvent: "UserOperationEvent" };
var ERC4337_CALLS = { handleOps: "handleOps" };
var ERC4337_FIELDS = {
  sender: "sender",
  paymaster: "paymaster",
  nonce: "nonce",
  success: "success",
  actualGasCost: "actualGasCost",
  actualGasUsed: "actualGasUsed",
  userOpHash: "userOpHash"
};
var ERC4337_LABELS = {
  account: "Smart account",
  entryPoint: "EntryPoint",
  paidBy: "Paid by",
  selfFunded: "Self-funded",
  gasCost: "Gas cost",
  userOpHash: "Operation hash",
  nonce: "Nonce"
};

// src/protocols/layerzero_v2/labels.ts
var LAYERZERO_V2 = {
  protocolId: "layerzero_v2",
  displayName: "LayerZero V2",
  docs: "https://docs.layerzero.network/v2"
};
var LAYERZERO_V2_COMPONENTS = {
  payloadVerified: "LayerZeroPayloadVerified"
};
var LAYERZERO_V2_EVENTS = { payloadVerified: "PayloadVerified" };
var LAYERZERO_V2_FIELDS = {
  dvn: "dvn",
  confirmations: "confirmations",
  proofHash: "proofHash"
};
var LAYERZERO_V2_LABELS = {
  verifier: "Verifier (DVN)",
  library: "Receive library",
  confirmations: "Confirmations waited",
  proofHash: "Payload hash",
  oneOfQuorum: "One attestation"
};

// src/protocols/relay/labels.ts
var RELAY = {
  protocolId: "relay",
  displayName: "Relay"
};
var RELAY_EVENTS = {
  fundsMovement: "FundsMovement"
};
var RELAY_COMPONENTS = {
  fundsMovement: "RelayFundsMovement"
};
var RELAY_FIELDS = {
  from: ["from", "sender", "depositor"],
  to: ["to", "recipient", "receiver"],
  token: ["token", "currency"],
  amount: ["amount", "value"],
  tag: ["data"]
};
var RELAY_LABELS = {
  movedBy: "Moved by",
  from: "From",
  to: "To",
  amount: "Amount",
  restates: "Restates an ERC-20 Transfer in this transaction"
};

// src/protocols/pancakeswap/labels.ts
var PANCAKESWAP = {
  protocolId: "pancakeswap_router",
  displayName: "PancakeSwap",
  docs: "https://docs.pancakeswap.finance"
};
var PANCAKESWAP_COMPONENTS = {
  /** A V3 packed path, unpacked into `TOKEN --fee--> TOKEN` hops. */
  path: "PancakeSwapPath",
  /** One Universal Router command: its name, its place in the plan, its args. */
  command: "PancakeSwapCommand",
  /** An Infinity pool identity — currencies, hook, fee — with no pool address. */
  poolKey: "PancakeSwapPoolKey",
  v2Swap: "PancakeSwapV2Swap",
  v3Swap: "PancakeSwapV3Swap",
  multicall: "PancakeSwapMulticall",
  execute: "PancakeSwapExecute",
  swapEvent: "PancakeSwapSwapEvent"
};
var PANCAKESWAP_KINDS = {
  path: "pancakeswap.v3_path",
  command: "pancakeswap.command",
  poolKey: "pancakeswap.pool_key"
};
var PANCAKESWAP_CALLS = {
  swapExactTokensForTokens: "swapExactTokensForTokens",
  exactInput: "exactInput",
  exactInputSingle: "exactInputSingle",
  multicall: "multicall",
  execute: "execute"
};
var PANCAKESWAP_EVENTS = {
  swap: "Swap"
};
var PANCAKESWAP_FIELDS = {
  path: "path",
  fee: "fee",
  commandName: "commandName",
  commandIndex: "commandIndex",
  currency0: "currency0",
  currency1: "currency1",
  hooks: "hooks",
  poolManager: "poolManager"
};

// src/protocols/pancakeswap_v3_pool/labels.ts
var PANCAKESWAP_V3_POOL = {
  protocolId: "pancakeswap_v3_pool",
  displayName: "PancakeSwap V3",
  docs: "https://docs.pancakeswap.finance/developers/smart-contracts/pancakeswap-exchange/v3-contracts"
};
var PANCAKESWAP_V3_POOL_COMPONENTS = {
  poolSwap: "PancakeSwapPoolSwap"
};
var PANCAKESWAP_V3_POOL_EVENTS = {
  swap: "Swap"
};
var PANCAKESWAP_V3_POOL_FIELDS = {
  sender: "sender",
  recipient: "recipient",
  amount0: "amount0",
  amount1: "amount1",
  sqrtPriceX96: "sqrtPriceX96",
  liquidity: "liquidity",
  tick: "tick",
  protocolFeesToken0: "protocolFeesToken0",
  protocolFeesToken1: "protocolFeesToken1"
};
var PANCAKESWAP_V3_POOL_LABELS = {
  pool: "Pool",
  trader: "Trader",
  recipient: "Received by",
  soldToken0: "Sold (token0)",
  soldToken1: "Sold (token1)",
  boughtToken0: "Bought (token0)",
  boughtToken1: "Bought (token1)",
  protocolFees: "Protocol fees",
  tick: "Tick",
  liquidity: "Liquidity"
};

// src/protocols/sushiswap/labels.ts
var SUSHISWAP = {
  protocolId: "sushiswap_router",
  displayName: "SushiSwap",
  docs: "https://docs.sushi.com"
};
var SUSHISWAP_COMPONENTS = {
  /** A `RouteProcessorData` struct SushiXSwapV2 forwards (source or dest leg). */
  routeProcessorData: "SushiSwapRouteProcessorData",
  snwapMultiple: "SushiSwapSnwapMultiple",
  route: "SushiSwapRoute",
  bridgeOnSource: "SushiSwapBridgeOnSource"
};
var SUSHISWAP_KINDS = {
  routeProcessorData: "sushiswap.route_processor_data"
};
var SUSHISWAP_CALLS = {
  snwapMultiple: "snwapMultiple"
};
var SUSHISWAP_EVENTS = {
  route: "Route",
  swapAndBridgeOnSource: "SwapAndBridgeOnSource"
};

// src/protocols/tokemak_messaging/labels.ts
var TOKEMAK_MESSAGING = {
  protocolId: "tokemak_messaging",
  displayName: "Tokemak Messaging",
  docs: "https://docs.tokemak.xyz"
};
var TOKEMAK_COMPONENTS = {
  /** Bespoke event card for the ReceivingRouter `MessageData` inbound message. */
  message: "TokemakMessage"
};
var TOKEMAK_FIELD_LABELS = {
  messageType: "Payload type",
  sourceChainSelector: "Source chain",
  messageOrigin: "Origin",
  messageReceiver: "Receiver",
  token: "Token",
  amount: "Amount",
  messageNonce: "Nonce",
  ccipMessageId: "CCIP message ID"
};

// src/protocols/wormhole/labels.ts
var WORMHOLE = {
  protocolId: "wormhole",
  displayName: "Wormhole",
  docs: "https://docs.wormhole.com"
};
var WORMHOLE_COMPONENTS = {
  /** Value component for the namespaced `wormhole.vaa` kind. */
  vaa: "WormholeVaa",
  /** Value component for the Executor signed quote (`wormhole.signed_quote`). */
  signedQuote: "WormholeSignedQuote",
  /** Value component for the Executor request (`wormhole.executor_request`). */
  executorRequest: "WormholeExecutorRequest",
  /** Value component for the relay instructions (`wormhole.relay_instructions`). */
  relayInstructions: "WormholeRelayInstructions",
  /** Value component for the Token Bridge transfer payload. */
  transferPayload: "WormholeTransferPayload",
  /** Call component for the Core Bridge `parseVM` / `parseAndVerifyVM` reads —
   * renders the parsed VAA struct (+ verification result), and the `encodedVM`
   * argument whether it stayed raw bytes or was itself decoded to a VAA. */
  parseVm: "WormholeParseVM"
};
var WORMHOLE_VAA_KIND = "wormhole.vaa";
var WORMHOLE_SIGNED_QUOTE_KIND = "wormhole.signed_quote";
var WORMHOLE_EXECUTOR_REQUEST_KIND = "wormhole.executor_request";
var WORMHOLE_RELAY_INSTRUCTIONS_KIND = "wormhole.relay_instructions";
var WORMHOLE_TRANSFER_PAYLOAD_KIND = "wormhole.transfer_payload";
var WORMHOLE_VAA_ID_FACT = "vaa_id";
var WORMHOLE_FIELD_LABELS = {
  vaaId: "VAA ID",
  emitterChain: "Emitter chain",
  emitterAddress: "Emitter",
  sequence: "Sequence",
  consistencyLevel: "Finality",
  guardianSet: "Guardian set",
  signatures: "Guardian signatures",
  payloadType: "Payload",
  amount: "Amount",
  token: "Token",
  tokenChain: "Token chain",
  recipient: "Recipient",
  recipientChain: "Destination chain",
  payloadHex: "Raw payload"
};
var WORMHOLE_VM_FIELD_LABELS = {
  version: "Version",
  timestamp: "Timestamp",
  nonce: "Nonce",
  emitterChain: "Emitter chain",
  emitterAddress: "Emitter",
  sequence: "Sequence",
  consistencyLevel: "Finality",
  guardianSet: "Guardian set",
  signatures: "Guardian signatures",
  hash: "VAA hash",
  payload: "Signed payload",
  valid: "Verification",
  reason: "Reason",
  encodedVaa: "Encoded VAA"
};
var WORMHOLE_QUOTE_FIELD_LABELS = {
  prefix: "Quote version",
  quoterAddress: "Quoter",
  payeeAddress: "Payee",
  srcChain: "Source chain",
  dstChain: "Destination chain",
  baseFee: "Base fee",
  dstGasPrice: "Dest gas price",
  srcPrice: "Source price",
  dstPrice: "Dest price",
  expiryTime: "Expires",
  signature: "Signature"
};
var WORMHOLE_EXECUTOR_FIELD_LABELS = {
  prefix: "Request type",
  autoDiscover: "Auto-discover",
  type: "Type",
  gasLimit: "Gas limit",
  msgValue: "Message value",
  amount: "Drop-off amount",
  recipient: "Recipient"
};
var WORMHOLE_REQUEST_PREFIX_LABELS = {
  ERV1: "Deliver VAA",
  ERN1: "Deliver NTT",
  ERC1: "Deliver CCTP v1",
  ERC2: "Deliver CCTP v2"
};

// src/protocols/uniswap/labels.ts
var UNISWAP_UNIVERSAL_ROUTER = {
  protocolId: "uniswap_universal_router",
  displayName: "Uniswap Universal Router",
  docs: "https://docs.uniswap.org/contracts/universal-router/overview"
};
var UNISWAP_PERMIT2 = {
  protocolId: "uniswap_permit2",
  displayName: "Permit2",
  docs: "https://docs.uniswap.org/contracts/permit2/overview"
};
var UNISWAP_V1_FACTORY = {
  protocolId: "uniswap_v1_factory",
  displayName: "Uniswap V1",
  docs: "https://docs.uniswap.org/contracts/v1/overview"
};
var UNISWAP_V1_EXCHANGE = {
  protocolId: "uniswap_v1_exchange",
  displayName: "Uniswap V1",
  docs: "https://docs.uniswap.org/contracts/v1/overview"
};
var UNISWAP_V2_ROUTER = {
  protocolId: "uniswap_v2_router",
  displayName: "Uniswap V2",
  docs: "https://docs.uniswap.org/contracts/v2/overview"
};
var UNISWAP_V3_ROUTER = {
  protocolId: "uniswap_v3_router",
  displayName: "Uniswap V3",
  docs: "https://docs.uniswap.org/contracts/v3/overview"
};
var UNISWAP_V3_POSITION_MANAGER = {
  protocolId: "uniswap_v3_position_manager",
  displayName: "Uniswap V3 Positions",
  docs: "https://docs.uniswap.org/contracts/v3/reference/periphery/NonfungiblePositionManager"
};
var UNISWAP_V4_POOL_MANAGER = {
  protocolId: "uniswap_v4",
  displayName: "Uniswap V4",
  docs: "https://docs.uniswap.org/contracts/v4/overview"
};
var UNISWAP_V4_POSITION_MANAGER = {
  protocolId: "uniswap_v4_position_manager",
  displayName: "Uniswap V4 Positions",
  docs: "https://docs.uniswap.org/contracts/v4/reference/periphery/PositionManager"
};
var UNISWAP_V2_FACTORY = {
  protocolId: "uniswap_v2_factory",
  displayName: "Uniswap V2",
  docs: "https://docs.uniswap.org/contracts/v2/overview"
};
var UNISWAP_V3_FACTORY = {
  protocolId: "uniswap_v3_factory",
  displayName: "Uniswap V3",
  docs: "https://docs.uniswap.org/contracts/v3/overview"
};
var UNISWAP_V2_PAIR = {
  protocolId: "uniswap_v2_pair",
  displayName: "Uniswap V2",
  docs: "https://docs.uniswap.org/contracts/v2/concepts/protocol-overview/smart-contracts"
};
var UNISWAP_V3_POOL = {
  protocolId: "uniswap_v3_pool",
  displayName: "Uniswap V3",
  docs: "https://docs.uniswap.org/contracts/v3/reference/core/UniswapV3Pool"
};
var UNISWAP_BRAND_LIST = [
  UNISWAP_UNIVERSAL_ROUTER,
  UNISWAP_PERMIT2,
  UNISWAP_V1_FACTORY,
  UNISWAP_V1_EXCHANGE,
  UNISWAP_V2_ROUTER,
  UNISWAP_V3_ROUTER,
  UNISWAP_V3_POSITION_MANAGER,
  UNISWAP_V4_POOL_MANAGER,
  UNISWAP_V4_POSITION_MANAGER,
  UNISWAP_V2_FACTORY,
  UNISWAP_V3_FACTORY,
  UNISWAP_V2_PAIR,
  UNISWAP_V3_POOL
];
var UNISWAP_COMPONENTS = {
  /** Universal Router `execute` — the command-leg multicall card. */
  universalRouter: "UniswapUniversalRouter",
  /** A V1/V2/V3 router swap call (exact-in / exact-out, single or multi-hop). */
  swap: "UniswapSwap",
  /** A liquidity call — V1 add/remove, V3/V4 position manager verbs. */
  liquidity: "UniswapLiquidity",
  /** A Permit2 approval / transfer call. */
  permit2: "UniswapPermit2",
  /** A pool event — reserved for when the backend attributes pool contracts. */
  poolEvent: "UniswapPoolEvent"
};
var UNISWAP_FIELD_LABELS = {
  contract: "Contract",
  // Swap legs.
  tokenIn: "From token",
  tokenOut: "To token",
  amountIn: "Amount in",
  amountInMax: "Max in",
  amountOut: "Amount out",
  amountOutMin: "Min out",
  recipient: "Recipient",
  payer: "Payer is user",
  path: "Route",
  fee: "Pool fee",
  sqrtPriceLimit: "Price limit (sqrtX96)",
  deadline: "Deadline",
  // V4 PoolManager swap (PoolKey + SwapParams).
  currency0: "Currency 0",
  currency1: "Currency 1",
  zeroForOne: "Direction (0\u21921)",
  amountSpecified: "Amount specified",
  hooks: "Hooks",
  swapDelta: "Swap delta",
  // Permit2.
  token: "Token",
  spender: "Spender",
  amount: "Amount",
  expiration: "Expiration",
  nonce: "Nonce",
  sigDeadline: "Signature deadline",
  owner: "Owner",
  from: "From",
  to: "To",
  // Liquidity.
  token0: "Token 0",
  token1: "Token 1",
  tokenId: "Position ID",
  liquidity: "Liquidity",
  tickLower: "Tick lower",
  tickUpper: "Tick upper",
  amount0: "Amount 0",
  amount1: "Amount 1",
  amount0Min: "Min amount 0",
  amount1Min: "Min amount 1",
  // Pool events.
  sender: "Sender",
  amount0In: "Amount 0 in",
  amount1In: "Amount 1 in",
  amount0Out: "Amount 0 out",
  amount1Out: "Amount 1 out",
  tick: "Tick",
  commands: "Commands"
};
var UNISWAP_COMMAND_LABELS = {
  V3_SWAP_EXACT_IN: "V3 swap \xB7 exact in",
  V3_SWAP_EXACT_OUT: "V3 swap \xB7 exact out",
  V4_SWAP: "V4 swap plan",
  V2_SWAP_EXACT_IN: "V2 swap \xB7 exact in",
  V2_SWAP_EXACT_OUT: "V2 swap \xB7 exact out",
  PERMIT2_TRANSFER_FROM: "Permit2 transfer",
  PERMIT2_TRANSFER_FROM_BATCH: "Permit2 transfer (batch)",
  PERMIT2_PERMIT: "Permit2 permit",
  PERMIT2_PERMIT_BATCH: "Permit2 permit (batch)",
  SWEEP: "Sweep",
  TRANSFER: "Transfer",
  PAY_PORTION: "Pay portion",
  WRAP_ETH: "Wrap ETH",
  UNWRAP_WETH: "Unwrap WETH",
  BALANCE_CHECK_ERC20: "Balance check",
  APPROVE_ERC20: "Approve ERC20",
  SEAPORT_V1_5: "Buy NFT \xB7 Seaport 1.5",
  SEAPORT_V1_4: "Buy NFT \xB7 Seaport 1.4",
  LOOKS_RARE_V2: "Buy NFT \xB7 LooksRare",
  X2Y2_721: "Buy NFT \xB7 X2Y2",
  X2Y2_1155: "Buy NFT \xB7 X2Y2",
  NFTX: "Buy NFT \xB7 NFTX",
  NFT20: "Buy NFT \xB7 NFT20",
  CRYPTOPUNKS: "Buy NFT \xB7 CryptoPunks",
  SUDOSWAP: "Buy NFT \xB7 sudoswap",
  FOUNDATION: "Buy NFT \xB7 Foundation",
  ELEMENT_MARKET: "Buy NFT \xB7 Element",
  EXECUTE_SUB_PLAN: "Sub-plan"
};
var UNISWAP_SWAP_COMMANDS = /* @__PURE__ */ new Set([
  "V3_SWAP_EXACT_IN",
  "V3_SWAP_EXACT_OUT",
  "V2_SWAP_EXACT_IN",
  "V2_SWAP_EXACT_OUT"
]);

// src/standards/erc20/labels.ts
var ERC20 = {
  protocolId: "erc20",
  displayName: "ERC20",
  docs: "https://eips.ethereum.org/EIPS/eip-20"
};

// src/standards/erc721/labels.ts
var ERC721 = {
  protocolId: "erc721",
  displayName: "ERC721",
  docs: "https://eips.ethereum.org/EIPS/eip-721"
};

// src/standards/erc1155/labels.ts
var ERC1155 = {
  protocolId: "erc1155",
  displayName: "ERC1155",
  docs: "https://eips.ethereum.org/EIPS/eip-1155"
};

// src/standards/native/labels.ts
var NATIVE_TRANSFER = {
  protocolId: "native_transfer",
  displayName: "Native transfer"
};

// src/standards/precompiles/labels.ts
var PRECOMPILE = {
  protocolId: "precompile",
  displayName: "Precompile"
};
var PRECOMPILE_COMPONENT = "PrecompileCall";
var PRECOMPILES = {
  ecrecover: "ECDSA signature recovery",
  sha256: "SHA-256 hash",
  ripemd160: "RIPEMD-160 hash",
  identity: "Identity (verbatim data copy)",
  modexp: "Modular exponentiation",
  ecadd: "BN254 point addition",
  ecmul: "BN254 scalar multiplication",
  ecpairing: "BN254 pairing check",
  blake2f: "BLAKE2b F compression",
  pointEvaluation: "KZG point evaluation (EIP-4844)"
};

// src/standards/lowlevel/labels.ts
var LOWLEVEL = {
  protocolId: "lowlevel",
  displayName: "Low-level"
};
var LOWLEVEL_COMPONENT = "LowLevelCall";
var LOWLEVEL_CALLS = {
  receive: {
    kind: "receive",
    caption: "Native value received",
    description: "Empty calldata carrying native value: the EVM dispatched this call to the contract's receive() function. The transferred amount appears as the accompanying native transfer."
  },
  fallback: {
    kind: "fallback",
    caption: "Catch-all entry point",
    description: "Empty calldata with no matching function: the EVM dispatched this call to the contract's fallback() function."
  }
};

// src/standards/deployment/labels.ts
var DEPLOYMENT = {
  protocolId: "deployment",
  displayName: "Deployment"
};
var CONTRACT_CREATION_CALL = "ContractCreation";
var INIT_CODE_PARAM = "init_code";
var CONTRACT_TYPE_PARAM = "contract_type";
var CODE_HASH_PARAM = "code_hash";
var PROTOCOL_ID_PARAM = "protocol_id";
var IDENTIFIED_BY_PARAM = "identified_by";
var IDENTIFIED_BY = {
  /** Init-code hash matched a known contract AND its canonical factory deployed it. */
  codeHash: "code_hash",
  /** A protocol definition pins the account that ran the opcode. */
  deployer: "deployer",
  /** Byte-identical to a known contract, but deployed by something else. */
  codeHashUnverified: "code_hash_unverified",
  /** The bytecode implements a standard interface in full — shape, not authorship. */
  interface: "interface"
};
var INITIAL_SUPPLY_PARAM = "initial_supply";
var MINTED_TO_PARAM = "minted_to";
var OWNER_PARAM = "owner";
var OWNERSHIP_RENOUNCED_PARAM = "ownership_renounced";
var TOKEN0_PARAM = "token0";
var TOKEN1_PARAM = "token1";
var FEE_TIER_PARAM = "fee_tier";
var TICK_SPACING_PARAM = "tick_spacing";
var FUNDED_WITH_PARAM = "funded_with";

// src/standards/proxy/labels.ts
var PROXY = {
  protocolId: "proxy",
  displayName: "Proxy"
};

// src/standards/ownership/labels.ts
var OWNERSHIP = {
  protocolId: "ownership",
  displayName: "Ownership"
};

// src/standards/access/labels.ts
var ACCESS = {
  protocolId: "access",
  displayName: "Access control"
};

// src/standards/erc4626/labels.ts
var ERC4626 = {
  protocolId: "erc4626",
  displayName: "Vault (ERC-4626)",
  docs: "https://eips.ethereum.org/EIPS/eip-4626"
};
var VAULT_COMPONENTS = {
  /** A vault entrypoint or view call (`deposit`/`mint`/`withdraw`/`redeem`/`totalAssets`/…). */
  call: "VaultCall",
  /** A vault log (`Deposit`/`Withdraw`). */
  event: "VaultEvent"
};

// src/protocols/chainlink/catalog.generated.json
var catalog_generated_default = {
  chainlink_automation: {
    entries: [
      {
        fields: [
          "id",
          "success",
          "from",
          "payment",
          "performData"
        ],
        kind: "event",
        name: "UpkeepPerformed",
        signature: "UpkeepPerformed(uint256,bool,address,uint96,bytes)"
      },
      {
        fields: [
          "id",
          "success",
          "checkBlockNumber",
          "gasUsed",
          "gasOverhead",
          "totalPayment"
        ],
        kind: "event",
        name: "UpkeepPerformed",
        signature: "UpkeepPerformed(uint256,bool,uint32,uint256,uint256,uint96)"
      },
      {
        fields: [
          "id",
          "success",
          "totalPayment",
          "gasUsed",
          "gasOverhead",
          "trigger"
        ],
        kind: "event",
        name: "UpkeepPerformed",
        signature: "UpkeepPerformed(uint256,bool,uint96,uint256,uint256,bytes)"
      },
      {
        fields: [
          "gasAmount",
          "data"
        ],
        kind: "function",
        name: "forward",
        signature: "forward(uint256,bytes)"
      },
      {
        fields: [
          "id",
          "performData"
        ],
        kind: "function",
        name: "performUpkeep",
        signature: "performUpkeep(uint256,bytes)"
      },
      {
        fields: [
          "reportContext",
          "rawReport",
          "rs",
          "ss",
          "rawVs"
        ],
        kind: "function",
        name: "transmit",
        signature: "transmit(bytes32[3],bytes,bytes32[],bytes32[],bytes32)"
      }
    ],
    label: "Chainlink Automation",
    vocabularies: {}
  },
  chainlink_ccip: {
    entries: [
      {
        fields: [
          "newAdmin"
        ],
        kind: "event",
        name: "AdminSet",
        signature: "AdminSet(address)"
      },
      {
        fields: [
          "sequenceNumber"
        ],
        kind: "event",
        name: "AlreadyAttempted",
        signature: "AlreadyAttempted(uint64)"
      },
      {
        fields: [
          "sourceChainSelector",
          "sequenceNumber"
        ],
        kind: "event",
        name: "AlreadyAttempted",
        signature: "AlreadyAttempted(uint64,uint64)"
      },
      {
        fields: [
          "configVersion",
          "voter",
          "taggedRoot"
        ],
        kind: "event",
        name: "AlreadyBlessed",
        signature: "AlreadyBlessed(uint32,address,(address,bytes32))"
      },
      {
        fields: [
          "configVersion",
          "voter",
          "taggedRoot"
        ],
        kind: "event",
        name: "AlreadyVotedToBless",
        signature: "AlreadyVotedToBless(uint32,address,(address,bytes32))"
      },
      {
        fields: [
          "caller"
        ],
        kind: "event",
        name: "AuthorizedCallerAdded",
        signature: "AuthorizedCallerAdded(address)"
      },
      {
        fields: [
          "caller"
        ],
        kind: "event",
        name: "AuthorizedCallerRemoved",
        signature: "AuthorizedCallerRemoved(address)"
      },
      {
        fields: [
          "sender",
          "amount"
        ],
        kind: "event",
        name: "Burned",
        signature: "Burned(address,uint256)"
      },
      {
        fields: [
          "destChainSelector",
          "sender",
          "messageId",
          "feeToken",
          "tokenAmountBeforeTokenPoolFees",
          "encodedMessage",
          "receipts",
          "verifierBlobs"
        ],
        kind: "event",
        name: "CCIPMessageSent",
        signature: "CCIPMessageSent(uint64,address,bytes32,address,uint256,bytes,(address,uint32,uint32,uint256,bytes)[],bytes[])"
      },
      {
        fields: [
          "destChainSelector",
          "sequenceNumber",
          "message"
        ],
        kind: "event",
        name: "CCIPMessageSent",
        signature: "CCIPMessageSent(uint64,uint64,((bytes32,uint64,uint64,uint64,uint64),address,bytes,bytes,bytes,address,uint256,uint256,(address,bytes,bytes,uint256,bytes)[]))"
      },
      {
        fields: [
          "message"
        ],
        kind: "event",
        name: "CCIPSendRequested",
        signature: "CCIPSendRequested((uint64,address,address,uint64,uint256,bool,uint64,address,uint256,bytes,(address,uint256)[],bytes[],bytes32))"
      },
      {
        fields: [
          "message"
        ],
        kind: "event",
        name: "CCIPSendRequested",
        signature: "CCIPSendRequested((uint64,uint64,uint256,address,uint64,uint256,bool,address,bytes,(address,uint256)[],address,bytes32))"
      },
      {
        fields: [
          "blessedMerkleRoots",
          "unblessedMerkleRoots",
          "priceUpdates"
        ],
        kind: "event",
        name: "CommitReportAccepted",
        signature: "CommitReportAccepted((uint64,bytes,uint64,uint64,bytes32)[],(uint64,bytes,uint64,uint64,bytes32)[],((address,uint224)[],(uint64,uint224)[]))"
      },
      {
        fields: [
          "config"
        ],
        kind: "event",
        name: "ConfigChanged",
        signature: "ConfigChanged((bool,uint128,uint128))"
      },
      {
        fields: [
          "staticConfig",
          "dynamicConfig"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet((address,uint64,uint64,address,address),(uint32,address,address,address,uint16,uint32))"
      },
      {
        fields: [
          "staticConfig",
          "dynamicConfig"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet((address,uint64,uint64,address,address,address),(uint32,address,address,uint16,uint32,uint32))"
      },
      {
        fields: [
          "staticConfig",
          "dynamicConfig"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet((address,uint64,uint64,address,address,address,address),(uint32,uint32,uint16,address,address))"
      },
      {
        fields: [
          "staticConfig",
          "dynamicConfig"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet((uint64,uint64,address,address),(address))"
      },
      {
        fields: [
          "configVersion",
          "config"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet(uint32,((address,address,address,uint8,uint8)[],uint16,uint16))"
      },
      {
        fields: [
          "configVersion",
          "config"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet(uint32,((address,address,uint8,uint8)[],uint16,uint16))"
      },
      {
        fields: [
          "version",
          "config"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet(uint32,(bytes32,(address,uint64)[],uint64))"
      },
      {
        fields: [
          "previousConfigBlockNumber",
          "configDigest",
          "configCount",
          "signers",
          "transmitters",
          "f",
          "onchainConfig",
          "offchainConfigVersion",
          "offchainConfig"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet(uint32,bytes32,uint64,address[],address[],uint8,bytes,uint64,bytes)"
      },
      {
        fields: [
          "ocrPluginType",
          "configDigest",
          "signers",
          "transmitters",
          "F"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet(uint8,bytes32,address[],address[],uint8)"
      },
      {
        fields: [
          "subject"
        ],
        kind: "event",
        name: "CurseLifted",
        signature: "CurseLifted(bytes16)"
      },
      {
        fields: [
          "subjects"
        ],
        kind: "event",
        name: "Cursed",
        signature: "Cursed(bytes16[])"
      },
      {
        fields: [
          "configVersion",
          "subject",
          "blockTimestamp"
        ],
        kind: "event",
        name: "Cursed",
        signature: "Cursed(uint32,bytes16,uint64)"
      },
      {
        fields: [
          "configVersion",
          "timestamp"
        ],
        kind: "event",
        name: "Cursed",
        signature: "Cursed(uint32,uint256)"
      },
      {
        fields: [
          "token",
          "depositor",
          "amount"
        ],
        kind: "event",
        name: "Deposit",
        signature: "Deposit(address,address,uint256)"
      },
      {
        fields: [
          "dynamicConfig"
        ],
        kind: "event",
        name: "DynamicConfigSet",
        signature: "DynamicConfigSet((address,uint32,address))"
      },
      {
        fields: [
          "sequenceNumber",
          "messageId",
          "state",
          "returnData"
        ],
        kind: "event",
        name: "ExecutionStateChanged",
        signature: "ExecutionStateChanged(uint64,bytes32,uint8,bytes)"
      },
      {
        fields: [
          "sourceChainSelector",
          "sequenceNumber",
          "messageId",
          "messageHash",
          "state",
          "returnData",
          "gasUsed"
        ],
        kind: "event",
        name: "ExecutionStateChanged",
        signature: "ExecutionStateChanged(uint64,uint64,bytes32,bytes32,uint8,bytes,uint256)"
      },
      {
        fields: [
          "sourceChainSelector",
          "messageNumber",
          "messageId",
          "state",
          "returnData"
        ],
        kind: "event",
        name: "ExecutionStateChanged",
        signature: "ExecutionStateChanged(uint64,uint64,bytes32,uint8,bytes)"
      },
      {
        fields: [
          "remoteChainSelector",
          "token",
          "amount"
        ],
        kind: "event",
        name: "InboundRateLimitConsumed",
        signature: "InboundRateLimitConsumed(uint64,address,uint256)"
      },
      {
        fields: [
          "oldEpochAndRound",
          "newEpochAndRound"
        ],
        kind: "event",
        name: "LatestPriceEpochAndRoundSet",
        signature: "LatestPriceEpochAndRoundSet(uint40,uint40)"
      },
      {
        fields: [
          "sender",
          "amount"
        ],
        kind: "event",
        name: "Locked",
        signature: "Locked(address,uint256)"
      },
      {
        fields: [
          "remoteChainSelector",
          "token",
          "sender",
          "amount"
        ],
        kind: "event",
        name: "LockedOrBurned",
        signature: "LockedOrBurned(uint64,address,address,uint256)"
      },
      {
        fields: [
          "sender",
          "recipient",
          "amount"
        ],
        kind: "event",
        name: "Minted",
        signature: "Minted(address,address,uint256)"
      },
      {
        fields: [
          "remoteChainSelector",
          "token",
          "amount"
        ],
        kind: "event",
        name: "OutboundRateLimitConsumed",
        signature: "OutboundRateLimitConsumed(uint64,address,uint256)"
      },
      {
        fields: [
          "timestamp"
        ],
        kind: "event",
        name: "OwnerCursed",
        signature: "OwnerCursed(uint256)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferRequested",
        signature: "OwnershipTransferRequested(address,address)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferred",
        signature: "OwnershipTransferred(address,address)"
      },
      {
        fields: [
          "account"
        ],
        kind: "event",
        name: "Paused",
        signature: "Paused(address)"
      },
      {
        fields: [
          "commitStore"
        ],
        kind: "event",
        name: "PermaBlessedCommitStoreAdded",
        signature: "PermaBlessedCommitStoreAdded(address)"
      },
      {
        fields: [
          "commitStore"
        ],
        kind: "event",
        name: "PermaBlessedCommitStoreRemoved",
        signature: "PermaBlessedCommitStoreRemoved(address)"
      },
      {
        fields: [
          "token",
          "pool"
        ],
        kind: "event",
        name: "PoolAdded",
        signature: "PoolAdded(address,address)"
      },
      {
        fields: [
          "token",
          "pool"
        ],
        kind: "event",
        name: "PoolRemoved",
        signature: "PoolRemoved(address,address)"
      },
      {
        fields: [],
        kind: "event",
        name: "RecoveredFromCurse",
        signature: "RecoveredFromCurse()"
      },
      {
        fields: [
          "sender",
          "recipient",
          "amount"
        ],
        kind: "event",
        name: "Released",
        signature: "Released(address,address,uint256)"
      },
      {
        fields: [
          "remoteChainSelector",
          "token",
          "sender",
          "recipient",
          "amount"
        ],
        kind: "event",
        name: "ReleasedOrMinted",
        signature: "ReleasedOrMinted(uint64,address,address,address,uint256)"
      },
      {
        fields: [
          "report"
        ],
        kind: "event",
        name: "ReportAccepted",
        signature: "ReportAccepted((((address,uint192)[],uint64,uint192),(uint64,uint64),bytes32))"
      },
      {
        fields: [
          "report"
        ],
        kind: "event",
        name: "ReportAccepted",
        signature: "ReportAccepted((((address,uint224)[],(uint64,uint224)[]),(uint64,uint64),bytes32))"
      },
      {
        fields: [
          "configVersion",
          "voter",
          "weight",
          "voteCount",
          "cursesHash",
          "accumulatedWeight"
        ],
        kind: "event",
        name: "ReusedVotesToCurse",
        signature: "ReusedVotesToCurse(uint32,address,uint8,uint32,bytes32,uint16)"
      },
      {
        fields: [
          "root"
        ],
        kind: "event",
        name: "RootRemoved",
        signature: "RootRemoved(bytes32)"
      },
      {
        fields: [
          "oldSeqNum",
          "newSeqNum"
        ],
        kind: "event",
        name: "SequenceNumberSet",
        signature: "SequenceNumberSet(uint64,uint64)"
      },
      {
        fields: [
          "sequenceNumber"
        ],
        kind: "event",
        name: "SkippedAlreadyExecutedMessage",
        signature: "SkippedAlreadyExecutedMessage(uint64)"
      },
      {
        fields: [
          "sourceChainSelector",
          "sequenceNumber"
        ],
        kind: "event",
        name: "SkippedAlreadyExecutedMessage",
        signature: "SkippedAlreadyExecutedMessage(uint64,uint64)"
      },
      {
        fields: [
          "nonce",
          "sender"
        ],
        kind: "event",
        name: "SkippedIncorrectNonce",
        signature: "SkippedIncorrectNonce(uint64,address)"
      },
      {
        fields: [
          "sourceChainSelector"
        ],
        kind: "event",
        name: "SkippedReportExecution",
        signature: "SkippedReportExecution(uint64)"
      },
      {
        fields: [
          "nonce",
          "sender"
        ],
        kind: "event",
        name: "SkippedSenderWithPreviousRampMessageInflight",
        signature: "SkippedSenderWithPreviousRampMessageInflight(uint64,address)"
      },
      {
        fields: [
          "voter",
          "subject",
          "onchainCursesHash",
          "cursesHash"
        ],
        kind: "event",
        name: "SkippedUnvoteToCurse",
        signature: "SkippedUnvoteToCurse(address,bytes16,bytes28,bytes28)"
      },
      {
        fields: [
          "voter",
          "expectedCursesHash",
          "actualCursesHash"
        ],
        kind: "event",
        name: "SkippedUnvoteToCurse",
        signature: "SkippedUnvoteToCurse(address,bytes32,bytes32)"
      },
      {
        fields: [
          "sourceChainSelector",
          "sourceConfig"
        ],
        kind: "event",
        name: "SourceChainConfigSet",
        signature: "SourceChainConfigSet(uint64,(address,bool,uint64,bool,bytes))"
      },
      {
        fields: [
          "sourceChainSelector"
        ],
        kind: "event",
        name: "SourceChainSelectorAdded",
        signature: "SourceChainSelectorAdded(uint64)"
      },
      {
        fields: [
          "staticConfig"
        ],
        kind: "event",
        name: "StaticConfigSet",
        signature: "StaticConfigSet((uint64,uint16,address,address,address))"
      },
      {
        fields: [
          "configVersion",
          "taggedRoot",
          "wasBlessed"
        ],
        kind: "event",
        name: "TaggedRootBlessVotesReset",
        signature: "TaggedRootBlessVotesReset(uint32,(address,bytes32),bool)"
      },
      {
        fields: [
          "configVersion",
          "taggedRoot",
          "accumulatedWeight"
        ],
        kind: "event",
        name: "TaggedRootBlessed",
        signature: "TaggedRootBlessed(uint32,(address,bytes32),uint16)"
      },
      {
        fields: [
          "sourceToken",
          "destToken"
        ],
        kind: "event",
        name: "TokenAggregateRateLimitAdded",
        signature: "TokenAggregateRateLimitAdded(address,address)"
      },
      {
        fields: [
          "sourceToken",
          "destToken"
        ],
        kind: "event",
        name: "TokenAggregateRateLimitRemoved",
        signature: "TokenAggregateRateLimitRemoved(address,address)"
      },
      {
        fields: [
          "tokens"
        ],
        kind: "event",
        name: "TokensConsumed",
        signature: "TokensConsumed(uint256)"
      },
      {
        fields: [
          "configDigest",
          "epoch"
        ],
        kind: "event",
        name: "Transmitted",
        signature: "Transmitted(bytes32,uint32)"
      },
      {
        fields: [
          "ocrPluginType",
          "configDigest",
          "sequenceNumber"
        ],
        kind: "event",
        name: "Transmitted",
        signature: "Transmitted(uint8,bytes32,uint64)"
      },
      {
        fields: [
          "subjects"
        ],
        kind: "event",
        name: "Uncursed",
        signature: "Uncursed(bytes16[])"
      },
      {
        fields: [
          "account"
        ],
        kind: "event",
        name: "Unpaused",
        signature: "Unpaused(address)"
      },
      {
        fields: [
          "configVersion",
          "voter",
          "subject",
          "weight",
          "cursesHash",
          "remainingAccumulatedWeight"
        ],
        kind: "event",
        name: "UnvotedToCurse",
        signature: "UnvotedToCurse(uint32,address,bytes16,uint8,bytes28,uint16)"
      },
      {
        fields: [
          "configVersion",
          "voter",
          "weight",
          "voteCount",
          "cursesHash"
        ],
        kind: "event",
        name: "UnvotedToCurse",
        signature: "UnvotedToCurse(uint32,address,uint8,uint32,bytes32)"
      },
      {
        fields: [
          "configVersion",
          "voter",
          "taggedRoot",
          "weight"
        ],
        kind: "event",
        name: "VotedToBless",
        signature: "VotedToBless(uint32,address,(address,bytes32),uint8)"
      },
      {
        fields: [
          "configVersion",
          "voter",
          "subject",
          "curseId",
          "weight",
          "blockTimestamp",
          "cursesHash",
          "accumulatedWeight"
        ],
        kind: "event",
        name: "VotedToCurse",
        signature: "VotedToCurse(uint32,address,bytes16,bytes16,uint8,uint64,bytes28,uint16)"
      },
      {
        fields: [
          "configVersion",
          "voter",
          "weight",
          "voteCount",
          "curseId",
          "cursesHash",
          "accumulatedWeight"
        ],
        kind: "event",
        name: "VotedToCurse",
        signature: "VotedToCurse(uint32,address,uint8,uint32,bytes32,bytes32,uint16)"
      },
      {
        fields: [
          "token",
          "recipient",
          "amount"
        ],
        kind: "event",
        name: "Withdrawal",
        signature: "Withdrawal(address,address,uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "acceptOwnership",
        signature: "acceptOwnership()"
      },
      {
        fields: [
          "authorizedCallerArgs"
        ],
        kind: "function",
        name: "applyAuthorizedCallerUpdates",
        signature: "applyAuthorizedCallerUpdates((address[],address[]))"
      },
      {
        fields: [
          "removes",
          "adds"
        ],
        kind: "function",
        name: "applyPoolUpdates",
        signature: "applyPoolUpdates((address,address)[],(address,address)[])"
      },
      {
        fields: [
          "sourceChainConfigUpdates"
        ],
        kind: "function",
        name: "applySourceChainConfigUpdates",
        signature: "applySourceChainConfigUpdates((address,uint64,bool,bool,bytes)[])"
      },
      {
        fields: [
          "account",
          "amount"
        ],
        kind: "function",
        name: "burn",
        signature: "burn(address,uint256)"
      },
      {
        fields: [
          "amount"
        ],
        kind: "function",
        name: "burn",
        signature: "burn(uint256)"
      },
      {
        fields: [
          "account",
          "amount"
        ],
        kind: "function",
        name: "burnFrom",
        signature: "burnFrom(address,uint256)"
      },
      {
        fields: [
          ""
        ],
        kind: "function",
        name: "ccipReceive",
        signature: "ccipReceive((bytes32,uint64,bytes,bytes,(address,uint256)[]))"
      },
      {
        fields: [
          "destinationChainSelector",
          "message"
        ],
        kind: "function",
        name: "ccipSend",
        signature: "ccipSend(uint64,(address,bytes,(address,uint256)[],address,bytes))"
      },
      {
        fields: [
          "reportContext",
          "report",
          "rs",
          "ss",
          "rawVs"
        ],
        kind: "function",
        name: "commit",
        signature: "commit(bytes32[2],bytes,bytes32[],bytes32[],bytes32)"
      },
      {
        fields: [],
        kind: "function",
        name: "currentRateLimiterState",
        signature: "currentRateLimiterState()"
      },
      {
        fields: [
          "subject"
        ],
        kind: "function",
        name: "curse",
        signature: "curse(bytes16)"
      },
      {
        fields: [
          "subjects"
        ],
        kind: "function",
        name: "curse",
        signature: "curse(bytes16[])"
      },
      {
        fields: [
          "encodedMessage",
          "ccvs",
          "verifierResults",
          "gasLimitOverride"
        ],
        kind: "function",
        name: "execute",
        signature: "execute(bytes,address[],bytes[],uint32)"
      },
      {
        fields: [
          "reportContext",
          "report"
        ],
        kind: "function",
        name: "execute",
        signature: "execute(bytes32[2],bytes)"
      },
      {
        fields: [
          "message",
          "offchainTokenData",
          "tokenGasOverrides"
        ],
        kind: "function",
        name: "executeSingleMessage",
        signature: "executeSingleMessage(((bytes32,uint64,uint64,uint64,uint64),bytes,bytes,address,uint256,(bytes,address,uint32,bytes,uint256)[]),bytes[],uint32[])"
      },
      {
        fields: [
          "message",
          "offchainTokenData"
        ],
        kind: "function",
        name: "executeSingleMessage",
        signature: "executeSingleMessage((uint64,address,address,uint64,uint256,bool,uint64,address,uint256,bytes,(address,uint256)[],bytes[],bytes32),bytes[])"
      },
      {
        fields: [
          "message",
          "offchainTokenData",
          "tokenGasOverrides"
        ],
        kind: "function",
        name: "executeSingleMessage",
        signature: "executeSingleMessage((uint64,address,address,uint64,uint256,bool,uint64,address,uint256,bytes,(address,uint256)[],bytes[],bytes32),bytes[],uint32[])"
      },
      {
        fields: [
          "message",
          "offchainTokenData"
        ],
        kind: "function",
        name: "executeSingleMessage",
        signature: "executeSingleMessage((uint64,uint64,uint256,address,uint64,uint256,bool,address,bytes,(address,uint256)[],address,bytes32),bytes[])"
      },
      {
        fields: [
          "destChainSelector",
          "message",
          "feeTokenAmount",
          "originalSender"
        ],
        kind: "function",
        name: "forwardFromRouter",
        signature: "forwardFromRouter(uint64,(bytes,bytes,(address,uint256)[],address,bytes),uint256,address)"
      },
      {
        fields: [],
        kind: "function",
        name: "getARM",
        signature: "getARM()"
      },
      {
        fields: [],
        kind: "function",
        name: "getAllAuthorizedCallers",
        signature: "getAllAuthorizedCallers()"
      },
      {
        fields: [],
        kind: "function",
        name: "getAllRateLimitTokens",
        signature: "getAllRateLimitTokens()"
      },
      {
        fields: [],
        kind: "function",
        name: "getAllSourceChainConfigs",
        signature: "getAllSourceChainConfigs()"
      },
      {
        fields: [
          "taggedRoot"
        ],
        kind: "function",
        name: "getBlessProgress",
        signature: "getBlessProgress((address,bytes32))"
      },
      {
        fields: [],
        kind: "function",
        name: "getConfigDetails",
        signature: "getConfigDetails()"
      },
      {
        fields: [],
        kind: "function",
        name: "getCurseProgress",
        signature: "getCurseProgress()"
      },
      {
        fields: [
          "subject"
        ],
        kind: "function",
        name: "getCurseProgress",
        signature: "getCurseProgress(bytes16)"
      },
      {
        fields: [],
        kind: "function",
        name: "getCursedSubjects",
        signature: "getCursedSubjects()"
      },
      {
        fields: [],
        kind: "function",
        name: "getCursedSubjectsCount",
        signature: "getCursedSubjectsCount()"
      },
      {
        fields: [
          "sourceToken"
        ],
        kind: "function",
        name: "getDestinationToken",
        signature: "getDestinationToken(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "getDestinationTokens",
        signature: "getDestinationTokens()"
      },
      {
        fields: [],
        kind: "function",
        name: "getDynamicConfig",
        signature: "getDynamicConfig()"
      },
      {
        fields: [
          "sequenceNumber"
        ],
        kind: "function",
        name: "getExecutionState",
        signature: "getExecutionState(uint64)"
      },
      {
        fields: [
          "sourceChainSelector",
          "sequenceNumber"
        ],
        kind: "function",
        name: "getExecutionState",
        signature: "getExecutionState(uint64,uint64)"
      },
      {
        fields: [],
        kind: "function",
        name: "getExpectedNextSequenceNumber",
        signature: "getExpectedNextSequenceNumber()"
      },
      {
        fields: [],
        kind: "function",
        name: "getLatestPriceEpochAndRound",
        signature: "getLatestPriceEpochAndRound()"
      },
      {
        fields: [],
        kind: "function",
        name: "getLatestPriceSequenceNumber",
        signature: "getLatestPriceSequenceNumber()"
      },
      {
        fields: [],
        kind: "function",
        name: "getLocalChainSelector",
        signature: "getLocalChainSelector()"
      },
      {
        fields: [
          "root"
        ],
        kind: "function",
        name: "getMerkleRoot",
        signature: "getMerkleRoot(bytes32)"
      },
      {
        fields: [
          "sourceChainSelector",
          "root"
        ],
        kind: "function",
        name: "getMerkleRoot",
        signature: "getMerkleRoot(uint64,bytes32)"
      },
      {
        fields: [],
        kind: "function",
        name: "getPermaBlessedCommitStores",
        signature: "getPermaBlessedCommitStores()"
      },
      {
        fields: [
          "destToken"
        ],
        kind: "function",
        name: "getPoolByDestToken",
        signature: "getPoolByDestToken(address)"
      },
      {
        fields: [
          "sourceToken"
        ],
        kind: "function",
        name: "getPoolBySourceToken",
        signature: "getPoolBySourceToken(address)"
      },
      {
        fields: [
          "offset",
          "limit"
        ],
        kind: "function",
        name: "getRecordedCurseRelatedOps",
        signature: "getRecordedCurseRelatedOps(uint256,uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "getRecordedCurseRelatedOpsCount",
        signature: "getRecordedCurseRelatedOpsCount()"
      },
      {
        fields: [],
        kind: "function",
        name: "getReportDigestHeader",
        signature: "getReportDigestHeader()"
      },
      {
        fields: [
          "sender"
        ],
        kind: "function",
        name: "getSenderNonce",
        signature: "getSenderNonce(address)"
      },
      {
        fields: [
          "sourceChainSelector"
        ],
        kind: "function",
        name: "getSourceChainConfig",
        signature: "getSourceChainConfig(uint64)"
      },
      {
        fields: [],
        kind: "function",
        name: "getStaticConfig",
        signature: "getStaticConfig()"
      },
      {
        fields: [],
        kind: "function",
        name: "getSupportedTokens",
        signature: "getSupportedTokens()"
      },
      {
        fields: [],
        kind: "function",
        name: "getTokenLimitAdmin",
        signature: "getTokenLimitAdmin()"
      },
      {
        fields: [],
        kind: "function",
        name: "getTransmitters",
        signature: "getTransmitters()"
      },
      {
        fields: [],
        kind: "function",
        name: "getVersionedConfig",
        signature: "getVersionedConfig()"
      },
      {
        fields: [],
        kind: "function",
        name: "isARMHealthy",
        signature: "isARMHealthy()"
      },
      {
        fields: [
          "taggedRoot"
        ],
        kind: "function",
        name: "isBlessed",
        signature: "isBlessed((address,bytes32))"
      },
      {
        fields: [
          "root"
        ],
        kind: "function",
        name: "isBlessed",
        signature: "isBlessed(bytes32)"
      },
      {
        fields: [],
        kind: "function",
        name: "isCursed",
        signature: "isCursed()"
      },
      {
        fields: [
          "subject"
        ],
        kind: "function",
        name: "isCursed",
        signature: "isCursed(bytes16)"
      },
      {
        fields: [
          "remoteChainSelector"
        ],
        kind: "function",
        name: "isSupportedChain",
        signature: "isSupportedChain(uint64)"
      },
      {
        fields: [
          "token"
        ],
        kind: "function",
        name: "isSupportedToken",
        signature: "isSupportedToken(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "isUnpausedAndARMHealthy",
        signature: "isUnpausedAndARMHealthy()"
      },
      {
        fields: [],
        kind: "function",
        name: "isUnpausedAndNotCursed",
        signature: "isUnpausedAndNotCursed()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestConfigDetails",
        signature: "latestConfigDetails()"
      },
      {
        fields: [
          "ocrPluginType"
        ],
        kind: "function",
        name: "latestConfigDetails",
        signature: "latestConfigDetails(uint8)"
      },
      {
        fields: [],
        kind: "function",
        name: "latestConfigDigestAndEpoch",
        signature: "latestConfigDigestAndEpoch()"
      },
      {
        fields: [
          "lockOrBurnIn"
        ],
        kind: "function",
        name: "lockOrBurn",
        signature: "lockOrBurn((bytes,uint64,address,uint256,address))"
      },
      {
        fields: [
          "originalSender",
          "",
          "amount",
          "remoteChainSelector",
          ""
        ],
        kind: "function",
        name: "lockOrBurn",
        signature: "lockOrBurn(address,bytes,uint256,uint64,bytes)"
      },
      {
        fields: [
          "report",
          "gasLimitOverrides"
        ],
        kind: "function",
        name: "manuallyExecute",
        signature: "manuallyExecute(((uint64,address,address,uint64,uint256,bool,uint64,address,uint256,bytes,(address,uint256)[],bytes[],bytes32)[],bytes[][],bytes32[],uint256),(uint256,uint32[])[])"
      },
      {
        fields: [
          "report",
          "gasLimitOverrides"
        ],
        kind: "function",
        name: "manuallyExecute",
        signature: "manuallyExecute(((uint64,address,address,uint64,uint256,bool,uint64,address,uint256,bytes,(address,uint256)[],bytes[],bytes32)[],bytes[][],bytes32[],uint256),uint256[])"
      },
      {
        fields: [
          "report",
          "gasLimitOverrides"
        ],
        kind: "function",
        name: "manuallyExecute",
        signature: "manuallyExecute(((uint64,uint64,uint256,address,uint64,uint256,bool,address,bytes,(address,uint256)[],address,bytes32)[],bytes[][],bytes32[],uint256),uint256[])"
      },
      {
        fields: [
          "reports",
          "gasLimitOverrides"
        ],
        kind: "function",
        name: "manuallyExecute",
        signature: "manuallyExecute((uint64,((bytes32,uint64,uint64,uint64,uint64),bytes,bytes,address,uint256,(bytes,address,uint32,bytes,uint256)[])[],bytes[][],bytes32[],uint256)[],(uint256,uint32[])[][])"
      },
      {
        fields: [
          "account",
          "amount"
        ],
        kind: "function",
        name: "mint",
        signature: "mint(address,uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "owner",
        signature: "owner()"
      },
      {
        fields: [],
        kind: "function",
        name: "ownerCurse",
        signature: "ownerCurse()"
      },
      {
        fields: [
          "curseId",
          "subjects"
        ],
        kind: "function",
        name: "ownerCurse",
        signature: "ownerCurse(bytes16,bytes16[])"
      },
      {
        fields: [
          "removes",
          "adds"
        ],
        kind: "function",
        name: "ownerRemoveThenAddPermaBlessedCommitStores",
        signature: "ownerRemoveThenAddPermaBlessedCommitStores(address[],address[])"
      },
      {
        fields: [
          "taggedRoots"
        ],
        kind: "function",
        name: "ownerResetBlessVotes",
        signature: "ownerResetBlessVotes((address,bytes32)[])"
      },
      {
        fields: [
          "ownerUnvoteToCurseRequests"
        ],
        kind: "function",
        name: "ownerUnvoteToCurse",
        signature: "ownerUnvoteToCurse((address,(bytes16,bytes28),bool)[])"
      },
      {
        fields: [
          "unvoteRecords"
        ],
        kind: "function",
        name: "ownerUnvoteToCurse",
        signature: "ownerUnvoteToCurse((address,bytes32,bool)[])"
      },
      {
        fields: [],
        kind: "function",
        name: "pause",
        signature: "pause()"
      },
      {
        fields: [],
        kind: "function",
        name: "paused",
        signature: "paused()"
      },
      {
        fields: [
          "releaseOrMintIn"
        ],
        kind: "function",
        name: "releaseOrMint",
        signature: "releaseOrMint((bytes,uint64,address,uint256,address,bytes,bytes,bytes))"
      },
      {
        fields: [
          "",
          "receiver",
          "amount",
          "remoteChainSelector",
          ""
        ],
        kind: "function",
        name: "releaseOrMint",
        signature: "releaseOrMint(bytes,address,uint256,uint64,bytes)"
      },
      {
        fields: [
          "rootToReset"
        ],
        kind: "function",
        name: "resetUnblessedRoots",
        signature: "resetUnblessedRoots(bytes32[])"
      },
      {
        fields: [
          "message",
          "gasForCallExactCheck",
          "gasLimit",
          "receiver"
        ],
        kind: "function",
        name: "routeMessage",
        signature: "routeMessage((bytes32,uint64,bytes,bytes,(address,uint256)[]),uint16,uint256,address)"
      },
      {
        fields: [
          "arm"
        ],
        kind: "function",
        name: "setARM",
        signature: "setARM(address)"
      },
      {
        fields: [
          "newAdmin"
        ],
        kind: "function",
        name: "setAdmin",
        signature: "setAdmin(address)"
      },
      {
        fields: [
          "config"
        ],
        kind: "function",
        name: "setConfig",
        signature: "setConfig(((address,address,address,uint8,uint8)[],uint16,uint16))"
      },
      {
        fields: [
          "config"
        ],
        kind: "function",
        name: "setConfig",
        signature: "setConfig(((address,address,uint8,uint8)[],uint16,uint16))"
      },
      {
        fields: [
          "newConfig"
        ],
        kind: "function",
        name: "setConfig",
        signature: "setConfig((bytes32,(address,uint64)[],uint64))"
      },
      {
        fields: [
          "dynamicConfig"
        ],
        kind: "function",
        name: "setDynamicConfig",
        signature: "setDynamicConfig((address,uint32,address))"
      },
      {
        fields: [
          "latestPriceEpochAndRound"
        ],
        kind: "function",
        name: "setLatestPriceEpochAndRound",
        signature: "setLatestPriceEpochAndRound(uint40)"
      },
      {
        fields: [
          "minSeqNr"
        ],
        kind: "function",
        name: "setMinSeqNr",
        signature: "setMinSeqNr(uint64)"
      },
      {
        fields: [
          "signers",
          "transmitters",
          "f",
          "onchainConfig",
          "offchainConfigVersion",
          "offchainConfig"
        ],
        kind: "function",
        name: "setOCR2Config",
        signature: "setOCR2Config(address[],address[],uint8,bytes,uint64,bytes)"
      },
      {
        fields: [
          "ocrConfigArgs"
        ],
        kind: "function",
        name: "setOCR3Configs",
        signature: "setOCR3Configs((bytes32,uint8,uint8,bool,address[],address[])[])"
      },
      {
        fields: [
          "config"
        ],
        kind: "function",
        name: "setRateLimiterConfig",
        signature: "setRateLimiterConfig((bool,uint128,uint128))"
      },
      {
        fields: [
          "to"
        ],
        kind: "function",
        name: "transferOwnership",
        signature: "transferOwnership(address)"
      },
      {
        fields: [
          "reportContext",
          "report",
          "rs",
          "ss",
          ""
        ],
        kind: "function",
        name: "transmit",
        signature: "transmit(bytes32[3],bytes,bytes32[],bytes32[],bytes32)"
      },
      {
        fields: [],
        kind: "function",
        name: "typeAndVersion",
        signature: "typeAndVersion()"
      },
      {
        fields: [
          "subject"
        ],
        kind: "function",
        name: "uncurse",
        signature: "uncurse(bytes16)"
      },
      {
        fields: [
          "subjects"
        ],
        kind: "function",
        name: "uncurse",
        signature: "uncurse(bytes16[])"
      },
      {
        fields: [],
        kind: "function",
        name: "unpause",
        signature: "unpause()"
      },
      {
        fields: [
          "unvoteToCurseRequests"
        ],
        kind: "function",
        name: "unvoteToCurse",
        signature: "unvoteToCurse((bytes16,bytes28)[])"
      },
      {
        fields: [
          "curseVoteAddr",
          "cursesHash"
        ],
        kind: "function",
        name: "unvoteToCurse",
        signature: "unvoteToCurse(address,bytes32)"
      },
      {
        fields: [
          "removes",
          "adds"
        ],
        kind: "function",
        name: "updateRateLimitTokens",
        signature: "updateRateLimitTokens((address,address)[],(address,address)[])"
      },
      {
        fields: [
          "offRampAddress",
          "merkleRoots",
          "signatures"
        ],
        kind: "function",
        name: "verify",
        signature: "verify(address,(uint64,bytes,uint64,uint64,bytes32)[],(bytes32,bytes32)[])"
      },
      {
        fields: [
          "hashedLeaves",
          "proofs",
          "proofFlagBits"
        ],
        kind: "function",
        name: "verify",
        signature: "verify(bytes32[],bytes32[],uint256)"
      },
      {
        fields: [
          "taggedRoots"
        ],
        kind: "function",
        name: "voteToBless",
        signature: "voteToBless((address,bytes32)[])"
      },
      {
        fields: [
          "curseId",
          "subjects"
        ],
        kind: "function",
        name: "voteToCurse",
        signature: "voteToCurse(bytes16,bytes16[])"
      },
      {
        fields: [
          "curseId"
        ],
        kind: "function",
        name: "voteToCurse",
        signature: "voteToCurse(bytes32)"
      }
    ],
    label: "Chainlink CCIP",
    vocabularies: {}
  },
  chainlink_cre: {
    entries: [
      {
        fields: [
          "updated",
          "failed",
          "nextCheckIndex"
        ],
        kind: "event",
        name: "BatchUpdated",
        signature: "BatchUpdated(uint256,uint256,uint256)"
      },
      {
        fields: [
          "index"
        ],
        kind: "event",
        name: "CheckIndexSet",
        signature: "CheckIndexSet(uint256)"
      },
      {
        fields: [
          "donId",
          "configVersion",
          "f",
          "signers"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet(uint32,uint32,uint8,address[])"
      },
      {
        fields: [
          "workflowId",
          "workflowOwner"
        ],
        kind: "event",
        name: "ExpectedWorkflowSet",
        signature: "ExpectedWorkflowSet(bytes32,address)"
      },
      {
        fields: [
          "forwarder"
        ],
        kind: "event",
        name: "ForwarderAdded",
        signature: "ForwarderAdded(address)"
      },
      {
        fields: [
          "forwarder"
        ],
        kind: "event",
        name: "ForwarderRemoved",
        signature: "ForwarderRemoved(address)"
      },
      {
        fields: [
          "size"
        ],
        kind: "event",
        name: "MaxBatchSizeSet",
        signature: "MaxBatchSizeSet(uint256)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferRequested",
        signature: "OwnershipTransferRequested(address,address)"
      },
      {
        fields: [
          "previousOwner",
          "newOwner"
        ],
        kind: "event",
        name: "OwnershipTransferStarted",
        signature: "OwnershipTransferStarted(address,address)"
      },
      {
        fields: [
          "previousOwner",
          "newOwner"
        ],
        kind: "event",
        name: "OwnershipTransferred",
        signature: "OwnershipTransferred(address,address)"
      },
      {
        fields: [
          "receiver",
          "workflowExecutionId",
          "reportId",
          "result"
        ],
        kind: "event",
        name: "ReportProcessed",
        signature: "ReportProcessed(address,bytes32,bytes2,bool)"
      },
      {
        fields: [
          "token"
        ],
        kind: "event",
        name: "TokenAdded",
        signature: "TokenAdded(address)"
      },
      {
        fields: [
          "token"
        ],
        kind: "event",
        name: "TokenRemoved",
        signature: "TokenRemoved(address)"
      },
      {
        fields: [
          "token",
          "reason"
        ],
        kind: "event",
        name: "TokenUpdateFailed",
        signature: "TokenUpdateFailed(address,bytes)"
      },
      {
        fields: [
          "forwarder",
          "workflowId",
          "workflowOwner",
          "currentTime",
          "triggerTime"
        ],
        kind: "event",
        name: "TriggerCanaryEvent",
        signature: "TriggerCanaryEvent(address,bytes32,address,uint256,uint256)"
      },
      {
        fields: [
          "updater",
          "allowed"
        ],
        kind: "event",
        name: "UpdaterSet",
        signature: "UpdaterSet(address,bool)"
      },
      {
        fields: [],
        kind: "function",
        name: "MAX_BATCH_CAP",
        signature: "MAX_BATCH_CAP()"
      },
      {
        fields: [],
        kind: "function",
        name: "METADATA_HEADER_LENGTH",
        signature: "METADATA_HEADER_LENGTH()"
      },
      {
        fields: [],
        kind: "function",
        name: "MIN_TWAP_WINDOW",
        signature: "MIN_TWAP_WINDOW()"
      },
      {
        fields: [],
        kind: "function",
        name: "ORACLE",
        signature: "ORACLE()"
      },
      {
        fields: [],
        kind: "function",
        name: "UPDATE_INTERVAL",
        signature: "UPDATE_INTERVAL()"
      },
      {
        fields: [],
        kind: "function",
        name: "acceptOwnership",
        signature: "acceptOwnership()"
      },
      {
        fields: [
          "forwarder"
        ],
        kind: "function",
        name: "addForwarder",
        signature: "addForwarder(address)"
      },
      {
        fields: [
          "token"
        ],
        kind: "function",
        name: "addToken",
        signature: "addToken(address)"
      },
      {
        fields: [
          "list"
        ],
        kind: "function",
        name: "addTokens",
        signature: "addTokens(address[])"
      },
      {
        fields: [],
        kind: "function",
        name: "checkIndex",
        signature: "checkIndex()"
      },
      {
        fields: [],
        kind: "function",
        name: "checkWork",
        signature: "checkWork()"
      },
      {
        fields: [
          "donId",
          "configVersion"
        ],
        kind: "function",
        name: "clearConfig",
        signature: "clearConfig(uint32,uint32)"
      },
      {
        fields: [],
        kind: "function",
        name: "expectedWorkflowId",
        signature: "expectedWorkflowId()"
      },
      {
        fields: [],
        kind: "function",
        name: "expectedWorkflowOwner",
        signature: "expectedWorkflowOwner()"
      },
      {
        fields: [],
        kind: "function",
        name: "getLastTimeStamp",
        signature: "getLastTimeStamp()"
      },
      {
        fields: [],
        kind: "function",
        name: "getStaleTokens",
        signature: "getStaleTokens()"
      },
      {
        fields: [],
        kind: "function",
        name: "getTokenStatuses",
        signature: "getTokenStatuses()"
      },
      {
        fields: [
          "receiver",
          "workflowExecutionId",
          "reportId"
        ],
        kind: "function",
        name: "getTransmissionId",
        signature: "getTransmissionId(address,bytes32,bytes2)"
      },
      {
        fields: [
          "receiver",
          "workflowExecutionId",
          "reportId"
        ],
        kind: "function",
        name: "getTransmissionInfo",
        signature: "getTransmissionInfo(address,bytes32,bytes2)"
      },
      {
        fields: [
          "receiver",
          "workflowExecutionId",
          "reportId"
        ],
        kind: "function",
        name: "getTransmitter",
        signature: "getTransmitter(address,bytes32,bytes2)"
      },
      {
        fields: [
          "forwarder"
        ],
        kind: "function",
        name: "isForwarder",
        signature: "isForwarder(address)"
      },
      {
        fields: [
          "caller"
        ],
        kind: "function",
        name: "isUpdater",
        signature: "isUpdater(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "lastScheduledAt",
        signature: "lastScheduledAt()"
      },
      {
        fields: [],
        kind: "function",
        name: "lastTimeStamp",
        signature: "lastTimeStamp()"
      },
      {
        fields: [],
        kind: "function",
        name: "maxBatchSize",
        signature: "maxBatchSize()"
      },
      {
        fields: [
          "metadata",
          "report"
        ],
        kind: "function",
        name: "onReport",
        signature: "onReport(bytes,bytes)"
      },
      {
        fields: [],
        kind: "function",
        name: "owner",
        signature: "owner()"
      },
      {
        fields: [],
        kind: "function",
        name: "pendingOwner",
        signature: "pendingOwner()"
      },
      {
        fields: [],
        kind: "function",
        name: "performUpkeep",
        signature: "performUpkeep()"
      },
      {
        fields: [
          "forwarder"
        ],
        kind: "function",
        name: "removeForwarder",
        signature: "removeForwarder(address)"
      },
      {
        fields: [
          "token"
        ],
        kind: "function",
        name: "removeToken",
        signature: "removeToken(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "renounceOwnership",
        signature: "renounceOwnership()"
      },
      {
        fields: [
          "receiver",
          "rawReport",
          "reportContext",
          "signatures"
        ],
        kind: "function",
        name: "report",
        signature: "report(address,bytes,bytes,bytes[])"
      },
      {
        fields: [
          "transmissionId",
          "transmitter",
          "receiver",
          "metadata",
          "validatedReport"
        ],
        kind: "function",
        name: "route",
        signature: "route(bytes32,address,address,bytes,bytes)"
      },
      {
        fields: [
          "index"
        ],
        kind: "function",
        name: "setCheckIndex",
        signature: "setCheckIndex(uint256)"
      },
      {
        fields: [
          "donId",
          "configVersion",
          "f",
          "signers"
        ],
        kind: "function",
        name: "setConfig",
        signature: "setConfig(uint32,uint32,uint8,address[])"
      },
      {
        fields: [
          "workflowId",
          "workflowOwner"
        ],
        kind: "function",
        name: "setExpectedWorkflow",
        signature: "setExpectedWorkflow(bytes32,address)"
      },
      {
        fields: [
          "size"
        ],
        kind: "function",
        name: "setMaxBatchSize",
        signature: "setMaxBatchSize(uint256)"
      },
      {
        fields: [
          "updater",
          "allowed"
        ],
        kind: "function",
        name: "setUpdater",
        signature: "setUpdater(address,bool)"
      },
      {
        fields: [
          "interfaceId"
        ],
        kind: "function",
        name: "supportsInterface",
        signature: "supportsInterface(bytes4)"
      },
      {
        fields: [
          ""
        ],
        kind: "function",
        name: "tokens",
        signature: "tokens(uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "tokensLength",
        signature: "tokensLength()"
      },
      {
        fields: [
          "newOwner"
        ],
        kind: "function",
        name: "transferOwnership",
        signature: "transferOwnership(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "typeAndVersion",
        signature: "typeAndVersion()"
      }
    ],
    label: "Chainlink CRE",
    vocabularies: {
      cre_delivery: {
        false: "Delivery failed",
        true: "Delivered"
      }
    }
  },
  chainlink_cre_automation_receiver: {
    entries: [],
    label: "CRE Automation receiver",
    vocabularies: {}
  },
  chainlink_cre_workflow_router: {
    entries: [],
    label: "CRE workflow router",
    vocabularies: {}
  },
  chainlink_cron_upkeep: {
    entries: [
      {
        fields: [
          "id",
          "target",
          "handler"
        ],
        kind: "event",
        name: "CronJobCreated",
        signature: "CronJobCreated(uint256,address,bytes)"
      },
      {
        fields: [
          "id"
        ],
        kind: "event",
        name: "CronJobDeleted",
        signature: "CronJobDeleted(uint256)"
      },
      {
        fields: [
          "id",
          "success"
        ],
        kind: "event",
        name: "CronJobExecuted",
        signature: "CronJobExecuted(uint256,bool)"
      },
      {
        fields: [
          "id",
          "timestamp"
        ],
        kind: "event",
        name: "CronJobExecuted",
        signature: "CronJobExecuted(uint256,uint256)"
      },
      {
        fields: [
          "id",
          "target",
          "handler"
        ],
        kind: "event",
        name: "CronJobUpdated",
        signature: "CronJobUpdated(uint256,address,bytes)"
      },
      {
        fields: [
          "upkeep",
          "owner"
        ],
        kind: "event",
        name: "NewCronUpkeepCreated",
        signature: "NewCronUpkeepCreated(address,address)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferRequested",
        signature: "OwnershipTransferRequested(address,address)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferred",
        signature: "OwnershipTransferred(address,address)"
      },
      {
        fields: [
          "account"
        ],
        kind: "event",
        name: "Paused",
        signature: "Paused(address)"
      },
      {
        fields: [
          "oldForwarder",
          "newForwarder"
        ],
        kind: "event",
        name: "PermissionedForwarderUpdated",
        signature: "PermissionedForwarderUpdated(address,address)"
      },
      {
        fields: [
          "account"
        ],
        kind: "event",
        name: "Unpaused",
        signature: "Unpaused(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "acceptOwnership",
        signature: "acceptOwnership()"
      },
      {
        fields: [
          ""
        ],
        kind: "function",
        name: "checkUpkeep",
        signature: "checkUpkeep(bytes)"
      },
      {
        fields: [
          "target",
          "handler",
          "encodedCronSpec"
        ],
        kind: "function",
        name: "createCronJobFromEncodedSpec",
        signature: "createCronJobFromEncodedSpec(address,bytes,bytes)"
      },
      {
        fields: [],
        kind: "function",
        name: "cronDelegateAddress",
        signature: "cronDelegateAddress()"
      },
      {
        fields: [
          "id"
        ],
        kind: "function",
        name: "deleteCronJob",
        signature: "deleteCronJob(uint256)"
      },
      {
        fields: [
          "target",
          "handler",
          "cronString"
        ],
        kind: "function",
        name: "encodeCronJob",
        signature: "encodeCronJob(address,bytes,string)"
      },
      {
        fields: [
          "cronString"
        ],
        kind: "function",
        name: "encodeCronString",
        signature: "encodeCronString(string)"
      },
      {
        fields: [],
        kind: "function",
        name: "getActiveCronJobIDs",
        signature: "getActiveCronJobIDs()"
      },
      {
        fields: [
          "id"
        ],
        kind: "function",
        name: "getCronJob",
        signature: "getCronJob(uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "getPermissionedForwarder",
        signature: "getPermissionedForwarder()"
      },
      {
        fields: [],
        kind: "function",
        name: "newCronUpkeep",
        signature: "newCronUpkeep()"
      },
      {
        fields: [
          "encodedJob"
        ],
        kind: "function",
        name: "newCronUpkeepWithJob",
        signature: "newCronUpkeepWithJob(bytes)"
      },
      {
        fields: [],
        kind: "function",
        name: "owner",
        signature: "owner()"
      },
      {
        fields: [],
        kind: "function",
        name: "pause",
        signature: "pause()"
      },
      {
        fields: [],
        kind: "function",
        name: "paused",
        signature: "paused()"
      },
      {
        fields: [
          "performData"
        ],
        kind: "function",
        name: "performUpkeep",
        signature: "performUpkeep(bytes)"
      },
      {
        fields: [],
        kind: "function",
        name: "s_maxJobs",
        signature: "s_maxJobs()"
      },
      {
        fields: [
          "maxJobs"
        ],
        kind: "function",
        name: "setMaxJobs",
        signature: "setMaxJobs(uint256)"
      },
      {
        fields: [
          "newForwarder"
        ],
        kind: "function",
        name: "setPermissionedForwarder",
        signature: "setPermissionedForwarder(address)"
      },
      {
        fields: [
          "to"
        ],
        kind: "function",
        name: "transferOwnership",
        signature: "transferOwnership(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "unpause",
        signature: "unpause()"
      },
      {
        fields: [
          "id",
          "newTarget",
          "newHandler",
          "newEncodedCronSpec"
        ],
        kind: "function",
        name: "updateCronJob",
        signature: "updateCronJob(uint256,address,bytes,bytes)"
      }
    ],
    label: "Chainlink Cron Upkeep",
    vocabularies: {}
  },
  chainlink_data_feeds: {
    entries: [
      {
        fields: [
          "user"
        ],
        kind: "event",
        name: "AddedAccess",
        signature: "AddedAccess(address)"
      },
      {
        fields: [
          "current",
          "roundId",
          "updatedAt"
        ],
        kind: "event",
        name: "AnswerUpdated",
        signature: "AnswerUpdated(int256,uint256,uint256)"
      },
      {
        fields: [
          "senders",
          "changedBy"
        ],
        kind: "event",
        name: "AuthorizedSendersChanged",
        signature: "AuthorizedSendersChanged(address[],address)"
      },
      {
        fields: [
          "old",
          "current"
        ],
        kind: "event",
        name: "BillingAccessControllerSet",
        signature: "BillingAccessControllerSet(address,address)"
      },
      {
        fields: [
          "maximumGasPriceGwei",
          "reasonableGasPriceGwei",
          "observationPaymentGjuels",
          "transmissionPaymentGjuels",
          "accountingGas"
        ],
        kind: "event",
        name: "BillingSet",
        signature: "BillingSet(uint32,uint32,uint32,uint32,uint24)"
      },
      {
        fields: [
          "maximumGasPrice",
          "reasonableGasPrice",
          "microLinkPerEth",
          "linkGweiPerObservation",
          "linkGweiPerTransmission"
        ],
        kind: "event",
        name: "BillingSet",
        signature: "BillingSet(uint32,uint32,uint32,uint32,uint32)"
      },
      {
        fields: [],
        kind: "event",
        name: "CheckAccessDisabled",
        signature: "CheckAccessDisabled()"
      },
      {
        fields: [],
        kind: "event",
        name: "CheckAccessEnabled",
        signature: "CheckAccessEnabled()"
      },
      {
        fields: [
          "previousConfigBlockNumber",
          "configDigest",
          "configCount",
          "signers",
          "transmitters",
          "f",
          "onchainConfig",
          "offchainConfigVersion",
          "offchainConfig"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet(uint32,bytes32,uint64,address[],address[],uint8,bytes,uint64,bytes)"
      },
      {
        fields: [
          "previousConfigBlockNumber",
          "configCount",
          "signers",
          "transmitters",
          "threshold",
          "encodedConfigVersion",
          "encoded"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet(uint32,uint64,address[],address[],uint8,uint64,bytes)"
      },
      {
        fields: [
          "cutoffTime"
        ],
        kind: "event",
        name: "CutoffTimeSet",
        signature: "CutoffTimeSet(uint32)"
      },
      {
        fields: [
          "oldLinkToken",
          "newLinkToken"
        ],
        kind: "event",
        name: "LinkTokenSet",
        signature: "LinkTokenSet(address,address)"
      },
      {
        fields: [
          "roundId",
          "startedBy",
          "startedAt"
        ],
        kind: "event",
        name: "NewRound",
        signature: "NewRound(uint256,address,uint256)"
      },
      {
        fields: [
          "aggregatorRoundId",
          "answer",
          "transmitter",
          "observations",
          "observers",
          "rawReportContext"
        ],
        kind: "event",
        name: "NewTransmission",
        signature: "NewTransmission(uint32,int192,address,int192[],bytes,bytes32)"
      },
      {
        fields: [
          "aggregatorRoundId",
          "answer",
          "transmitter",
          "observationsTimestamp",
          "observations",
          "observers",
          "juelsPerFeeCoin",
          "configDigest",
          "epochAndRound"
        ],
        kind: "event",
        name: "NewTransmission",
        signature: "NewTransmission(uint32,int192,address,uint32,int192[],bytes,int192,bytes32,uint40)"
      },
      {
        fields: [
          "transmitter",
          "payee",
          "amount",
          "linkToken"
        ],
        kind: "event",
        name: "OraclePaid",
        signature: "OraclePaid(address,address,uint256,address)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferRequested",
        signature: "OwnershipTransferRequested(address,address)"
      },
      {
        fields: [
          "from",
          "to",
          "message"
        ],
        kind: "event",
        name: "OwnershipTransferRequestedWithMessage",
        signature: "OwnershipTransferRequestedWithMessage(address,address,bytes)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferred",
        signature: "OwnershipTransferred(address,address)"
      },
      {
        fields: [
          "transmitter",
          "current",
          "proposed"
        ],
        kind: "event",
        name: "PayeeshipTransferRequested",
        signature: "PayeeshipTransferRequested(address,address,address)"
      },
      {
        fields: [
          "transmitter",
          "previous",
          "current"
        ],
        kind: "event",
        name: "PayeeshipTransferred",
        signature: "PayeeshipTransferred(address,address,address)"
      },
      {
        fields: [
          "primaryRoundId"
        ],
        kind: "event",
        name: "PrimaryFeedUnlocked",
        signature: "PrimaryFeedUnlocked(uint32)"
      },
      {
        fields: [
          "user"
        ],
        kind: "event",
        name: "RemovedAccess",
        signature: "RemovedAccess(address)"
      },
      {
        fields: [
          "old",
          "current"
        ],
        kind: "event",
        name: "RequesterAccessControllerSet",
        signature: "RequesterAccessControllerSet(address,address)"
      },
      {
        fields: [
          "requester",
          "configDigest",
          "epoch",
          "round"
        ],
        kind: "event",
        name: "RoundRequested",
        signature: "RoundRequested(address,bytes16,uint32,uint8)"
      },
      {
        fields: [
          "requester",
          "configDigest",
          "epoch",
          "round"
        ],
        kind: "event",
        name: "RoundRequested",
        signature: "RoundRequested(address,bytes32,uint32,uint8)"
      },
      {
        fields: [
          "secondaryRoundId"
        ],
        kind: "event",
        name: "SecondaryRoundIdUpdated",
        signature: "SecondaryRoundIdUpdated(uint32)"
      },
      {
        fields: [
          "configDigest",
          "epoch"
        ],
        kind: "event",
        name: "Transmitted",
        signature: "Transmitted(bytes32,uint32)"
      },
      {
        fields: [
          "previousValidator",
          "previousGasLimit",
          "currentValidator",
          "currentGasLimit"
        ],
        kind: "event",
        name: "ValidatorConfigSet",
        signature: "ValidatorConfigSet(address,uint32,address,uint32)"
      },
      {
        fields: [],
        kind: "function",
        name: "acceptOwnership",
        signature: "acceptOwnership()"
      },
      {
        fields: [
          "transmitter"
        ],
        kind: "function",
        name: "acceptPayeeship",
        signature: "acceptPayeeship(address)"
      },
      {
        fields: [
          "_user"
        ],
        kind: "function",
        name: "addAccess",
        signature: "addAccess(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "billingAccessController",
        signature: "billingAccessController()"
      },
      {
        fields: [],
        kind: "function",
        name: "checkEnabled",
        signature: "checkEnabled()"
      },
      {
        fields: [],
        kind: "function",
        name: "decimals",
        signature: "decimals()"
      },
      {
        fields: [],
        kind: "function",
        name: "description",
        signature: "description()"
      },
      {
        fields: [],
        kind: "function",
        name: "disableAccessCheck",
        signature: "disableAccessCheck()"
      },
      {
        fields: [],
        kind: "function",
        name: "enableAccessCheck",
        signature: "enableAccessCheck()"
      },
      {
        fields: [
          "to",
          "data"
        ],
        kind: "function",
        name: "forward",
        signature: "forward(address,bytes)"
      },
      {
        fields: [
          "_roundId"
        ],
        kind: "function",
        name: "getAnswer",
        signature: "getAnswer(uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "getAuthorizedSenders",
        signature: "getAuthorizedSenders()"
      },
      {
        fields: [],
        kind: "function",
        name: "getBilling",
        signature: "getBilling()"
      },
      {
        fields: [],
        kind: "function",
        name: "getBillingAccessController",
        signature: "getBillingAccessController()"
      },
      {
        fields: [],
        kind: "function",
        name: "getLinkToken",
        signature: "getLinkToken()"
      },
      {
        fields: [],
        kind: "function",
        name: "getRequesterAccessController",
        signature: "getRequesterAccessController()"
      },
      {
        fields: [
          "_roundId"
        ],
        kind: "function",
        name: "getRoundData",
        signature: "getRoundData(uint80)"
      },
      {
        fields: [
          "_roundId"
        ],
        kind: "function",
        name: "getTimestamp",
        signature: "getTimestamp(uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "getTransmitters",
        signature: "getTransmitters()"
      },
      {
        fields: [],
        kind: "function",
        name: "getValidatorConfig",
        signature: "getValidatorConfig()"
      },
      {
        fields: [
          "_user",
          "_calldata"
        ],
        kind: "function",
        name: "hasAccess",
        signature: "hasAccess(address,bytes)"
      },
      {
        fields: [
          "sender"
        ],
        kind: "function",
        name: "isAuthorizedSender",
        signature: "isAuthorizedSender(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "latestAnswer",
        signature: "latestAnswer()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestConfigDetails",
        signature: "latestConfigDetails()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestConfigDigestAndEpoch",
        signature: "latestConfigDigestAndEpoch()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestRound",
        signature: "latestRound()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestRoundData",
        signature: "latestRoundData()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestTimestamp",
        signature: "latestTimestamp()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestTransmissionDetails",
        signature: "latestTransmissionDetails()"
      },
      {
        fields: [],
        kind: "function",
        name: "linkAvailableForPayment",
        signature: "linkAvailableForPayment()"
      },
      {
        fields: [],
        kind: "function",
        name: "linkToken",
        signature: "linkToken()"
      },
      {
        fields: [],
        kind: "function",
        name: "maxAnswer",
        signature: "maxAnswer()"
      },
      {
        fields: [],
        kind: "function",
        name: "minAnswer",
        signature: "minAnswer()"
      },
      {
        fields: [
          "tos",
          "datas"
        ],
        kind: "function",
        name: "multiForward",
        signature: "multiForward(address[],bytes[])"
      },
      {
        fields: [
          "transmitterAddress"
        ],
        kind: "function",
        name: "oracleObservationCount",
        signature: "oracleObservationCount(address)"
      },
      {
        fields: [
          "transmitterAddress"
        ],
        kind: "function",
        name: "owedPayment",
        signature: "owedPayment(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "owner",
        signature: "owner()"
      },
      {
        fields: [
          "to",
          "data"
        ],
        kind: "function",
        name: "ownerForward",
        signature: "ownerForward(address,bytes)"
      },
      {
        fields: [
          "_user"
        ],
        kind: "function",
        name: "removeAccess",
        signature: "removeAccess(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "requestNewRound",
        signature: "requestNewRound()"
      },
      {
        fields: [],
        kind: "function",
        name: "requesterAccessController",
        signature: "requesterAccessController()"
      },
      {
        fields: [
          "senders"
        ],
        kind: "function",
        name: "setAuthorizedSenders",
        signature: "setAuthorizedSenders(address[])"
      },
      {
        fields: [
          "maximumGasPriceGwei",
          "reasonableGasPriceGwei",
          "observationPaymentGjuels",
          "transmissionPaymentGjuels",
          "accountingGas"
        ],
        kind: "function",
        name: "setBilling",
        signature: "setBilling(uint32,uint32,uint32,uint32,uint24)"
      },
      {
        fields: [
          "_maximumGasPrice",
          "_reasonableGasPrice",
          "_microLinkPerEth",
          "_linkGweiPerObservation",
          "_linkGweiPerTransmission"
        ],
        kind: "function",
        name: "setBilling",
        signature: "setBilling(uint32,uint32,uint32,uint32,uint32)"
      },
      {
        fields: [
          "_billingAccessController"
        ],
        kind: "function",
        name: "setBillingAccessController",
        signature: "setBillingAccessController(address)"
      },
      {
        fields: [
          "signers",
          "transmitters",
          "f",
          "onchainConfig",
          "offchainConfigVersion",
          "offchainConfig"
        ],
        kind: "function",
        name: "setConfig",
        signature: "setConfig(address[],address[],uint8,bytes,uint64,bytes)"
      },
      {
        fields: [
          "_signers",
          "_transmitters",
          "_threshold",
          "_encodedConfigVersion",
          "_encoded"
        ],
        kind: "function",
        name: "setConfig",
        signature: "setConfig(address[],address[],uint8,uint64,bytes)"
      },
      {
        fields: [
          "_cutoffTime"
        ],
        kind: "function",
        name: "setCutoffTime",
        signature: "setCutoffTime(uint32)"
      },
      {
        fields: [
          "linkToken",
          "recipient"
        ],
        kind: "function",
        name: "setLinkToken",
        signature: "setLinkToken(address,address)"
      },
      {
        fields: [
          "transmitters",
          "payees"
        ],
        kind: "function",
        name: "setPayees",
        signature: "setPayees(address[],address[])"
      },
      {
        fields: [
          "requesterAccessController"
        ],
        kind: "function",
        name: "setRequesterAccessController",
        signature: "setRequesterAccessController(address)"
      },
      {
        fields: [
          "newValidator",
          "newGasLimit"
        ],
        kind: "function",
        name: "setValidatorConfig",
        signature: "setValidatorConfig(address,uint32)"
      },
      {
        fields: [
          "to"
        ],
        kind: "function",
        name: "transferOwnership",
        signature: "transferOwnership(address)"
      },
      {
        fields: [
          "to",
          "message"
        ],
        kind: "function",
        name: "transferOwnershipWithMessage",
        signature: "transferOwnershipWithMessage(address,bytes)"
      },
      {
        fields: [
          "transmitter",
          "proposed"
        ],
        kind: "function",
        name: "transferPayeeship",
        signature: "transferPayeeship(address,address)"
      },
      {
        fields: [
          "_report",
          "_rs",
          "_ss",
          "_rawVs"
        ],
        kind: "function",
        name: "transmit",
        signature: "transmit(bytes,bytes32[],bytes32[],bytes32)"
      },
      {
        fields: [
          "reportContext",
          "report",
          "rs",
          "ss",
          "rawVs"
        ],
        kind: "function",
        name: "transmit",
        signature: "transmit(bytes32[3],bytes,bytes32[],bytes32[],bytes32)"
      },
      {
        fields: [
          "reportContext",
          "report",
          "rs",
          "ss",
          "rawVs"
        ],
        kind: "function",
        name: "transmitSecondary",
        signature: "transmitSecondary(bytes32[3],bytes,bytes32[],bytes32[],bytes32)"
      },
      {
        fields: [],
        kind: "function",
        name: "transmitters",
        signature: "transmitters()"
      },
      {
        fields: [],
        kind: "function",
        name: "typeAndVersion",
        signature: "typeAndVersion()"
      },
      {
        fields: [],
        kind: "function",
        name: "validatorConfig",
        signature: "validatorConfig()"
      },
      {
        fields: [],
        kind: "function",
        name: "version",
        signature: "version()"
      },
      {
        fields: [
          "recipient",
          "amount"
        ],
        kind: "function",
        name: "withdrawFunds",
        signature: "withdrawFunds(address,uint256)"
      },
      {
        fields: [
          "transmitter"
        ],
        kind: "function",
        name: "withdrawPayment",
        signature: "withdrawPayment(address)"
      }
    ],
    label: "Chainlink Data Feeds",
    vocabularies: {}
  },
  chainlink_data_feeds_cache: {
    entries: [
      {
        fields: [
          "current",
          "roundId",
          "updatedAt"
        ],
        kind: "event",
        name: "AnswerUpdated",
        signature: "AnswerUpdated(int256,uint256,uint256)"
      },
      {
        fields: [
          "dataId",
          "decimals",
          "description",
          "workflowMetadata"
        ],
        kind: "event",
        name: "BundleFeedConfigSet",
        signature: "BundleFeedConfigSet(bytes16,uint8[],string,(address,address,bytes10)[])"
      },
      {
        fields: [
          "dataId",
          "timestamp",
          "bundle"
        ],
        kind: "event",
        name: "BundleReportUpdated",
        signature: "BundleReportUpdated(bytes16,uint256,bytes)"
      },
      {
        fields: [
          "dataId",
          "decimals",
          "description",
          "workflowMetadata"
        ],
        kind: "event",
        name: "DecimalFeedConfigSet",
        signature: "DecimalFeedConfigSet(bytes16,uint8,string,(address,address,bytes10)[])"
      },
      {
        fields: [
          "dataId",
          "roundId",
          "timestamp",
          "answer"
        ],
        kind: "event",
        name: "DecimalReportUpdated",
        signature: "DecimalReportUpdated(bytes16,uint256,uint256,uint224)"
      },
      {
        fields: [
          "feedAdmin",
          "isAdmin"
        ],
        kind: "event",
        name: "FeedAdminSet",
        signature: "FeedAdminSet(address,bool)"
      },
      {
        fields: [
          "dataId"
        ],
        kind: "event",
        name: "FeedConfigRemoved",
        signature: "FeedConfigRemoved(bytes16)"
      },
      {
        fields: [
          "dataId",
          "sender",
          "workflowOwner",
          "workflowName"
        ],
        kind: "event",
        name: "InvalidUpdatePermission",
        signature: "InvalidUpdatePermission(bytes16,address,address,bytes10)"
      },
      {
        fields: [
          "roundId",
          "startedBy",
          "startedAt"
        ],
        kind: "event",
        name: "NewRound",
        signature: "NewRound(uint256,address,uint256)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferRequested",
        signature: "OwnershipTransferRequested(address,address)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferred",
        signature: "OwnershipTransferred(address,address)"
      },
      {
        fields: [
          "proxy",
          "dataId"
        ],
        kind: "event",
        name: "ProxyDataIdRemoved",
        signature: "ProxyDataIdRemoved(address,bytes16)"
      },
      {
        fields: [
          "proxy",
          "dataId"
        ],
        kind: "event",
        name: "ProxyDataIdUpdated",
        signature: "ProxyDataIdUpdated(address,bytes16)"
      },
      {
        fields: [
          "dataId",
          "reportTimestamp",
          "latestTimestamp"
        ],
        kind: "event",
        name: "StaleBundleReport",
        signature: "StaleBundleReport(bytes16,uint256,uint256)"
      },
      {
        fields: [
          "dataId",
          "reportTimestamp",
          "latestTimestamp"
        ],
        kind: "event",
        name: "StaleDecimalReport",
        signature: "StaleDecimalReport(bytes16,uint256,uint256)"
      },
      {
        fields: [
          "token",
          "to",
          "amount"
        ],
        kind: "event",
        name: "TokenRecovered",
        signature: "TokenRecovered(address,address,uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "acceptOwnership",
        signature: "acceptOwnership()"
      },
      {
        fields: [],
        kind: "function",
        name: "bundleDecimals",
        signature: "bundleDecimals()"
      },
      {
        fields: [
          "dataId",
          "workflowMetadata"
        ],
        kind: "function",
        name: "checkFeedPermission",
        signature: "checkFeedPermission(bytes16,(address,address,bytes10))"
      },
      {
        fields: [],
        kind: "function",
        name: "decimals",
        signature: "decimals()"
      },
      {
        fields: [],
        kind: "function",
        name: "description",
        signature: "description()"
      },
      {
        fields: [
          "roundId"
        ],
        kind: "function",
        name: "getAnswer",
        signature: "getAnswer(uint256)"
      },
      {
        fields: [
          "dataId"
        ],
        kind: "function",
        name: "getBundleDecimals",
        signature: "getBundleDecimals(bytes16)"
      },
      {
        fields: [
          "proxy"
        ],
        kind: "function",
        name: "getDataIdForProxy",
        signature: "getDataIdForProxy(address)"
      },
      {
        fields: [
          "dataId"
        ],
        kind: "function",
        name: "getDecimals",
        signature: "getDecimals(bytes16)"
      },
      {
        fields: [
          "dataId"
        ],
        kind: "function",
        name: "getDescription",
        signature: "getDescription(bytes16)"
      },
      {
        fields: [
          "dataId",
          "startIndex",
          "maxCount"
        ],
        kind: "function",
        name: "getFeedMetadata",
        signature: "getFeedMetadata(bytes16,uint256,uint256)"
      },
      {
        fields: [
          "dataId"
        ],
        kind: "function",
        name: "getLatestAnswer",
        signature: "getLatestAnswer(bytes16)"
      },
      {
        fields: [
          "dataId"
        ],
        kind: "function",
        name: "getLatestBundle",
        signature: "getLatestBundle(bytes16)"
      },
      {
        fields: [
          "dataId"
        ],
        kind: "function",
        name: "getLatestBundleTimestamp",
        signature: "getLatestBundleTimestamp(bytes16)"
      },
      {
        fields: [
          "dataId"
        ],
        kind: "function",
        name: "getLatestRoundData",
        signature: "getLatestRoundData(bytes16)"
      },
      {
        fields: [
          "dataId"
        ],
        kind: "function",
        name: "getLatestTimestamp",
        signature: "getLatestTimestamp(bytes16)"
      },
      {
        fields: [
          "roundId"
        ],
        kind: "function",
        name: "getRoundData",
        signature: "getRoundData(uint80)"
      },
      {
        fields: [
          "roundId"
        ],
        kind: "function",
        name: "getTimestamp",
        signature: "getTimestamp(uint256)"
      },
      {
        fields: [
          "feedAdmin"
        ],
        kind: "function",
        name: "isFeedAdmin",
        signature: "isFeedAdmin(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "latestAnswer",
        signature: "latestAnswer()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestBundle",
        signature: "latestBundle()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestBundleTimestamp",
        signature: "latestBundleTimestamp()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestRound",
        signature: "latestRound()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestRoundData",
        signature: "latestRoundData()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestTimestamp",
        signature: "latestTimestamp()"
      },
      {
        fields: [
          "metadata",
          "report"
        ],
        kind: "function",
        name: "onReport",
        signature: "onReport(bytes,bytes)"
      },
      {
        fields: [],
        kind: "function",
        name: "owner",
        signature: "owner()"
      },
      {
        fields: [
          "token",
          "to",
          "amount"
        ],
        kind: "function",
        name: "recoverTokens",
        signature: "recoverTokens(address,address,uint256)"
      },
      {
        fields: [
          "proxies"
        ],
        kind: "function",
        name: "removeDataIdMappingsForProxies",
        signature: "removeDataIdMappingsForProxies(address[])"
      },
      {
        fields: [
          "dataIds"
        ],
        kind: "function",
        name: "removeFeedConfigs",
        signature: "removeFeedConfigs(bytes16[])"
      },
      {
        fields: [
          "dataIds",
          "descriptions",
          "decimalsMatrix",
          "workflowMetadata"
        ],
        kind: "function",
        name: "setBundleFeedConfigs",
        signature: "setBundleFeedConfigs(bytes16[],string[],uint8[][],(address,address,bytes10)[])"
      },
      {
        fields: [
          "dataIds",
          "descriptions",
          "workflowMetadata"
        ],
        kind: "function",
        name: "setDecimalFeedConfigs",
        signature: "setDecimalFeedConfigs(bytes16[],string[],(address,address,bytes10)[])"
      },
      {
        fields: [
          "feedAdmin",
          "isAdmin"
        ],
        kind: "function",
        name: "setFeedAdmin",
        signature: "setFeedAdmin(address,bool)"
      },
      {
        fields: [
          "interfaceId"
        ],
        kind: "function",
        name: "supportsInterface",
        signature: "supportsInterface(bytes4)"
      },
      {
        fields: [
          "to"
        ],
        kind: "function",
        name: "transferOwnership",
        signature: "transferOwnership(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "typeAndVersion",
        signature: "typeAndVersion()"
      },
      {
        fields: [
          "proxies",
          "dataIds"
        ],
        kind: "function",
        name: "updateDataIdMappingsForProxies",
        signature: "updateDataIdMappingsForProxies(address[],bytes16[])"
      },
      {
        fields: [],
        kind: "function",
        name: "version",
        signature: "version()"
      }
    ],
    label: "Chainlink Data Feeds Cache",
    vocabularies: {
      cache_feed: {
        "0x02000001070700030000000000000000": "S&P Global SSA EURC Feed",
        "0x02000001080700030000000000000000": "S&P Global SSA EURCV Feed",
        "0x02000001090700030000000000000000": "S&P Global SSA FDUSD Feed",
        "0x02000001100700030000000000000000": "S&P Global SSA GUSD Feed",
        "0x02000001110700030000000000000000": "S&P Global SSA TUSD Feed",
        "0x02000001120700030000000000000000": "S&P Global SSA USDe Feed",
        "0x02000001130700030000000000000000": "S&P Global SSA USDP Feed",
        "0x02000001140700030000000000000000": "S&P Global SSA USDS/DAI Feed",
        "0x02000001150700030000000000000000": "S&P Global SSA USDT Feed",
        "0x02000001160700030000000000000000": "S&P Global SSA USDC Feed",
        "0x02000001250700030000000000000000": "S&P Global SSA USDG Feed",
        "0x25f193c277a24fe5e349c9154de6a436": "USD1 BitGo Reported Reserves"
      }
    }
  },
  chainlink_data_streams: {
    entries: [
      {
        fields: [
          "oldAccessController",
          "newAccessController"
        ],
        kind: "event",
        name: "AccessControllerSet",
        signature: "AccessControllerSet(address,address)"
      },
      {
        fields: [
          "configDigest"
        ],
        kind: "event",
        name: "ConfigActivated",
        signature: "ConfigActivated(bytes32)"
      },
      {
        fields: [
          "configDigest"
        ],
        kind: "event",
        name: "ConfigDeactivated",
        signature: "ConfigDeactivated(bytes32)"
      },
      {
        fields: [
          "configDigest",
          "signers",
          "f"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet(bytes32,address[],uint8)"
      },
      {
        fields: [
          "configDigest",
          "prevSigners",
          "newSigners"
        ],
        kind: "event",
        name: "ConfigUpdated",
        signature: "ConfigUpdated(bytes32,address[],address[])"
      },
      {
        fields: [
          "oldFeeManager",
          "newFeeManager"
        ],
        kind: "event",
        name: "FeeManagerSet",
        signature: "FeeManagerSet(address,address)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferRequested",
        signature: "OwnershipTransferRequested(address,address)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferred",
        signature: "OwnershipTransferred(address,address)"
      },
      {
        fields: [
          "feedId",
          "requester"
        ],
        kind: "event",
        name: "ReportVerified",
        signature: "ReportVerified(bytes32,address)"
      },
      {
        fields: [
          "verifierAddress"
        ],
        kind: "event",
        name: "VerifierInitialized",
        signature: "VerifierInitialized(address)"
      },
      {
        fields: [
          "oldConfigDigest",
          "newConfigDigest",
          "verifierAddress"
        ],
        kind: "event",
        name: "VerifierSet",
        signature: "VerifierSet(bytes32,bytes32,address)"
      },
      {
        fields: [
          "configDigest",
          "verifierAddress"
        ],
        kind: "event",
        name: "VerifierUnset",
        signature: "VerifierUnset(bytes32,address)"
      },
      {
        fields: [],
        kind: "function",
        name: "acceptOwnership",
        signature: "acceptOwnership()"
      },
      {
        fields: [
          "configDigest"
        ],
        kind: "function",
        name: "activateConfig",
        signature: "activateConfig(bytes32)"
      },
      {
        fields: [
          "configDigest"
        ],
        kind: "function",
        name: "deactivateConfig",
        signature: "deactivateConfig(bytes32)"
      },
      {
        fields: [
          "",
          "",
          ""
        ],
        kind: "function",
        name: "getFeeAndReward",
        signature: "getFeeAndReward(address,bytes,address)"
      },
      {
        fields: [
          "configDigest"
        ],
        kind: "function",
        name: "getVerifier",
        signature: "getVerifier(bytes32)"
      },
      {
        fields: [],
        kind: "function",
        name: "i_linkAddress",
        signature: "i_linkAddress()"
      },
      {
        fields: [],
        kind: "function",
        name: "i_nativeAddress",
        signature: "i_nativeAddress()"
      },
      {
        fields: [],
        kind: "function",
        name: "i_rewardManager",
        signature: "i_rewardManager()"
      },
      {
        fields: [
          "verifierAddress"
        ],
        kind: "function",
        name: "initializeVerifier",
        signature: "initializeVerifier(address)"
      },
      {
        fields: [
          "configDigest"
        ],
        kind: "function",
        name: "latestConfigDetails",
        signature: "latestConfigDetails(bytes32)"
      },
      {
        fields: [],
        kind: "function",
        name: "linkAvailableForPayment",
        signature: "linkAvailableForPayment()"
      },
      {
        fields: [],
        kind: "function",
        name: "owner",
        signature: "owner()"
      },
      {
        fields: [
          ""
        ],
        kind: "function",
        name: "payLinkDeficit",
        signature: "payLinkDeficit(bytes32)"
      },
      {
        fields: [
          "",
          "",
          "subscriber"
        ],
        kind: "function",
        name: "processFee",
        signature: "processFee(bytes,bytes,address)"
      },
      {
        fields: [
          "",
          "",
          "subscriber"
        ],
        kind: "function",
        name: "processFeeBulk",
        signature: "processFeeBulk(bytes[],bytes,address)"
      },
      {
        fields: [],
        kind: "function",
        name: "s_accessController",
        signature: "s_accessController()"
      },
      {
        fields: [],
        kind: "function",
        name: "s_feeManager",
        signature: "s_feeManager()"
      },
      {
        fields: [
          "",
          ""
        ],
        kind: "function",
        name: "s_globalDiscounts",
        signature: "s_globalDiscounts(address,address)"
      },
      {
        fields: [],
        kind: "function",
        name: "s_nativeSurcharge",
        signature: "s_nativeSurcharge()"
      },
      {
        fields: [
          "",
          "",
          ""
        ],
        kind: "function",
        name: "s_subscriberDiscounts",
        signature: "s_subscriberDiscounts(address,bytes32,address)"
      },
      {
        fields: [
          "accessController"
        ],
        kind: "function",
        name: "setAccessController",
        signature: "setAccessController(address)"
      },
      {
        fields: [
          "configDigest",
          "signers",
          "f",
          "recipientAddressesAndWeights"
        ],
        kind: "function",
        name: "setConfig",
        signature: "setConfig(bytes32,address[],uint8,(address,uint64)[])"
      },
      {
        fields: [
          "feeManager"
        ],
        kind: "function",
        name: "setFeeManager",
        signature: "setFeeManager(address)"
      },
      {
        fields: [
          "",
          ""
        ],
        kind: "function",
        name: "setFeeRecipients",
        signature: "setFeeRecipients(bytes32,(address,uint64)[])"
      },
      {
        fields: [
          ""
        ],
        kind: "function",
        name: "setNativeSurcharge",
        signature: "setNativeSurcharge(uint64)"
      },
      {
        fields: [
          "currentConfigDigest",
          "newConfigDigest",
          "addressesAndWeights"
        ],
        kind: "function",
        name: "setVerifier",
        signature: "setVerifier(bytes32,bytes32,(address,uint64)[])"
      },
      {
        fields: [
          "interfaceId"
        ],
        kind: "function",
        name: "supportsInterface",
        signature: "supportsInterface(bytes4)"
      },
      {
        fields: [
          "to"
        ],
        kind: "function",
        name: "transferOwnership",
        signature: "transferOwnership(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "typeAndVersion",
        signature: "typeAndVersion()"
      },
      {
        fields: [
          "configDigest"
        ],
        kind: "function",
        name: "unsetVerifier",
        signature: "unsetVerifier(bytes32)"
      },
      {
        fields: [
          "configDigest",
          "prevSigners",
          "newSigners",
          "f"
        ],
        kind: "function",
        name: "updateConfig",
        signature: "updateConfig(bytes32,address[],address[],uint8)"
      },
      {
        fields: [
          "",
          "",
          "",
          ""
        ],
        kind: "function",
        name: "updateSubscriberDiscount",
        signature: "updateSubscriberDiscount(address,bytes32,address,uint64)"
      },
      {
        fields: [
          "",
          "",
          ""
        ],
        kind: "function",
        name: "updateSubscriberGlobalDiscount",
        signature: "updateSubscriberGlobalDiscount(address,address,uint64)"
      },
      {
        fields: [
          "signedReport",
          "sender"
        ],
        kind: "function",
        name: "verify",
        signature: "verify(bytes,address)"
      },
      {
        fields: [
          "payload",
          "parameterPayload"
        ],
        kind: "function",
        name: "verify",
        signature: "verify(bytes,bytes)"
      },
      {
        fields: [
          "payloads",
          "parameterPayload"
        ],
        kind: "function",
        name: "verifyBulk",
        signature: "verifyBulk(bytes[],bytes)"
      },
      {
        fields: [
          "",
          "",
          ""
        ],
        kind: "function",
        name: "withdraw",
        signature: "withdraw(address,address,uint192)"
      }
    ],
    label: "Chainlink Data Streams",
    vocabularies: {}
  },
  chainlink_direct_request: {
    entries: [
      {
        fields: [
          "requestId"
        ],
        kind: "event",
        name: "CancelOracleRequest",
        signature: "CancelOracleRequest(bytes32)"
      },
      {
        fields: [
          "specId",
          "requester",
          "requestId",
          "payment",
          "callbackAddr",
          "callbackFunctionId",
          "cancelExpiration",
          "dataVersion",
          "data"
        ],
        kind: "event",
        name: "OracleRequest",
        signature: "OracleRequest(bytes32,address,bytes32,uint256,address,bytes4,uint256,uint256,bytes)"
      },
      {
        fields: [
          "requestId",
          "payment",
          "callbackAddress",
          "callbackFunctionId",
          "expiration",
          "data"
        ],
        kind: "function",
        name: "fulfillOracleRequest",
        signature: "fulfillOracleRequest(bytes32,uint256,address,bytes4,uint256,bytes32)"
      },
      {
        fields: [
          "requestId",
          "payment",
          "callbackAddress",
          "callbackFunctionId",
          "expiration",
          "data"
        ],
        kind: "function",
        name: "fulfillOracleRequest2",
        signature: "fulfillOracleRequest2(bytes32,uint256,address,bytes4,uint256,bytes)"
      }
    ],
    label: "Chainlink Direct Request",
    vocabularies: {}
  },
  chainlink_functions: {
    entries: [
      {
        fields: [
          "requestId"
        ],
        kind: "event",
        name: "CommitmentDeleted",
        signature: "CommitmentDeleted(bytes32)"
      },
      {
        fields: [
          "previousConfigBlockNumber",
          "configDigest",
          "configCount",
          "signers",
          "transmitters",
          "f",
          "onchainConfig",
          "offchainConfigVersion",
          "offchainConfig"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet(uint32,bytes32,uint64,address[],address[],uint8,bytes,uint64,bytes)"
      },
      {
        fields: [
          ""
        ],
        kind: "event",
        name: "ConfigUpdated",
        signature: "ConfigUpdated((uint16,uint72,bytes4,uint16,uint32[],uint16,uint72))"
      },
      {
        fields: [
          "config"
        ],
        kind: "event",
        name: "ConfigUpdated",
        signature: "ConfigUpdated((uint32,uint32,uint32,uint32,uint32,uint72,uint16,uint224))"
      },
      {
        fields: [
          "config"
        ],
        kind: "event",
        name: "ConfigUpdated",
        signature: "ConfigUpdated((uint32,uint32,uint32,uint32,uint40,uint16,uint64,uint8,uint224,uint32,uint16,uint16,uint16))"
      },
      {
        fields: [
          "config"
        ],
        kind: "event",
        name: "ConfigUpdated",
        signature: "ConfigUpdated((uint32,uint32,uint32,uint32,uint72,uint40,uint16,uint224,uint32))"
      },
      {
        fields: [
          "proposedContractSetId",
          "proposedContractSetFromAddress",
          "proposedContractSetToAddress"
        ],
        kind: "event",
        name: "ContractProposed",
        signature: "ContractProposed(bytes32,address,address)"
      },
      {
        fields: [
          "id",
          "from",
          "to"
        ],
        kind: "event",
        name: "ContractUpdated",
        signature: "ContractUpdated(bytes32,address,address)"
      },
      {
        fields: [
          "to",
          "amount"
        ],
        kind: "event",
        name: "FundsRecovered",
        signature: "FundsRecovered(address,uint256)"
      },
      {
        fields: [
          "requestId",
          "requestingContract",
          "requestInitiator",
          "subscriptionId",
          "subscriptionOwner",
          "data",
          "dataVersion",
          "flags",
          "callbackGasLimit",
          "commitment"
        ],
        kind: "event",
        name: "OracleRequest",
        signature: "OracleRequest(bytes32,address,address,uint64,address,bytes,uint16,bytes32,uint64,(bytes32,address,uint96,address,uint64,uint32,uint72,uint72,uint40,uint40,uint32))"
      },
      {
        fields: [
          "requestId",
          "transmitter"
        ],
        kind: "event",
        name: "OracleResponse",
        signature: "OracleResponse(bytes32,address)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferRequested",
        signature: "OwnershipTransferRequested(address,address)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferred",
        signature: "OwnershipTransferred(address,address)"
      },
      {
        fields: [
          "account"
        ],
        kind: "event",
        name: "Paused",
        signature: "Paused(address)"
      },
      {
        fields: [
          "requestId",
          "juelsPerGas",
          "l1FeeShareWei",
          "callbackCostJuels",
          "donFeeJuels",
          "adminFeeJuels",
          "operationFeeJuels"
        ],
        kind: "event",
        name: "RequestBilled",
        signature: "RequestBilled(bytes32,uint96,uint256,uint96,uint72,uint72,uint72)"
      },
      {
        fields: [
          "requestId",
          "juelsPerGas",
          "l1FeeShareWei",
          "callbackCostJuels",
          "totalCostJuels"
        ],
        kind: "event",
        name: "RequestBilled",
        signature: "RequestBilled(bytes32,uint96,uint256,uint96,uint96)"
      },
      {
        fields: [
          "id"
        ],
        kind: "event",
        name: "RequestFulfilled",
        signature: "RequestFulfilled(bytes32)"
      },
      {
        fields: [
          "requestId",
          "coordinator",
          "transmitter",
          "resultCode"
        ],
        kind: "event",
        name: "RequestNotProcessed",
        signature: "RequestNotProcessed(bytes32,address,address,uint8)"
      },
      {
        fields: [
          "requestId",
          "subscriptionId",
          "totalCostJuels",
          "transmitter",
          "resultCode",
          "response",
          "err",
          "callbackReturnData"
        ],
        kind: "event",
        name: "RequestProcessed",
        signature: "RequestProcessed(bytes32,uint64,uint96,address,uint8,bytes,bytes,bytes)"
      },
      {
        fields: [
          "id"
        ],
        kind: "event",
        name: "RequestSent",
        signature: "RequestSent(bytes32)"
      },
      {
        fields: [
          "requestId",
          "donId",
          "subscriptionId",
          "subscriptionOwner",
          "requestingContract",
          "requestInitiator",
          "data",
          "dataVersion",
          "callbackGasLimit",
          "estimatedTotalCostJuels"
        ],
        kind: "event",
        name: "RequestStart",
        signature: "RequestStart(bytes32,bytes32,uint64,address,address,address,bytes,uint16,uint32,uint96)"
      },
      {
        fields: [
          "requestId"
        ],
        kind: "event",
        name: "RequestTimedOut",
        signature: "RequestTimedOut(bytes32)"
      },
      {
        fields: [
          "subscriptionId",
          "fundsRecipient",
          "fundsAmount"
        ],
        kind: "event",
        name: "SubscriptionCanceled",
        signature: "SubscriptionCanceled(uint64,address,uint256)"
      },
      {
        fields: [
          "subscriptionId",
          "consumer"
        ],
        kind: "event",
        name: "SubscriptionConsumerAdded",
        signature: "SubscriptionConsumerAdded(uint64,address)"
      },
      {
        fields: [
          "subscriptionId",
          "consumer"
        ],
        kind: "event",
        name: "SubscriptionConsumerRemoved",
        signature: "SubscriptionConsumerRemoved(uint64,address)"
      },
      {
        fields: [
          "subscriptionId",
          "owner"
        ],
        kind: "event",
        name: "SubscriptionCreated",
        signature: "SubscriptionCreated(uint64,address)"
      },
      {
        fields: [
          "subscriptionId",
          "oldBalance",
          "newBalance"
        ],
        kind: "event",
        name: "SubscriptionFunded",
        signature: "SubscriptionFunded(uint64,uint256,uint256)"
      },
      {
        fields: [
          "subscriptionId",
          "from",
          "to"
        ],
        kind: "event",
        name: "SubscriptionOwnerTransferRequested",
        signature: "SubscriptionOwnerTransferRequested(uint64,address,address)"
      },
      {
        fields: [
          "subscriptionId",
          "from",
          "to"
        ],
        kind: "event",
        name: "SubscriptionOwnerTransferred",
        signature: "SubscriptionOwnerTransferred(uint64,address,address)"
      },
      {
        fields: [
          "configDigest",
          "epoch"
        ],
        kind: "event",
        name: "Transmitted",
        signature: "Transmitted(bytes32,uint32)"
      },
      {
        fields: [
          "account"
        ],
        kind: "event",
        name: "Unpaused",
        signature: "Unpaused(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "MAX_CALLBACK_RETURN_BYTES",
        signature: "MAX_CALLBACK_RETURN_BYTES()"
      },
      {
        fields: [],
        kind: "function",
        name: "acceptOwnership",
        signature: "acceptOwnership()"
      },
      {
        fields: [
          "subscriptionId"
        ],
        kind: "function",
        name: "acceptSubscriptionOwnerTransfer",
        signature: "acceptSubscriptionOwnerTransfer(uint64)"
      },
      {
        fields: [
          "subscriptionId",
          "consumer"
        ],
        kind: "function",
        name: "addConsumer",
        signature: "addConsumer(uint64,address)"
      },
      {
        fields: [
          "subscriptionId",
          "to"
        ],
        kind: "function",
        name: "cancelSubscription",
        signature: "cancelSubscription(uint64,address)"
      },
      {
        fields: [],
        kind: "function",
        name: "createSubscription",
        signature: "createSubscription()"
      },
      {
        fields: [
          "consumer"
        ],
        kind: "function",
        name: "createSubscriptionWithConsumer",
        signature: "createSubscriptionWithConsumer(address)"
      },
      {
        fields: [
          "requestId"
        ],
        kind: "function",
        name: "deleteCommitment",
        signature: "deleteCommitment(bytes32)"
      },
      {
        fields: [
          "subscriptionId",
          "data",
          "callbackGasLimit",
          "gasPriceWei"
        ],
        kind: "function",
        name: "estimateCost",
        signature: "estimateCost(uint64,bytes,uint32,uint256)"
      },
      {
        fields: [
          "response",
          "err",
          "juelsPerGas",
          "costWithoutCallback",
          "transmitter",
          "commitment"
        ],
        kind: "function",
        name: "fulfill",
        signature: "fulfill(bytes,bytes,uint96,uint96,address,(bytes32,address,uint96,address,uint64,uint32,uint72,uint72,uint40,uint40,uint32))"
      },
      {
        fields: [],
        kind: "function",
        name: "getAdminFee",
        signature: "getAdminFee()"
      },
      {
        fields: [],
        kind: "function",
        name: "getAdminFeeJuels",
        signature: "getAdminFeeJuels()"
      },
      {
        fields: [],
        kind: "function",
        name: "getAllowListId",
        signature: "getAllowListId()"
      },
      {
        fields: [],
        kind: "function",
        name: "getConfig",
        signature: "getConfig()"
      },
      {
        fields: [
          "client",
          "subscriptionId"
        ],
        kind: "function",
        name: "getConsumer",
        signature: "getConsumer(address,uint64)"
      },
      {
        fields: [
          "id"
        ],
        kind: "function",
        name: "getContractById",
        signature: "getContractById(bytes32)"
      },
      {
        fields: [
          ""
        ],
        kind: "function",
        name: "getDONFee",
        signature: "getDONFee(bytes)"
      },
      {
        fields: [
          ""
        ],
        kind: "function",
        name: "getDONFeeJuels",
        signature: "getDONFeeJuels(bytes)"
      },
      {
        fields: [],
        kind: "function",
        name: "getDONPublicKey",
        signature: "getDONPublicKey()"
      },
      {
        fields: [
          "subscriptionId"
        ],
        kind: "function",
        name: "getFlags",
        signature: "getFlags(uint64)"
      },
      {
        fields: [],
        kind: "function",
        name: "getOperationFeeJuels",
        signature: "getOperationFeeJuels()"
      },
      {
        fields: [
          "id"
        ],
        kind: "function",
        name: "getProposedContractById",
        signature: "getProposedContractById(bytes32)"
      },
      {
        fields: [],
        kind: "function",
        name: "getProposedContractSet",
        signature: "getProposedContractSet()"
      },
      {
        fields: [
          "subscriptionId"
        ],
        kind: "function",
        name: "getSubscription",
        signature: "getSubscription(uint64)"
      },
      {
        fields: [],
        kind: "function",
        name: "getSubscriptionCount",
        signature: "getSubscriptionCount()"
      },
      {
        fields: [
          "subscriptionIdStart",
          "subscriptionIdEnd"
        ],
        kind: "function",
        name: "getSubscriptionsInRange",
        signature: "getSubscriptionsInRange(uint64,uint64)"
      },
      {
        fields: [],
        kind: "function",
        name: "getThresholdPublicKey",
        signature: "getThresholdPublicKey()"
      },
      {
        fields: [],
        kind: "function",
        name: "getTotalBalance",
        signature: "getTotalBalance()"
      },
      {
        fields: [],
        kind: "function",
        name: "getUsdPerUnitLink",
        signature: "getUsdPerUnitLink()"
      },
      {
        fields: [],
        kind: "function",
        name: "getWeiPerUnitLink",
        signature: "getWeiPerUnitLink()"
      },
      {
        fields: [
          "requestId",
          "response",
          "err"
        ],
        kind: "function",
        name: "handleOracleFulfillment",
        signature: "handleOracleFulfillment(bytes32,bytes,bytes)"
      },
      {
        fields: [],
        kind: "function",
        name: "isConfigured",
        signature: "isConfigured()"
      },
      {
        fields: [
          "subscriptionId",
          "callbackGasLimit"
        ],
        kind: "function",
        name: "isValidCallbackGasLimit",
        signature: "isValidCallbackGasLimit(uint64,uint32)"
      },
      {
        fields: [],
        kind: "function",
        name: "latestConfigDetails",
        signature: "latestConfigDetails()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestConfigDigestAndEpoch",
        signature: "latestConfigDigestAndEpoch()"
      },
      {
        fields: [
          "",
          "amount",
          "data"
        ],
        kind: "function",
        name: "onTokenTransfer",
        signature: "onTokenTransfer(address,uint256,bytes)"
      },
      {
        fields: [
          "recipient",
          "amount"
        ],
        kind: "function",
        name: "oracleWithdraw",
        signature: "oracleWithdraw(address,uint96)"
      },
      {
        fields: [],
        kind: "function",
        name: "oracleWithdrawAll",
        signature: "oracleWithdrawAll()"
      },
      {
        fields: [],
        kind: "function",
        name: "owner",
        signature: "owner()"
      },
      {
        fields: [
          "subscriptionId"
        ],
        kind: "function",
        name: "ownerCancelSubscription",
        signature: "ownerCancelSubscription(uint64)"
      },
      {
        fields: [
          "recipient",
          "amount"
        ],
        kind: "function",
        name: "ownerWithdraw",
        signature: "ownerWithdraw(address,uint96)"
      },
      {
        fields: [],
        kind: "function",
        name: "pause",
        signature: "pause()"
      },
      {
        fields: [],
        kind: "function",
        name: "paused",
        signature: "paused()"
      },
      {
        fields: [
          "subscriptionId"
        ],
        kind: "function",
        name: "pendingRequestExists",
        signature: "pendingRequestExists(uint64)"
      },
      {
        fields: [
          "proposedContractSetIds",
          "proposedContractSetAddresses"
        ],
        kind: "function",
        name: "proposeContractsUpdate",
        signature: "proposeContractsUpdate(bytes32[],address[])"
      },
      {
        fields: [
          "subscriptionId",
          "newOwner"
        ],
        kind: "function",
        name: "proposeSubscriptionOwnerTransfer",
        signature: "proposeSubscriptionOwnerTransfer(uint64,address)"
      },
      {
        fields: [
          "to"
        ],
        kind: "function",
        name: "recoverFunds",
        signature: "recoverFunds(address)"
      },
      {
        fields: [
          "subscriptionId",
          "consumer"
        ],
        kind: "function",
        name: "removeConsumer",
        signature: "removeConsumer(uint64,address)"
      },
      {
        fields: [
          "stamper",
          "scrollId",
          "sealId"
        ],
        kind: "function",
        name: "requestStampData",
        signature: "requestStampData(address,uint256,uint256)"
      },
      {
        fields: [
          "subscriptionId",
          "data",
          "dataVersion",
          "callbackGasLimit",
          "donId"
        ],
        kind: "function",
        name: "sendRequest",
        signature: "sendRequest(uint64,bytes,uint16,uint32,bytes32)"
      },
      {
        fields: [
          "subscriptionId",
          "data",
          "dataVersion",
          "callbackGasLimit",
          "donId"
        ],
        kind: "function",
        name: "sendRequestToProposed",
        signature: "sendRequestToProposed(uint64,bytes,uint16,uint32,bytes32)"
      },
      {
        fields: [
          "allowListId"
        ],
        kind: "function",
        name: "setAllowListId",
        signature: "setAllowListId(bytes32)"
      },
      {
        fields: [
          "_signers",
          "_transmitters",
          "_f",
          "_onchainConfig",
          "_offchainConfigVersion",
          "_offchainConfig"
        ],
        kind: "function",
        name: "setConfig",
        signature: "setConfig(address[],address[],uint8,bytes,uint64,bytes)"
      },
      {
        fields: [
          "donPublicKey"
        ],
        kind: "function",
        name: "setDONPublicKey",
        signature: "setDONPublicKey(bytes)"
      },
      {
        fields: [
          "subscriptionId",
          "flags"
        ],
        kind: "function",
        name: "setFlags",
        signature: "setFlags(uint64,bytes32)"
      },
      {
        fields: [
          "thresholdPublicKey"
        ],
        kind: "function",
        name: "setThresholdPublicKey",
        signature: "setThresholdPublicKey(bytes)"
      },
      {
        fields: [
          "request"
        ],
        kind: "function",
        name: "startRequest",
        signature: "startRequest((bytes,bytes32,address,uint96,uint72,uint64,uint64,uint32,uint16,uint64,address))"
      },
      {
        fields: [
          "requestsToTimeoutByCommitment"
        ],
        kind: "function",
        name: "timeoutRequests",
        signature: "timeoutRequests((bytes32,address,uint96,address,uint64,uint32,uint72,uint72,uint40,uint40,uint32)[])"
      },
      {
        fields: [
          "to"
        ],
        kind: "function",
        name: "transferOwnership",
        signature: "transferOwnership(address)"
      },
      {
        fields: [
          "reportContext",
          "report",
          "rs",
          "ss",
          "rawVs"
        ],
        kind: "function",
        name: "transmit",
        signature: "transmit(bytes32[3],bytes,bytes32[],bytes32[],bytes32)"
      },
      {
        fields: [],
        kind: "function",
        name: "transmitters",
        signature: "transmitters()"
      },
      {
        fields: [],
        kind: "function",
        name: "typeAndVersion",
        signature: "typeAndVersion()"
      },
      {
        fields: [],
        kind: "function",
        name: "unpause",
        signature: "unpause()"
      },
      {
        fields: [
          "config"
        ],
        kind: "function",
        name: "updateConfig",
        signature: "updateConfig((uint16,uint72,bytes4,uint16,uint32[],uint16,uint72))"
      },
      {
        fields: [
          "config"
        ],
        kind: "function",
        name: "updateConfig",
        signature: "updateConfig((uint32,uint32,uint32,uint32,uint32,uint72,uint16,uint224))"
      },
      {
        fields: [
          "config"
        ],
        kind: "function",
        name: "updateConfig",
        signature: "updateConfig((uint32,uint32,uint32,uint32,uint40,uint16,uint64,uint8,uint224,uint32,uint16,uint16,uint16))"
      },
      {
        fields: [
          "config"
        ],
        kind: "function",
        name: "updateConfig",
        signature: "updateConfig((uint32,uint32,uint32,uint32,uint72,uint40,uint16,uint224,uint32))"
      },
      {
        fields: [],
        kind: "function",
        name: "updateContracts",
        signature: "updateContracts()"
      }
    ],
    label: "Chainlink Functions",
    vocabularies: {
      functions_fulfillment: {
        "0": "Callback succeeded",
        "1": "Callback failed",
        "2": "Invalid request ID",
        "3": "Cost exceeds commitment",
        "4": "Insufficient gas provided",
        "5": "Subscription balance invariant violation",
        "6": "Invalid commitment"
      }
    }
  },
  chainlink_payment_abstraction: {
    entries: [
      {
        fields: [
          "asset"
        ],
        kind: "event",
        name: "AssetAddedToAllowlist",
        signature: "AssetAddedToAllowlist(address)"
      },
      {
        fields: [
          "to",
          "asset",
          "amount"
        ],
        kind: "event",
        name: "AssetEmergencyWithdrawn",
        signature: "AssetEmergencyWithdrawn(address,address,uint256)"
      },
      {
        fields: [
          "asset"
        ],
        kind: "event",
        name: "AssetRemovedFromAllowlist",
        signature: "AssetRemovedFromAllowlist(address)"
      },
      {
        fields: [
          "to",
          "asset",
          "amount"
        ],
        kind: "event",
        name: "AssetTransferredForSwap",
        signature: "AssetTransferredForSwap(address,address,uint256)"
      },
      {
        fields: [
          "messageId",
          "message"
        ],
        kind: "event",
        name: "BridgeAssetsMessageSent",
        signature: "BridgeAssetsMessageSent(bytes32,(bytes,bytes,(address,uint256)[],address,bytes))"
      },
      {
        fields: [
          "ccipRouter"
        ],
        kind: "event",
        name: "CCIPRouterClientSet",
        signature: "CCIPRouterClientSet(address)"
      },
      {
        fields: [],
        kind: "event",
        name: "DefaultAdminDelayChangeCanceled",
        signature: "DefaultAdminDelayChangeCanceled()"
      },
      {
        fields: [
          "newDelay",
          "effectSchedule"
        ],
        kind: "event",
        name: "DefaultAdminDelayChangeScheduled",
        signature: "DefaultAdminDelayChangeScheduled(uint48,uint48)"
      },
      {
        fields: [],
        kind: "event",
        name: "DefaultAdminTransferCanceled",
        signature: "DefaultAdminTransferCanceled()"
      },
      {
        fields: [
          "newAdmin",
          "acceptSchedule"
        ],
        kind: "event",
        name: "DefaultAdminTransferScheduled",
        signature: "DefaultAdminTransferScheduled(address,uint48)"
      },
      {
        fields: [
          "chainSelector"
        ],
        kind: "event",
        name: "DestinationChainAddedToAllowlist",
        signature: "DestinationChainAddedToAllowlist(uint64)"
      },
      {
        fields: [
          "chainSelector"
        ],
        kind: "event",
        name: "DestinationChainRemovedFromAllowlist",
        signature: "DestinationChainRemovedFromAllowlist(uint64)"
      },
      {
        fields: [
          "serviceProvider",
          "earmarkCounter",
          "amountLinkOwed",
          "earmarkData"
        ],
        kind: "event",
        name: "EarmarkSet",
        signature: "EarmarkSet(address,uint256,int96,bytes)"
      },
      {
        fields: [
          "linkToken"
        ],
        kind: "event",
        name: "LinkTokenSet",
        signature: "LinkTokenSet(address)"
      },
      {
        fields: [
          "to",
          "asset",
          "amount"
        ],
        kind: "event",
        name: "NonAllowlistedAssetWithdrawn",
        signature: "NonAllowlistedAssetWithdrawn(address,address,uint256)"
      },
      {
        fields: [
          "account"
        ],
        kind: "event",
        name: "Paused",
        signature: "Paused(address)"
      },
      {
        fields: [
          "chainSelector",
          "receiver"
        ],
        kind: "event",
        name: "ReceiverAddedToAllowlist",
        signature: "ReceiverAddedToAllowlist(uint64,bytes)"
      },
      {
        fields: [
          "chainSelector",
          "receiver"
        ],
        kind: "event",
        name: "ReceiverRemovedFromAllowlist",
        signature: "ReceiverRemovedFromAllowlist(uint64,bytes)"
      },
      {
        fields: [
          "role",
          "previousAdminRole",
          "newAdminRole"
        ],
        kind: "event",
        name: "RoleAdminChanged",
        signature: "RoleAdminChanged(bytes32,bytes32,bytes32)"
      },
      {
        fields: [
          "role",
          "account",
          "sender"
        ],
        kind: "event",
        name: "RoleGranted",
        signature: "RoleGranted(bytes32,address,address)"
      },
      {
        fields: [
          "role",
          "account",
          "sender"
        ],
        kind: "event",
        name: "RoleRevoked",
        signature: "RoleRevoked(bytes32,address,address)"
      },
      {
        fields: [
          "serviceProvider"
        ],
        kind: "event",
        name: "ServiceProviderAllowlisted",
        signature: "ServiceProviderAllowlisted(address)"
      },
      {
        fields: [
          "serviceProvider"
        ],
        kind: "event",
        name: "ServiceProviderRemovedFromAllowlist",
        signature: "ServiceProviderRemovedFromAllowlist(address)"
      },
      {
        fields: [
          "account"
        ],
        kind: "event",
        name: "Unpaused",
        signature: "Unpaused(address)"
      },
      {
        fields: [
          "serviceProvider",
          "amount"
        ],
        kind: "event",
        name: "Withdrawn",
        signature: "Withdrawn(address,uint256)"
      },
      {
        fields: [
          "wrappedNativeToken"
        ],
        kind: "event",
        name: "WrappedNativeTokenSet",
        signature: "WrappedNativeTokenSet(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "DEFAULT_ADMIN_ROLE",
        signature: "DEFAULT_ADMIN_ROLE()"
      },
      {
        fields: [],
        kind: "function",
        name: "MIN_GAS_FOR_RECEIVE",
        signature: "MIN_GAS_FOR_RECEIVE()"
      },
      {
        fields: [],
        kind: "function",
        name: "acceptDefaultAdminTransfer",
        signature: "acceptDefaultAdminTransfer()"
      },
      {
        fields: [
          "serviceProviders"
        ],
        kind: "function",
        name: "addAllowlistedServiceProviders",
        signature: "addAllowlistedServiceProviders(address[])"
      },
      {
        fields: [
          "assetsToRemove",
          "assetsToAdd"
        ],
        kind: "function",
        name: "applyAllowlistedAssetUpdates",
        signature: "applyAllowlistedAssetUpdates(address[],address[])"
      },
      {
        fields: [
          "receiversToRemove",
          "receiversToAdd"
        ],
        kind: "function",
        name: "applyAllowlistedReceiverUpdates",
        signature: "applyAllowlistedReceiverUpdates((uint64,bytes[])[],(uint64,bytes[])[])"
      },
      {
        fields: [
          "newAdmin"
        ],
        kind: "function",
        name: "beginDefaultAdminTransfer",
        signature: "beginDefaultAdminTransfer(address)"
      },
      {
        fields: [
          "bridgeAssetAmounts",
          "destinationChainSelector",
          "bridgeReceiver",
          "extraArgs"
        ],
        kind: "function",
        name: "bridgeAssets",
        signature: "bridgeAssets((address,uint256)[],uint64,bytes,bytes)"
      },
      {
        fields: [],
        kind: "function",
        name: "cancelDefaultAdminTransfer",
        signature: "cancelDefaultAdminTransfer()"
      },
      {
        fields: [
          "newDelay"
        ],
        kind: "function",
        name: "changeDefaultAdminDelay",
        signature: "changeDefaultAdminDelay(uint48)"
      },
      {
        fields: [],
        kind: "function",
        name: "defaultAdmin",
        signature: "defaultAdmin()"
      },
      {
        fields: [],
        kind: "function",
        name: "defaultAdminDelay",
        signature: "defaultAdminDelay()"
      },
      {
        fields: [],
        kind: "function",
        name: "defaultAdminDelayIncreaseWait",
        signature: "defaultAdminDelayIncreaseWait()"
      },
      {
        fields: [],
        kind: "function",
        name: "deposit",
        signature: "deposit()"
      },
      {
        fields: [],
        kind: "function",
        name: "emergencyPause",
        signature: "emergencyPause()"
      },
      {
        fields: [],
        kind: "function",
        name: "emergencyUnpause",
        signature: "emergencyUnpause()"
      },
      {
        fields: [
          "to",
          "assetAmounts"
        ],
        kind: "function",
        name: "emergencyWithdraw",
        signature: "emergencyWithdraw(address,(address,uint256)[])"
      },
      {
        fields: [
          "to",
          "amount"
        ],
        kind: "function",
        name: "emergencyWithdrawNative",
        signature: "emergencyWithdrawNative(address,uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "getAllowlistedAssets",
        signature: "getAllowlistedAssets()"
      },
      {
        fields: [],
        kind: "function",
        name: "getAllowlistedDestinationChains",
        signature: "getAllowlistedDestinationChains()"
      },
      {
        fields: [
          "destChainSelector"
        ],
        kind: "function",
        name: "getAllowlistedReceivers",
        signature: "getAllowlistedReceivers(uint64)"
      },
      {
        fields: [],
        kind: "function",
        name: "getLinkToken",
        signature: "getLinkToken()"
      },
      {
        fields: [
          "role"
        ],
        kind: "function",
        name: "getRoleAdmin",
        signature: "getRoleAdmin(bytes32)"
      },
      {
        fields: [
          "role",
          "index"
        ],
        kind: "function",
        name: "getRoleMember",
        signature: "getRoleMember(bytes32,uint256)"
      },
      {
        fields: [
          "role"
        ],
        kind: "function",
        name: "getRoleMemberCount",
        signature: "getRoleMemberCount(bytes32)"
      },
      {
        fields: [
          "role"
        ],
        kind: "function",
        name: "getRoleMembers",
        signature: "getRoleMembers(bytes32)"
      },
      {
        fields: [],
        kind: "function",
        name: "getRouter",
        signature: "getRouter()"
      },
      {
        fields: [
          "serviceProviderAddress"
        ],
        kind: "function",
        name: "getServiceProvider",
        signature: "getServiceProvider(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "getTotalLinkAmountOwed",
        signature: "getTotalLinkAmountOwed()"
      },
      {
        fields: [],
        kind: "function",
        name: "getWrappedNativeToken",
        signature: "getWrappedNativeToken()"
      },
      {
        fields: [
          "role",
          "account"
        ],
        kind: "function",
        name: "grantRole",
        signature: "grantRole(bytes32,address)"
      },
      {
        fields: [
          "role",
          "account"
        ],
        kind: "function",
        name: "hasRole",
        signature: "hasRole(bytes32,address)"
      },
      {
        fields: [
          "asset"
        ],
        kind: "function",
        name: "isAssetAllowlisted",
        signature: "isAssetAllowlisted(address)"
      },
      {
        fields: [
          "serviceProvider"
        ],
        kind: "function",
        name: "isServiceProviderAllowlisted",
        signature: "isServiceProviderAllowlisted(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "linkAvailableForPayment",
        signature: "linkAvailableForPayment()"
      },
      {
        fields: [
          "",
          "",
          ""
        ],
        kind: "function",
        name: "onTokenTransfer",
        signature: "onTokenTransfer(address,uint256,bytes)"
      },
      {
        fields: [],
        kind: "function",
        name: "owner",
        signature: "owner()"
      },
      {
        fields: [],
        kind: "function",
        name: "paused",
        signature: "paused()"
      },
      {
        fields: [],
        kind: "function",
        name: "pendingDefaultAdmin",
        signature: "pendingDefaultAdmin()"
      },
      {
        fields: [],
        kind: "function",
        name: "pendingDefaultAdminDelay",
        signature: "pendingDefaultAdminDelay()"
      },
      {
        fields: [
          "serviceProviders"
        ],
        kind: "function",
        name: "removeAllowlistedServiceProviders",
        signature: "removeAllowlistedServiceProviders(address[])"
      },
      {
        fields: [
          "role",
          "account"
        ],
        kind: "function",
        name: "renounceRole",
        signature: "renounceRole(bytes32,address)"
      },
      {
        fields: [
          "role",
          "account"
        ],
        kind: "function",
        name: "revokeRole",
        signature: "revokeRole(bytes32,address)"
      },
      {
        fields: [],
        kind: "function",
        name: "rollbackDefaultAdminDelay",
        signature: "rollbackDefaultAdminDelay()"
      },
      {
        fields: [
          "earmarks"
        ],
        kind: "function",
        name: "setEarmarks",
        signature: "setEarmarks((address,int96,bytes)[])"
      },
      {
        fields: [
          "wrappedNativeToken"
        ],
        kind: "function",
        name: "setWrappedNativeToken",
        signature: "setWrappedNativeToken(address)"
      },
      {
        fields: [
          "interfaceId"
        ],
        kind: "function",
        name: "supportsInterface",
        signature: "supportsInterface(bytes4)"
      },
      {
        fields: [
          "to",
          "assetAmounts"
        ],
        kind: "function",
        name: "transferForSwap",
        signature: "transferForSwap(address,(address,uint256)[])"
      },
      {
        fields: [],
        kind: "function",
        name: "typeAndVersion",
        signature: "typeAndVersion()"
      },
      {
        fields: [
          "serviceProviders"
        ],
        kind: "function",
        name: "withdraw",
        signature: "withdraw(address[])"
      },
      {
        fields: [
          "to",
          "amount"
        ],
        kind: "function",
        name: "withdrawNative",
        signature: "withdrawNative(address,uint256)"
      },
      {
        fields: [
          "to",
          "assetAmounts"
        ],
        kind: "function",
        name: "withdrawNonAllowlistedAssets",
        signature: "withdrawNonAllowlistedAssets(address,(address,uint256)[])"
      }
    ],
    label: "Chainlink Payment Abstraction",
    vocabularies: {}
  },
  chainlink_sequencer: {
    entries: [
      {
        fields: [
          "user"
        ],
        kind: "event",
        name: "AddedAccess",
        signature: "AddedAccess(address)"
      },
      {
        fields: [
          "current",
          "roundId",
          "updatedAt"
        ],
        kind: "event",
        name: "AnswerUpdated",
        signature: "AnswerUpdated(int256,uint256,uint256)"
      },
      {
        fields: [
          "old",
          "current"
        ],
        kind: "event",
        name: "BillingAccessControllerSet",
        signature: "BillingAccessControllerSet(address,address)"
      },
      {
        fields: [
          "maximumGasPriceGwei",
          "reasonableGasPriceGwei",
          "observationPaymentGjuels",
          "transmissionPaymentGjuels",
          "accountingGas"
        ],
        kind: "event",
        name: "BillingSet",
        signature: "BillingSet(uint32,uint32,uint32,uint32,uint24)"
      },
      {
        fields: [],
        kind: "event",
        name: "CheckAccessDisabled",
        signature: "CheckAccessDisabled()"
      },
      {
        fields: [],
        kind: "event",
        name: "CheckAccessEnabled",
        signature: "CheckAccessEnabled()"
      },
      {
        fields: [
          "previousConfigBlockNumber",
          "configDigest",
          "configCount",
          "signers",
          "transmitters",
          "f",
          "onchainConfig",
          "offchainConfigVersion",
          "offchainConfig"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet(uint32,bytes32,uint64,address[],address[],uint8,bytes,uint64,bytes)"
      },
      {
        fields: [],
        kind: "event",
        name: "Initialized",
        signature: "Initialized()"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "L1SenderTransferred",
        signature: "L1SenderTransferred(address,address)"
      },
      {
        fields: [
          "oldLinkToken",
          "newLinkToken"
        ],
        kind: "event",
        name: "LinkTokenSet",
        signature: "LinkTokenSet(address,address)"
      },
      {
        fields: [
          "roundId",
          "startedBy",
          "startedAt"
        ],
        kind: "event",
        name: "NewRound",
        signature: "NewRound(uint256,address,uint256)"
      },
      {
        fields: [
          "aggregatorRoundId",
          "answer",
          "transmitter",
          "observationsTimestamp",
          "observations",
          "observers",
          "juelsPerFeeCoin",
          "configDigest",
          "epochAndRound"
        ],
        kind: "event",
        name: "NewTransmission",
        signature: "NewTransmission(uint32,int192,address,uint32,int192[],bytes,int192,bytes32,uint40)"
      },
      {
        fields: [
          "transmitter",
          "payee",
          "amount",
          "linkToken"
        ],
        kind: "event",
        name: "OraclePaid",
        signature: "OraclePaid(address,address,uint256,address)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferRequested",
        signature: "OwnershipTransferRequested(address,address)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferred",
        signature: "OwnershipTransferred(address,address)"
      },
      {
        fields: [
          "transmitter",
          "current",
          "proposed"
        ],
        kind: "event",
        name: "PayeeshipTransferRequested",
        signature: "PayeeshipTransferRequested(address,address,address)"
      },
      {
        fields: [
          "transmitter",
          "previous",
          "current"
        ],
        kind: "event",
        name: "PayeeshipTransferred",
        signature: "PayeeshipTransferred(address,address,address)"
      },
      {
        fields: [
          "user"
        ],
        kind: "event",
        name: "RemovedAccess",
        signature: "RemovedAccess(address)"
      },
      {
        fields: [
          "old",
          "current"
        ],
        kind: "event",
        name: "RequesterAccessControllerSet",
        signature: "RequesterAccessControllerSet(address,address)"
      },
      {
        fields: [
          "requester",
          "configDigest",
          "epoch",
          "round"
        ],
        kind: "event",
        name: "RoundRequested",
        signature: "RoundRequested(address,bytes32,uint32,uint8)"
      },
      {
        fields: [
          "status",
          "updatedAt"
        ],
        kind: "event",
        name: "RoundUpdated",
        signature: "RoundUpdated(int256,uint64)"
      },
      {
        fields: [
          "configDigest",
          "epoch"
        ],
        kind: "event",
        name: "Transmitted",
        signature: "Transmitted(bytes32,uint32)"
      },
      {
        fields: [
          "latestStatus",
          "latestTimestamp",
          "incomingStatus",
          "incomingTimestamp"
        ],
        kind: "event",
        name: "UpdateIgnored",
        signature: "UpdateIgnored(bool,uint64,bool,uint64)"
      },
      {
        fields: [
          "previousValidator",
          "previousGasLimit",
          "currentValidator",
          "currentGasLimit"
        ],
        kind: "event",
        name: "ValidatorConfigSet",
        signature: "ValidatorConfigSet(address,uint32,address,uint32)"
      },
      {
        fields: [],
        kind: "function",
        name: "FLAGS",
        signature: "FLAGS()"
      },
      {
        fields: [],
        kind: "function",
        name: "FLAG_L2_SEQ_OFFLINE",
        signature: "FLAG_L2_SEQ_OFFLINE()"
      },
      {
        fields: [],
        kind: "function",
        name: "acceptOwnership",
        signature: "acceptOwnership()"
      },
      {
        fields: [
          "transmitter"
        ],
        kind: "function",
        name: "acceptPayeeship",
        signature: "acceptPayeeship(address)"
      },
      {
        fields: [
          "_user"
        ],
        kind: "function",
        name: "addAccess",
        signature: "addAccess(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "aliasedL1MessageSender",
        signature: "aliasedL1MessageSender()"
      },
      {
        fields: [],
        kind: "function",
        name: "checkEnabled",
        signature: "checkEnabled()"
      },
      {
        fields: [],
        kind: "function",
        name: "decimals",
        signature: "decimals()"
      },
      {
        fields: [],
        kind: "function",
        name: "description",
        signature: "description()"
      },
      {
        fields: [],
        kind: "function",
        name: "disableAccessCheck",
        signature: "disableAccessCheck()"
      },
      {
        fields: [],
        kind: "function",
        name: "enableAccessCheck",
        signature: "enableAccessCheck()"
      },
      {
        fields: [
          "roundId"
        ],
        kind: "function",
        name: "getAnswer",
        signature: "getAnswer(uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "getBilling",
        signature: "getBilling()"
      },
      {
        fields: [],
        kind: "function",
        name: "getBillingAccessController",
        signature: "getBillingAccessController()"
      },
      {
        fields: [],
        kind: "function",
        name: "getLinkToken",
        signature: "getLinkToken()"
      },
      {
        fields: [],
        kind: "function",
        name: "getRequesterAccessController",
        signature: "getRequesterAccessController()"
      },
      {
        fields: [
          "_roundId"
        ],
        kind: "function",
        name: "getRoundData",
        signature: "getRoundData(uint80)"
      },
      {
        fields: [
          "roundId"
        ],
        kind: "function",
        name: "getTimestamp",
        signature: "getTimestamp(uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "getTransmitters",
        signature: "getTransmitters()"
      },
      {
        fields: [],
        kind: "function",
        name: "getValidatorConfig",
        signature: "getValidatorConfig()"
      },
      {
        fields: [
          "_user",
          "_calldata"
        ],
        kind: "function",
        name: "hasAccess",
        signature: "hasAccess(address,bytes)"
      },
      {
        fields: [],
        kind: "function",
        name: "initialize",
        signature: "initialize()"
      },
      {
        fields: [],
        kind: "function",
        name: "l1Sender",
        signature: "l1Sender()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestAnswer",
        signature: "latestAnswer()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestConfigDetails",
        signature: "latestConfigDetails()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestConfigDigestAndEpoch",
        signature: "latestConfigDigestAndEpoch()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestRound",
        signature: "latestRound()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestRoundData",
        signature: "latestRoundData()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestTimestamp",
        signature: "latestTimestamp()"
      },
      {
        fields: [],
        kind: "function",
        name: "latestTransmissionDetails",
        signature: "latestTransmissionDetails()"
      },
      {
        fields: [],
        kind: "function",
        name: "linkAvailableForPayment",
        signature: "linkAvailableForPayment()"
      },
      {
        fields: [],
        kind: "function",
        name: "maxAnswer",
        signature: "maxAnswer()"
      },
      {
        fields: [],
        kind: "function",
        name: "minAnswer",
        signature: "minAnswer()"
      },
      {
        fields: [
          "transmitterAddress"
        ],
        kind: "function",
        name: "oracleObservationCount",
        signature: "oracleObservationCount(address)"
      },
      {
        fields: [
          "transmitterAddress"
        ],
        kind: "function",
        name: "owedPayment",
        signature: "owedPayment(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "owner",
        signature: "owner()"
      },
      {
        fields: [
          "_user"
        ],
        kind: "function",
        name: "removeAccess",
        signature: "removeAccess(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "requestNewRound",
        signature: "requestNewRound()"
      },
      {
        fields: [
          "maximumGasPriceGwei",
          "reasonableGasPriceGwei",
          "observationPaymentGjuels",
          "transmissionPaymentGjuels",
          "accountingGas"
        ],
        kind: "function",
        name: "setBilling",
        signature: "setBilling(uint32,uint32,uint32,uint32,uint24)"
      },
      {
        fields: [
          "_billingAccessController"
        ],
        kind: "function",
        name: "setBillingAccessController",
        signature: "setBillingAccessController(address)"
      },
      {
        fields: [
          "signers",
          "transmitters",
          "f",
          "onchainConfig",
          "offchainConfigVersion",
          "offchainConfig"
        ],
        kind: "function",
        name: "setConfig",
        signature: "setConfig(address[],address[],uint8,bytes,uint64,bytes)"
      },
      {
        fields: [
          "linkToken",
          "recipient"
        ],
        kind: "function",
        name: "setLinkToken",
        signature: "setLinkToken(address,address)"
      },
      {
        fields: [
          "transmitters",
          "payees"
        ],
        kind: "function",
        name: "setPayees",
        signature: "setPayees(address[],address[])"
      },
      {
        fields: [
          "requesterAccessController"
        ],
        kind: "function",
        name: "setRequesterAccessController",
        signature: "setRequesterAccessController(address)"
      },
      {
        fields: [
          "newValidator",
          "newGasLimit"
        ],
        kind: "function",
        name: "setValidatorConfig",
        signature: "setValidatorConfig(address,uint32)"
      },
      {
        fields: [
          "to"
        ],
        kind: "function",
        name: "transferL1Sender",
        signature: "transferL1Sender(address)"
      },
      {
        fields: [
          "to"
        ],
        kind: "function",
        name: "transferOwnership",
        signature: "transferOwnership(address)"
      },
      {
        fields: [
          "transmitter",
          "proposed"
        ],
        kind: "function",
        name: "transferPayeeship",
        signature: "transferPayeeship(address,address)"
      },
      {
        fields: [
          "reportContext",
          "report",
          "rs",
          "ss",
          "rawVs"
        ],
        kind: "function",
        name: "transmit",
        signature: "transmit(bytes32[3],bytes,bytes32[],bytes32[],bytes32)"
      },
      {
        fields: [],
        kind: "function",
        name: "typeAndVersion",
        signature: "typeAndVersion()"
      },
      {
        fields: [
          "status",
          "timestamp"
        ],
        kind: "function",
        name: "updateStatus",
        signature: "updateStatus(bool,uint64)"
      },
      {
        fields: [],
        kind: "function",
        name: "version",
        signature: "version()"
      },
      {
        fields: [
          "recipient",
          "amount"
        ],
        kind: "function",
        name: "withdrawFunds",
        signature: "withdrawFunds(address,uint256)"
      },
      {
        fields: [
          "transmitter"
        ],
        kind: "function",
        name: "withdrawPayment",
        signature: "withdrawPayment(address)"
      }
    ],
    label: "Chainlink Sequencer Uptime",
    vocabularies: {
      uptime: {
        "0": "Up",
        "1": "Down"
      }
    }
  },
  chainlink_staking: {
    entries: [
      {
        fields: [
          "amountFunded",
          "totalBalance"
        ],
        kind: "event",
        name: "AlerterRewardDeposited",
        signature: "AlerterRewardDeposited(uint256,uint256)"
      },
      {
        fields: [
          "amountWithdrawn",
          "remainingBalance"
        ],
        kind: "event",
        name: "AlerterRewardWithdrawn",
        signature: "AlerterRewardWithdrawn(uint256,uint256)"
      },
      {
        fields: [
          "alerter",
          "alerterRewardActual",
          "alerterRewardExpected"
        ],
        kind: "event",
        name: "AlertingRewardPaid",
        signature: "AlertingRewardPaid(address,uint256,uint256)"
      },
      {
        fields: [
          "oldClaimPeriod",
          "newClaimPeriod"
        ],
        kind: "event",
        name: "ClaimPeriodSet",
        signature: "ClaimPeriodSet(uint256,uint256)"
      },
      {
        fields: [
          "baseRewardPerToken"
        ],
        kind: "event",
        name: "CommunityPoolRewardUpdated",
        signature: "CommunityPoolRewardUpdated(uint256)"
      },
      {
        fields: [],
        kind: "event",
        name: "DefaultAdminDelayChangeCanceled",
        signature: "DefaultAdminDelayChangeCanceled()"
      },
      {
        fields: [
          "newDelay",
          "effectSchedule"
        ],
        kind: "event",
        name: "DefaultAdminDelayChangeScheduled",
        signature: "DefaultAdminDelayChangeScheduled(uint48,uint48)"
      },
      {
        fields: [],
        kind: "event",
        name: "DefaultAdminTransferCanceled",
        signature: "DefaultAdminTransferCanceled()"
      },
      {
        fields: [
          "newAdmin",
          "acceptSchedule"
        ],
        kind: "event",
        name: "DefaultAdminTransferScheduled",
        signature: "DefaultAdminTransferScheduled(address,uint48)"
      },
      {
        fields: [
          "oldDelegationRate",
          "newDelegationRate"
        ],
        kind: "event",
        name: "DelegationRateSet",
        signature: "DelegationRateSet(uint256,uint256)"
      },
      {
        fields: [
          "vestedReward",
          "vestedRewardPerToken",
          "reclaimedReward",
          "isOperatorReward"
        ],
        kind: "event",
        name: "ForfeitedRewardDistributed",
        signature: "ForfeitedRewardDistributed(uint256,uint256,uint256,bool)"
      },
      {
        fields: [
          "maxPrincipalPerStaker"
        ],
        kind: "event",
        name: "MaxPrincipalAmountIncreased",
        signature: "MaxPrincipalAmountIncreased(uint256)"
      },
      {
        fields: [
          "oldMerkleRoot",
          "newMerkleRoot"
        ],
        kind: "event",
        name: "MerkleRootChanged",
        signature: "MerkleRootChanged(bytes32,bytes32)"
      },
      {
        fields: [
          "oldMigrationProxy",
          "newMigrationProxy"
        ],
        kind: "event",
        name: "MigrationProxySet",
        signature: "MigrationProxySet(address,address)"
      },
      {
        fields: [
          "oldMigrationTarget",
          "newMigrationTarget"
        ],
        kind: "event",
        name: "MigrationTargetSet",
        signature: "MigrationTargetSet(address,address)"
      },
      {
        fields: [
          "operator"
        ],
        kind: "event",
        name: "OperatorAdded",
        signature: "OperatorAdded(address)"
      },
      {
        fields: [
          "baseRewardPerToken",
          "delegatedRewardPerToken"
        ],
        kind: "event",
        name: "OperatorPoolRewardUpdated",
        signature: "OperatorPoolRewardUpdated(uint256,uint256)"
      },
      {
        fields: [
          "operator",
          "principal",
          "newTotalPrincipal"
        ],
        kind: "event",
        name: "OperatorRemoved",
        signature: "OperatorRemoved(address,uint256,uint256)"
      },
      {
        fields: [
          "oldOperatorStakingPool",
          "newOperatorStakingPool"
        ],
        kind: "event",
        name: "OperatorStakingPoolChanged",
        signature: "OperatorStakingPoolChanged(address,address)"
      },
      {
        fields: [
          "account"
        ],
        kind: "event",
        name: "Paused",
        signature: "Paused(address)"
      },
      {
        fields: [],
        kind: "event",
        name: "PoolClosed",
        signature: "PoolClosed()"
      },
      {
        fields: [],
        kind: "event",
        name: "PoolOpened",
        signature: "PoolOpened()"
      },
      {
        fields: [
          "maxPoolSize"
        ],
        kind: "event",
        name: "PoolSizeIncreased",
        signature: "PoolSizeIncreased(uint256)"
      },
      {
        fields: [
          "pool",
          "amount",
          "emissionRate"
        ],
        kind: "event",
        name: "RewardAdded",
        signature: "RewardAdded(address,uint256,uint256)"
      },
      {
        fields: [
          "staker",
          "claimedRewards"
        ],
        kind: "event",
        name: "RewardClaimed",
        signature: "RewardClaimed(address,uint256)"
      },
      {
        fields: [
          "staker",
          "shouldForfeit"
        ],
        kind: "event",
        name: "RewardFinalized",
        signature: "RewardFinalized(address,bool)"
      },
      {
        fields: [
          "oldRewardVault",
          "newRewardVault"
        ],
        kind: "event",
        name: "RewardVaultSet",
        signature: "RewardVaultSet(address,address)"
      },
      {
        fields: [
          "role",
          "previousAdminRole",
          "newAdminRole"
        ],
        kind: "event",
        name: "RoleAdminChanged",
        signature: "RoleAdminChanged(bytes32,bytes32,bytes32)"
      },
      {
        fields: [
          "role",
          "account",
          "sender"
        ],
        kind: "event",
        name: "RoleGranted",
        signature: "RoleGranted(bytes32,address,address)"
      },
      {
        fields: [
          "role",
          "account",
          "sender"
        ],
        kind: "event",
        name: "RoleRevoked",
        signature: "RoleRevoked(bytes32,address,address)"
      },
      {
        fields: [
          "operator",
          "slashedAmount",
          "updatedStakerPrincipal",
          "newTotalPrincipal"
        ],
        kind: "event",
        name: "Slashed",
        signature: "Slashed(address,uint256,uint256,uint256)"
      },
      {
        fields: [
          "slasher",
          "refillRate",
          "slashCapacity"
        ],
        kind: "event",
        name: "SlasherConfigSet",
        signature: "SlasherConfigSet(address,uint256,uint256)"
      },
      {
        fields: [
          "staker",
          "amount",
          "newStake",
          "newTotalPrincipal"
        ],
        kind: "event",
        name: "Staked",
        signature: "Staked(address,uint256,uint256,uint256)"
      },
      {
        fields: [
          "migrationTarget",
          "amount",
          "migrationData"
        ],
        kind: "event",
        name: "StakerMigrated",
        signature: "StakerMigrated(address,uint256,bytes)"
      },
      {
        fields: [
          "staker",
          "vestedBaseReward",
          "vestedDelegatedReward",
          "baseRewardPerToken",
          "operatorDelegatedRewardPerToken",
          "claimedBaseRewardsInPeriod"
        ],
        kind: "event",
        name: "StakerRewardUpdated",
        signature: "StakerRewardUpdated(address,uint256,uint256,uint256,uint256,uint256)"
      },
      {
        fields: [
          "staker"
        ],
        kind: "event",
        name: "UnbondingPeriodReset",
        signature: "UnbondingPeriodReset(address)"
      },
      {
        fields: [
          "oldUnbondingPeriod",
          "newUnbondingPeriod"
        ],
        kind: "event",
        name: "UnbondingPeriodSet",
        signature: "UnbondingPeriodSet(uint256,uint256)"
      },
      {
        fields: [
          "staker"
        ],
        kind: "event",
        name: "UnbondingPeriodStarted",
        signature: "UnbondingPeriodStarted(address)"
      },
      {
        fields: [
          "account"
        ],
        kind: "event",
        name: "Unpaused",
        signature: "Unpaused(address)"
      },
      {
        fields: [
          "staker",
          "amount",
          "newStake",
          "newTotalPrincipal"
        ],
        kind: "event",
        name: "Unstaked",
        signature: "Unstaked(address,uint256,uint256,uint256)"
      },
      {
        fields: [
          "totalUnvestedRewards"
        ],
        kind: "event",
        name: "VaultClosed",
        signature: "VaultClosed(uint256)"
      },
      {
        fields: [],
        kind: "event",
        name: "VaultOpened",
        signature: "VaultOpened()"
      },
      {
        fields: [],
        kind: "function",
        name: "ALERT_REWARDER_ROLE",
        signature: "ALERT_REWARDER_ROLE()"
      },
      {
        fields: [],
        kind: "function",
        name: "DEFAULT_ADMIN_ROLE",
        signature: "DEFAULT_ADMIN_ROLE()"
      },
      {
        fields: [],
        kind: "function",
        name: "INITIATOR_ROLE",
        signature: "INITIATOR_ROLE()"
      },
      {
        fields: [],
        kind: "function",
        name: "OPERATOR_MANAGER_ROLE",
        signature: "OPERATOR_MANAGER_ROLE()"
      },
      {
        fields: [],
        kind: "function",
        name: "PAUSER_ROLE",
        signature: "PAUSER_ROLE()"
      },
      {
        fields: [],
        kind: "function",
        name: "REWARDER_ROLE",
        signature: "REWARDER_ROLE()"
      },
      {
        fields: [],
        kind: "function",
        name: "SLASHER_ROLE",
        signature: "SLASHER_ROLE()"
      },
      {
        fields: [],
        kind: "function",
        name: "acceptDefaultAdminTransfer",
        signature: "acceptDefaultAdminTransfer()"
      },
      {
        fields: [
          "operators"
        ],
        kind: "function",
        name: "addOperators",
        signature: "addOperators(address[])"
      },
      {
        fields: [
          "pool",
          "amount",
          "emissionRate"
        ],
        kind: "function",
        name: "addReward",
        signature: "addReward(address,uint256,uint256)"
      },
      {
        fields: [
          "slasher",
          "config"
        ],
        kind: "function",
        name: "addSlasher",
        signature: "addSlasher(address,(uint256,uint256))"
      },
      {
        fields: [
          "newAdmin"
        ],
        kind: "function",
        name: "beginDefaultAdminTransfer",
        signature: "beginDefaultAdminTransfer(address)"
      },
      {
        fields: [
          "staker"
        ],
        kind: "function",
        name: "calculateLatestStakerReward",
        signature: "calculateLatestStakerReward(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "cancelDefaultAdminTransfer",
        signature: "cancelDefaultAdminTransfer()"
      },
      {
        fields: [
          "newDelay"
        ],
        kind: "function",
        name: "changeDefaultAdminDelay",
        signature: "changeDefaultAdminDelay(uint48)"
      },
      {
        fields: [],
        kind: "function",
        name: "claimReward",
        signature: "claimReward()"
      },
      {
        fields: [],
        kind: "function",
        name: "close",
        signature: "close()"
      },
      {
        fields: [
          "staker",
          "oldPrincipal",
          "stakedAt",
          "unstakedAmount",
          "shouldForfeit"
        ],
        kind: "function",
        name: "concludeRewardPeriod",
        signature: "concludeRewardPeriod(address,uint256,uint256,uint256,bool)"
      },
      {
        fields: [],
        kind: "function",
        name: "defaultAdmin",
        signature: "defaultAdmin()"
      },
      {
        fields: [],
        kind: "function",
        name: "defaultAdminDelay",
        signature: "defaultAdminDelay()"
      },
      {
        fields: [],
        kind: "function",
        name: "defaultAdminDelayIncreaseWait",
        signature: "defaultAdminDelayIncreaseWait()"
      },
      {
        fields: [
          "amount"
        ],
        kind: "function",
        name: "depositAlerterReward",
        signature: "depositAlerterReward(uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "emergencyPause",
        signature: "emergencyPause()"
      },
      {
        fields: [],
        kind: "function",
        name: "emergencyUnpause",
        signature: "emergencyUnpause()"
      },
      {
        fields: [],
        kind: "function",
        name: "getAlerterRewardFunds",
        signature: "getAlerterRewardFunds()"
      },
      {
        fields: [],
        kind: "function",
        name: "getChainlinkToken",
        signature: "getChainlinkToken()"
      },
      {
        fields: [
          "staker"
        ],
        kind: "function",
        name: "getClaimPeriodEndsAt",
        signature: "getClaimPeriodEndsAt(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "getClaimPeriodLimits",
        signature: "getClaimPeriodLimits()"
      },
      {
        fields: [],
        kind: "function",
        name: "getDelegationRate",
        signature: "getDelegationRate()"
      },
      {
        fields: [],
        kind: "function",
        name: "getFinalVestingCheckpointData",
        signature: "getFinalVestingCheckpointData()"
      },
      {
        fields: [],
        kind: "function",
        name: "getMaxPoolSize",
        signature: "getMaxPoolSize()"
      },
      {
        fields: [],
        kind: "function",
        name: "getMaxUnbondingPeriod",
        signature: "getMaxUnbondingPeriod()"
      },
      {
        fields: [],
        kind: "function",
        name: "getMerkleRoot",
        signature: "getMerkleRoot()"
      },
      {
        fields: [],
        kind: "function",
        name: "getMigrationProxy",
        signature: "getMigrationProxy()"
      },
      {
        fields: [],
        kind: "function",
        name: "getMigrationTarget",
        signature: "getMigrationTarget()"
      },
      {
        fields: [
          "staker"
        ],
        kind: "function",
        name: "getMultiplier",
        signature: "getMultiplier(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "getMultiplierDuration",
        signature: "getMultiplierDuration()"
      },
      {
        fields: [],
        kind: "function",
        name: "getNumOperators",
        signature: "getNumOperators()"
      },
      {
        fields: [],
        kind: "function",
        name: "getOperators",
        signature: "getOperators()"
      },
      {
        fields: [
          "staker"
        ],
        kind: "function",
        name: "getRemovedPrincipal",
        signature: "getRemovedPrincipal(address)"
      },
      {
        fields: [
          "staker"
        ],
        kind: "function",
        name: "getReward",
        signature: "getReward(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "getRewardBuckets",
        signature: "getRewardBuckets()"
      },
      {
        fields: [],
        kind: "function",
        name: "getRewardPerTokenUpdatedAt",
        signature: "getRewardPerTokenUpdatedAt()"
      },
      {
        fields: [],
        kind: "function",
        name: "getRewardVault",
        signature: "getRewardVault()"
      },
      {
        fields: [
          "role"
        ],
        kind: "function",
        name: "getRoleAdmin",
        signature: "getRoleAdmin(bytes32)"
      },
      {
        fields: [
          "slasher"
        ],
        kind: "function",
        name: "getSlashCapacity",
        signature: "getSlashCapacity(address)"
      },
      {
        fields: [
          "slasher"
        ],
        kind: "function",
        name: "getSlasherConfig",
        signature: "getSlasherConfig(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "getStakerLimits",
        signature: "getStakerLimits()"
      },
      {
        fields: [
          "staker"
        ],
        kind: "function",
        name: "getStakerPrincipal",
        signature: "getStakerPrincipal(address)"
      },
      {
        fields: [
          "staker",
          "blockNumber"
        ],
        kind: "function",
        name: "getStakerPrincipalAt",
        signature: "getStakerPrincipalAt(address,uint256)"
      },
      {
        fields: [
          "staker"
        ],
        kind: "function",
        name: "getStakerStakedAtTime",
        signature: "getStakerStakedAtTime(address)"
      },
      {
        fields: [
          "staker",
          "blockNumber"
        ],
        kind: "function",
        name: "getStakerStakedAtTimeAt",
        signature: "getStakerStakedAtTimeAt(address,uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "getStakingPools",
        signature: "getStakingPools()"
      },
      {
        fields: [
          "staker"
        ],
        kind: "function",
        name: "getStoredReward",
        signature: "getStoredReward(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "getTotalPrincipal",
        signature: "getTotalPrincipal()"
      },
      {
        fields: [
          "staker"
        ],
        kind: "function",
        name: "getUnbondingEndsAt",
        signature: "getUnbondingEndsAt(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "getUnbondingParams",
        signature: "getUnbondingParams()"
      },
      {
        fields: [],
        kind: "function",
        name: "getUnvestedRewards",
        signature: "getUnvestedRewards()"
      },
      {
        fields: [
          "role",
          "account"
        ],
        kind: "function",
        name: "grantRole",
        signature: "grantRole(bytes32,address)"
      },
      {
        fields: [
          "staker",
          "proof"
        ],
        kind: "function",
        name: "hasAccess",
        signature: "hasAccess(address,bytes32[])"
      },
      {
        fields: [],
        kind: "function",
        name: "hasRewardAdded",
        signature: "hasRewardAdded()"
      },
      {
        fields: [
          "stakingPool"
        ],
        kind: "function",
        name: "hasRewardDurationEnded",
        signature: "hasRewardDurationEnded(address)"
      },
      {
        fields: [
          "role",
          "account"
        ],
        kind: "function",
        name: "hasRole",
        signature: "hasRole(bytes32,address)"
      },
      {
        fields: [],
        kind: "function",
        name: "isActive",
        signature: "isActive()"
      },
      {
        fields: [],
        kind: "function",
        name: "isOpen",
        signature: "isOpen()"
      },
      {
        fields: [
          "staker"
        ],
        kind: "function",
        name: "isOperator",
        signature: "isOperator(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "isPaused",
        signature: "isPaused()"
      },
      {
        fields: [
          "staker"
        ],
        kind: "function",
        name: "isRemoved",
        signature: "isRemoved(address)"
      },
      {
        fields: [
          "data"
        ],
        kind: "function",
        name: "migrate",
        signature: "migrate(bytes)"
      },
      {
        fields: [
          "sender",
          "amount",
          "data"
        ],
        kind: "function",
        name: "onTokenTransfer",
        signature: "onTokenTransfer(address,uint256,bytes)"
      },
      {
        fields: [],
        kind: "function",
        name: "open",
        signature: "open()"
      },
      {
        fields: [],
        kind: "function",
        name: "owner",
        signature: "owner()"
      },
      {
        fields: [],
        kind: "function",
        name: "paused",
        signature: "paused()"
      },
      {
        fields: [],
        kind: "function",
        name: "pendingDefaultAdmin",
        signature: "pendingDefaultAdmin()"
      },
      {
        fields: [],
        kind: "function",
        name: "pendingDefaultAdminDelay",
        signature: "pendingDefaultAdminDelay()"
      },
      {
        fields: [
          "operators"
        ],
        kind: "function",
        name: "removeOperators",
        signature: "removeOperators(address[])"
      },
      {
        fields: [
          "slasher"
        ],
        kind: "function",
        name: "removeSlasher",
        signature: "removeSlasher(address)"
      },
      {
        fields: [
          "role",
          "account"
        ],
        kind: "function",
        name: "renounceRole",
        signature: "renounceRole(bytes32,address)"
      },
      {
        fields: [
          "role",
          "account"
        ],
        kind: "function",
        name: "revokeRole",
        signature: "revokeRole(bytes32,address)"
      },
      {
        fields: [],
        kind: "function",
        name: "rollbackDefaultAdminDelay",
        signature: "rollbackDefaultAdminDelay()"
      },
      {
        fields: [
          "claimPeriod"
        ],
        kind: "function",
        name: "setClaimPeriod",
        signature: "setClaimPeriod(uint256)"
      },
      {
        fields: [
          "newDelegationRate"
        ],
        kind: "function",
        name: "setDelegationRate",
        signature: "setDelegationRate(uint256)"
      },
      {
        fields: [
          "newMerkleRoot"
        ],
        kind: "function",
        name: "setMerkleRoot",
        signature: "setMerkleRoot(bytes32)"
      },
      {
        fields: [
          "migrationProxy"
        ],
        kind: "function",
        name: "setMigrationProxy",
        signature: "setMigrationProxy(address)"
      },
      {
        fields: [
          "newMigrationTarget"
        ],
        kind: "function",
        name: "setMigrationTarget",
        signature: "setMigrationTarget(address)"
      },
      {
        fields: [
          "newOperatorStakingPool"
        ],
        kind: "function",
        name: "setOperatorStakingPool",
        signature: "setOperatorStakingPool(address)"
      },
      {
        fields: [
          "maxPoolSize",
          "maxPrincipalPerStaker"
        ],
        kind: "function",
        name: "setPoolConfig",
        signature: "setPoolConfig(uint256,uint256)"
      },
      {
        fields: [
          "newRewardVault"
        ],
        kind: "function",
        name: "setRewardVault",
        signature: "setRewardVault(address)"
      },
      {
        fields: [
          "slasher",
          "config"
        ],
        kind: "function",
        name: "setSlasherConfig",
        signature: "setSlasherConfig(address,(uint256,uint256))"
      },
      {
        fields: [
          "newUnbondingPeriod"
        ],
        kind: "function",
        name: "setUnbondingPeriod",
        signature: "setUnbondingPeriod(uint256)"
      },
      {
        fields: [
          "stakers",
          "alerter",
          "principalAmount",
          "alerterRewardAmount"
        ],
        kind: "function",
        name: "slashAndReward",
        signature: "slashAndReward(address[],address,uint256,uint256)"
      },
      {
        fields: [
          "interfaceId"
        ],
        kind: "function",
        name: "supportsInterface",
        signature: "supportsInterface(bytes4)"
      },
      {
        fields: [],
        kind: "function",
        name: "typeAndVersion",
        signature: "typeAndVersion()"
      },
      {
        fields: [],
        kind: "function",
        name: "unbond",
        signature: "unbond()"
      },
      {
        fields: [
          "amount"
        ],
        kind: "function",
        name: "unstake",
        signature: "unstake(uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "unstakeRemovedPrincipal",
        signature: "unstakeRemovedPrincipal()"
      },
      {
        fields: [
          "staker",
          "stakerPrincipal"
        ],
        kind: "function",
        name: "updateReward",
        signature: "updateReward(address,uint256)"
      },
      {
        fields: [
          "amount"
        ],
        kind: "function",
        name: "withdrawAlerterReward",
        signature: "withdrawAlerterReward(uint256)"
      }
    ],
    label: "Chainlink Staking v0.2",
    vocabularies: {}
  },
  chainlink_vrf: {
    entries: [
      {
        fields: [
          "minimumRequestConfirmations",
          "maxGasLimit",
          "stalenessSeconds",
          "gasAfterPaymentCalculation",
          "fallbackWeiPerUnitLink",
          "fulfillmentFlatFeeNativePPM",
          "fulfillmentFlatFeeLinkDiscountPPM",
          "nativePremiumPercentage",
          "linkPremiumPercentage"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet(uint16,uint32,uint32,uint32,int256,uint32,uint32,uint8,uint8)"
      },
      {
        fields: [
          "wrapperGasOverhead",
          "coordinatorGasOverheadNative",
          "coordinatorGasOverheadLink",
          "coordinatorGasOverheadPerWord",
          "coordinatorNativePremiumPercentage",
          "coordinatorLinkPremiumPercentage",
          "keyHash",
          "maxNumWords",
          "stalenessSeconds",
          "fallbackWeiPerUnitLink",
          "fulfillmentFlatFeeNativePPM",
          "fulfillmentFlatFeeLinkDiscountPPM"
        ],
        kind: "event",
        name: "ConfigSet",
        signature: "ConfigSet(uint32,uint32,uint32,uint16,uint8,uint8,bytes32,uint8,uint32,int256,uint32,uint32)"
      },
      {
        fields: [
          "coordinatorAddress"
        ],
        kind: "event",
        name: "CoordinatorDeregistered",
        signature: "CoordinatorDeregistered(address)"
      },
      {
        fields: [
          "coordinatorAddress"
        ],
        kind: "event",
        name: "CoordinatorRegistered",
        signature: "CoordinatorRegistered(address)"
      },
      {
        fields: [
          "vrfCoordinator"
        ],
        kind: "event",
        name: "CoordinatorSet",
        signature: "CoordinatorSet(address)"
      },
      {
        fields: [],
        kind: "event",
        name: "Disabled",
        signature: "Disabled()"
      },
      {
        fields: [],
        kind: "event",
        name: "Enabled",
        signature: "Enabled()"
      },
      {
        fields: [
          "requestId",
          "reason"
        ],
        kind: "event",
        name: "ErrorReturned",
        signature: "ErrorReturned(uint256,string)"
      },
      {
        fields: [
          "requestId",
          "fallbackWeiPerUnitLink"
        ],
        kind: "event",
        name: "FallbackWeiPerUnitLinkUsed",
        signature: "FallbackWeiPerUnitLinkUsed(uint256,int256)"
      },
      {
        fields: [
          "size"
        ],
        kind: "event",
        name: "FulfillmentTxSizeSet",
        signature: "FulfillmentTxSizeSet(uint32)"
      },
      {
        fields: [
          "to",
          "amount"
        ],
        kind: "event",
        name: "FundsRecovered",
        signature: "FundsRecovered(address,uint256)"
      },
      {
        fields: [
          "mode",
          "coefficient"
        ],
        kind: "event",
        name: "L1FeeCalculationSet",
        signature: "L1FeeCalculationSet(uint8,uint8)"
      },
      {
        fields: [
          "fee"
        ],
        kind: "event",
        name: "L1GasFee",
        signature: "L1GasFee(uint256)"
      },
      {
        fields: [
          "newCoordinator",
          "subId"
        ],
        kind: "event",
        name: "MigrationCompleted",
        signature: "MigrationCompleted(address,uint256)"
      },
      {
        fields: [
          "to",
          "amount"
        ],
        kind: "event",
        name: "NativeFundsRecovered",
        signature: "NativeFundsRecovered(address,uint256)"
      },
      {
        fields: [
          "to",
          "amount"
        ],
        kind: "event",
        name: "NativeWithdrawn",
        signature: "NativeWithdrawn(address,uint256)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferRequested",
        signature: "OwnershipTransferRequested(address,address)"
      },
      {
        fields: [
          "from",
          "to"
        ],
        kind: "event",
        name: "OwnershipTransferred",
        signature: "OwnershipTransferred(address,address)"
      },
      {
        fields: [
          "keyHash",
          "maxGas"
        ],
        kind: "event",
        name: "ProvingKeyDeregistered",
        signature: "ProvingKeyDeregistered(bytes32,uint64)"
      },
      {
        fields: [
          "keyHash",
          "maxGas"
        ],
        kind: "event",
        name: "ProvingKeyRegistered",
        signature: "ProvingKeyRegistered(bytes32,uint64)"
      },
      {
        fields: [
          "requestId",
          "outputSeed",
          "subId",
          "payment",
          "nativePayment",
          "success",
          "onlyPremium"
        ],
        kind: "event",
        name: "RandomWordsFulfilled",
        signature: "RandomWordsFulfilled(uint256,uint256,uint256,uint96,bool,bool,bool)"
      },
      {
        fields: [
          "keyHash",
          "requestId",
          "preSeed",
          "subId",
          "minimumRequestConfirmations",
          "callbackGasLimit",
          "numWords",
          "extraArgs",
          "sender"
        ],
        kind: "event",
        name: "RandomWordsRequested",
        signature: "RandomWordsRequested(bytes32,uint256,uint256,uint256,uint16,uint32,uint32,bytes,address)"
      },
      {
        fields: [
          "requestId",
          "lowLevelData"
        ],
        kind: "event",
        name: "RawErrorReturned",
        signature: "RawErrorReturned(uint256,bytes)"
      },
      {
        fields: [
          "subId",
          "to",
          "amountLink",
          "amountNative"
        ],
        kind: "event",
        name: "SubscriptionCanceled",
        signature: "SubscriptionCanceled(uint256,address,uint256,uint256)"
      },
      {
        fields: [
          "subId",
          "consumer"
        ],
        kind: "event",
        name: "SubscriptionConsumerAdded",
        signature: "SubscriptionConsumerAdded(uint256,address)"
      },
      {
        fields: [
          "subId",
          "consumer"
        ],
        kind: "event",
        name: "SubscriptionConsumerRemoved",
        signature: "SubscriptionConsumerRemoved(uint256,address)"
      },
      {
        fields: [
          "subId",
          "owner"
        ],
        kind: "event",
        name: "SubscriptionCreated",
        signature: "SubscriptionCreated(uint256,address)"
      },
      {
        fields: [
          "subId",
          "oldBalance",
          "newBalance"
        ],
        kind: "event",
        name: "SubscriptionFunded",
        signature: "SubscriptionFunded(uint256,uint256,uint256)"
      },
      {
        fields: [
          "subId",
          "oldNativeBalance",
          "newNativeBalance"
        ],
        kind: "event",
        name: "SubscriptionFundedWithNative",
        signature: "SubscriptionFundedWithNative(uint256,uint256,uint256)"
      },
      {
        fields: [
          "subId",
          "from",
          "to"
        ],
        kind: "event",
        name: "SubscriptionOwnerTransferRequested",
        signature: "SubscriptionOwnerTransferRequested(uint256,address,address)"
      },
      {
        fields: [
          "subId",
          "from",
          "to"
        ],
        kind: "event",
        name: "SubscriptionOwnerTransferred",
        signature: "SubscriptionOwnerTransferred(uint256,address,address)"
      },
      {
        fields: [
          "to",
          "amount"
        ],
        kind: "event",
        name: "Withdrawn",
        signature: "Withdrawn(address,uint256)"
      },
      {
        fields: [
          "requestId",
          "consumer"
        ],
        kind: "event",
        name: "WrapperFulfillmentFailed",
        signature: "WrapperFulfillmentFailed(uint256,address)"
      },
      {
        fields: [],
        kind: "function",
        name: "BLOCKHASH_STORE",
        signature: "BLOCKHASH_STORE()"
      },
      {
        fields: [],
        kind: "function",
        name: "COORDINATOR",
        signature: "COORDINATOR()"
      },
      {
        fields: [],
        kind: "function",
        name: "LINK",
        signature: "LINK()"
      },
      {
        fields: [],
        kind: "function",
        name: "LINK_NATIVE_FEED",
        signature: "LINK_NATIVE_FEED()"
      },
      {
        fields: [],
        kind: "function",
        name: "MAX_CONSUMERS",
        signature: "MAX_CONSUMERS()"
      },
      {
        fields: [],
        kind: "function",
        name: "MAX_NUM_WORDS",
        signature: "MAX_NUM_WORDS()"
      },
      {
        fields: [],
        kind: "function",
        name: "MAX_REQUEST_CONFIRMATIONS",
        signature: "MAX_REQUEST_CONFIRMATIONS()"
      },
      {
        fields: [],
        kind: "function",
        name: "SUBSCRIPTION_ID",
        signature: "SUBSCRIPTION_ID()"
      },
      {
        fields: [],
        kind: "function",
        name: "acceptOwnership",
        signature: "acceptOwnership()"
      },
      {
        fields: [
          "subId"
        ],
        kind: "function",
        name: "acceptSubscriptionOwnerTransfer",
        signature: "acceptSubscriptionOwnerTransfer(uint256)"
      },
      {
        fields: [
          "subId",
          "consumer"
        ],
        kind: "function",
        name: "addConsumer",
        signature: "addConsumer(uint256,address)"
      },
      {
        fields: [
          "_callbackGasLimit",
          "_numWords"
        ],
        kind: "function",
        name: "calculateRequestPrice",
        signature: "calculateRequestPrice(uint32,uint32)"
      },
      {
        fields: [
          "_callbackGasLimit",
          "_numWords"
        ],
        kind: "function",
        name: "calculateRequestPriceNative",
        signature: "calculateRequestPriceNative(uint32,uint32)"
      },
      {
        fields: [
          "subId",
          "to"
        ],
        kind: "function",
        name: "cancelSubscription",
        signature: "cancelSubscription(uint256,address)"
      },
      {
        fields: [
          "extraArgs",
          "isLinkMode"
        ],
        kind: "function",
        name: "checkPaymentMode",
        signature: "checkPaymentMode(bytes,bool)"
      },
      {
        fields: [],
        kind: "function",
        name: "createSubscription",
        signature: "createSubscription()"
      },
      {
        fields: [
          "target"
        ],
        kind: "function",
        name: "deregisterMigratableCoordinator",
        signature: "deregisterMigratableCoordinator(address)"
      },
      {
        fields: [
          "publicProvingKey"
        ],
        kind: "function",
        name: "deregisterProvingKey",
        signature: "deregisterProvingKey(uint256[2])"
      },
      {
        fields: [],
        kind: "function",
        name: "disable",
        signature: "disable()"
      },
      {
        fields: [],
        kind: "function",
        name: "enable",
        signature: "enable()"
      },
      {
        fields: [
          "_callbackGasLimit",
          "_numWords",
          "_requestGasPriceWei"
        ],
        kind: "function",
        name: "estimateRequestPrice",
        signature: "estimateRequestPrice(uint32,uint32,uint256)"
      },
      {
        fields: [
          "_callbackGasLimit",
          "_numWords",
          "_requestGasPriceWei"
        ],
        kind: "function",
        name: "estimateRequestPriceNative",
        signature: "estimateRequestPriceNative(uint32,uint32,uint256)"
      },
      {
        fields: [
          "proof",
          "rc",
          "onlyPremium"
        ],
        kind: "function",
        name: "fulfillRandomWords",
        signature: "fulfillRandomWords((uint256[2],uint256[2],uint256,uint256,uint256,address,uint256[2],uint256[2],uint256),(uint64,uint256,uint32,uint32,address,bytes),bool)"
      },
      {
        fields: [
          "proofs",
          "rcs"
        ],
        kind: "function",
        name: "fulfillRandomWords",
        signature: "fulfillRandomWords((uint256[2],uint256[2],uint256,uint256,uint256,address,uint256[2],uint256[2],uint256)[],(uint64,uint256,uint32,uint32,address,bytes)[])"
      },
      {
        fields: [
          "subId"
        ],
        kind: "function",
        name: "fundSubscriptionWithNative",
        signature: "fundSubscriptionWithNative(uint256)"
      },
      {
        fields: [
          "startIndex",
          "maxCount"
        ],
        kind: "function",
        name: "getActiveSubscriptionIds",
        signature: "getActiveSubscriptionIds(uint256,uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "getConfig",
        signature: "getConfig()"
      },
      {
        fields: [
          "subId"
        ],
        kind: "function",
        name: "getSubscription",
        signature: "getSubscription(uint256)"
      },
      {
        fields: [
          "publicKey"
        ],
        kind: "function",
        name: "hashOfKey",
        signature: "hashOfKey(uint256[2])"
      },
      {
        fields: [],
        kind: "function",
        name: "lastRequestId",
        signature: "lastRequestId()"
      },
      {
        fields: [],
        kind: "function",
        name: "link",
        signature: "link()"
      },
      {
        fields: [],
        kind: "function",
        name: "linkNativeFeed",
        signature: "linkNativeFeed()"
      },
      {
        fields: [
          "subId",
          "newCoordinator"
        ],
        kind: "function",
        name: "migrate",
        signature: "migrate(uint256,address)"
      },
      {
        fields: [
          "",
          "amount",
          "data"
        ],
        kind: "function",
        name: "onTokenTransfer",
        signature: "onTokenTransfer(address,uint256,bytes)"
      },
      {
        fields: [],
        kind: "function",
        name: "owner",
        signature: "owner()"
      },
      {
        fields: [
          "subId"
        ],
        kind: "function",
        name: "ownerCancelSubscription",
        signature: "ownerCancelSubscription(uint256)"
      },
      {
        fields: [
          "subId"
        ],
        kind: "function",
        name: "pendingRequestExists",
        signature: "pendingRequestExists(uint256)"
      },
      {
        fields: [
          "requestId",
          "randomWords"
        ],
        kind: "function",
        name: "rawFulfillRandomWords",
        signature: "rawFulfillRandomWords(uint256,uint256[])"
      },
      {
        fields: [
          "to"
        ],
        kind: "function",
        name: "recoverFunds",
        signature: "recoverFunds(address)"
      },
      {
        fields: [
          "to"
        ],
        kind: "function",
        name: "recoverNativeFunds",
        signature: "recoverNativeFunds(address)"
      },
      {
        fields: [
          "target"
        ],
        kind: "function",
        name: "registerMigratableCoordinator",
        signature: "registerMigratableCoordinator(address)"
      },
      {
        fields: [
          "publicProvingKey",
          "maxGas"
        ],
        kind: "function",
        name: "registerProvingKey",
        signature: "registerProvingKey(uint256[2],uint64)"
      },
      {
        fields: [
          "subId",
          "consumer"
        ],
        kind: "function",
        name: "removeConsumer",
        signature: "removeConsumer(uint256,address)"
      },
      {
        fields: [
          "req"
        ],
        kind: "function",
        name: "requestRandomWords",
        signature: "requestRandomWords((bytes32,uint256,uint16,uint32,uint32,bytes))"
      },
      {
        fields: [
          "_callbackGasLimit",
          "_requestConfirmations",
          "_numWords",
          "extraArgs"
        ],
        kind: "function",
        name: "requestRandomWordsInNative",
        signature: "requestRandomWordsInNative(uint32,uint16,uint32,bytes)"
      },
      {
        fields: [
          "subId",
          "newOwner"
        ],
        kind: "function",
        name: "requestSubscriptionOwnerTransfer",
        signature: "requestSubscriptionOwnerTransfer(uint256,address)"
      },
      {
        fields: [
          ""
        ],
        kind: "function",
        name: "s_callbacks",
        signature: "s_callbacks(uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "s_config",
        signature: "s_config()"
      },
      {
        fields: [],
        kind: "function",
        name: "s_configured",
        signature: "s_configured()"
      },
      {
        fields: [],
        kind: "function",
        name: "s_currentSubNonce",
        signature: "s_currentSubNonce()"
      },
      {
        fields: [],
        kind: "function",
        name: "s_disabled",
        signature: "s_disabled()"
      },
      {
        fields: [],
        kind: "function",
        name: "s_fallbackWeiPerUnitLink",
        signature: "s_fallbackWeiPerUnitLink()"
      },
      {
        fields: [],
        kind: "function",
        name: "s_fulfillmentTxSizeBytes",
        signature: "s_fulfillmentTxSizeBytes()"
      },
      {
        fields: [],
        kind: "function",
        name: "s_l1FeeCalculationMode",
        signature: "s_l1FeeCalculationMode()"
      },
      {
        fields: [],
        kind: "function",
        name: "s_l1FeeCoefficient",
        signature: "s_l1FeeCoefficient()"
      },
      {
        fields: [
          ""
        ],
        kind: "function",
        name: "s_provingKeyHashes",
        signature: "s_provingKeyHashes(uint256)"
      },
      {
        fields: [
          ""
        ],
        kind: "function",
        name: "s_provingKeys",
        signature: "s_provingKeys(bytes32)"
      },
      {
        fields: [
          ""
        ],
        kind: "function",
        name: "s_requestCommitments",
        signature: "s_requestCommitments(uint256)"
      },
      {
        fields: [],
        kind: "function",
        name: "s_totalBalance",
        signature: "s_totalBalance()"
      },
      {
        fields: [],
        kind: "function",
        name: "s_totalNativeBalance",
        signature: "s_totalNativeBalance()"
      },
      {
        fields: [],
        kind: "function",
        name: "s_vrfCoordinator",
        signature: "s_vrfCoordinator()"
      },
      {
        fields: [
          "minimumRequestConfirmations",
          "maxGasLimit",
          "stalenessSeconds",
          "gasAfterPaymentCalculation",
          "fallbackWeiPerUnitLink",
          "fulfillmentFlatFeeNativePPM",
          "fulfillmentFlatFeeLinkDiscountPPM",
          "nativePremiumPercentage",
          "linkPremiumPercentage"
        ],
        kind: "function",
        name: "setConfig",
        signature: "setConfig(uint16,uint32,uint32,uint32,int256,uint32,uint32,uint8,uint8)"
      },
      {
        fields: [
          "_wrapperGasOverhead",
          "_coordinatorGasOverheadNative",
          "_coordinatorGasOverheadLink",
          "_coordinatorGasOverheadPerWord",
          "_coordinatorNativePremiumPercentage",
          "_coordinatorLinkPremiumPercentage",
          "_keyHash",
          "_maxNumWords",
          "_stalenessSeconds",
          "_fallbackWeiPerUnitLink",
          "_fulfillmentFlatFeeNativePPM",
          "_fulfillmentFlatFeeLinkDiscountPPM"
        ],
        kind: "function",
        name: "setConfig",
        signature: "setConfig(uint32,uint32,uint32,uint16,uint8,uint8,bytes32,uint8,uint32,int256,uint32,uint32)"
      },
      {
        fields: [
          "_vrfCoordinator"
        ],
        kind: "function",
        name: "setCoordinator",
        signature: "setCoordinator(address)"
      },
      {
        fields: [
          "_size"
        ],
        kind: "function",
        name: "setFulfillmentTxSize",
        signature: "setFulfillmentTxSize(uint32)"
      },
      {
        fields: [
          "mode",
          "coefficient"
        ],
        kind: "function",
        name: "setL1FeeCalculation",
        signature: "setL1FeeCalculation(uint8,uint8)"
      },
      {
        fields: [
          "link",
          "linkNativeFeed"
        ],
        kind: "function",
        name: "setLINKAndLINKNativeFeed",
        signature: "setLINKAndLINKNativeFeed(address,address)"
      },
      {
        fields: [
          "to"
        ],
        kind: "function",
        name: "transferOwnership",
        signature: "transferOwnership(address)"
      },
      {
        fields: [],
        kind: "function",
        name: "typeAndVersion",
        signature: "typeAndVersion()"
      },
      {
        fields: [
          "recipient"
        ],
        kind: "function",
        name: "withdraw",
        signature: "withdraw(address)"
      },
      {
        fields: [
          "recipient"
        ],
        kind: "function",
        name: "withdrawNative",
        signature: "withdrawNative(address)"
      }
    ],
    label: "Chainlink VRF v2.5",
    vocabularies: {}
  }
};

// src/protocols/aave_v3/labels.ts
var AAVE_V3 = {
  protocolId: "aave_v3",
  displayName: "Aave V3",
  docs: "https://aave.com/docs"
};

// src/protocols/op_stack_l1_block/labels.ts
var OP_STACK_L1_BLOCK = {
  protocolId: "op_stack_l1_block",
  displayName: "OP Stack L1Block",
  docs: "https://specs.optimism.io/protocol/deposits.html"
};

// src/protocols/aerodrome/labels.ts
var AERODROME = {
  protocolId: "aerodrome",
  displayName: "Aerodrome / Velodrome",
  docs: "https://aerodrome.finance/docs"
};

// src/protocols/hyperlane/labels.ts
var HYPERLANE = {
  protocolId: "hyperlane",
  displayName: "Hyperlane",
  docs: "https://docs.hyperlane.xyz"
};

// src/protocols/seaport/labels.ts
var SEAPORT = {
  protocolId: "seaport",
  displayName: "Seaport",
  docs: "https://docs.opensea.io/docs/seaport"
};
var GNOSIS_SAFE_BRAND = {
  id: "gnosis_safe",
  displayName: "Safe",
  docs: "https://docs.safe.global",
  category: "safe",
  icon: Shield
};
var SUSHISWAP_BRAND = {
  id: "sushiswap_v2_factory",
  displayName: "SushiSwap",
  docs: "https://docs.sushi.com/contracts/classic",
  category: "exchange",
  icon: Droplets
};
var DEPLOYER_BRAND_LIST = [
  GNOSIS_SAFE_BRAND,
  SUSHISWAP_BRAND
];

// src/theme/brand/catalog.ts
var STANDARD_BRANDS = {
  [ERC20.protocolId]: {
    id: ERC20.protocolId,
    displayName: ERC20.displayName,
    docs: ERC20.docs,
    category: "token",
    icon: Coins
  },
  [ERC721.protocolId]: {
    id: ERC721.protocolId,
    displayName: ERC721.displayName,
    docs: ERC721.docs,
    category: "nft",
    icon: Image
  },
  [ERC1155.protocolId]: {
    id: ERC1155.protocolId,
    displayName: ERC1155.displayName,
    docs: ERC1155.docs,
    category: "nft",
    // Distinguish multi-token (1155) from single NFTs (721) at a glance.
    icon: Layers
  },
  [NATIVE_TRANSFER.protocolId]: {
    id: NATIVE_TRANSFER.protocolId,
    displayName: NATIVE_TRANSFER.displayName,
    category: "native",
    icon: Coins
  },
  [PRECOMPILE.protocolId]: {
    id: PRECOMPILE.protocolId,
    displayName: PRECOMPILE.displayName,
    category: "precompile",
    icon: Cpu
  },
  [LOWLEVEL.protocolId]: {
    id: LOWLEVEL.protocolId,
    displayName: LOWLEVEL.displayName,
    // Low-level `receive()`/`fallback()` plumbing reads as quiet structure (like
    // proxy delegation), so the neutral grey keeps these frequent cards calm.
    category: "neutral",
    icon: Workflow
  },
  [DEPLOYMENT.protocolId]: {
    id: DEPLOYMENT.protocolId,
    displayName: DEPLOYMENT.displayName,
    category: "deployment",
    icon: FileCode2
  },
  [PROXY.protocolId]: {
    id: PROXY.protocolId,
    displayName: PROXY.displayName,
    category: "proxy",
    icon: Network
  },
  [OWNERSHIP.protocolId]: {
    id: OWNERSHIP.protocolId,
    displayName: OWNERSHIP.displayName,
    category: "ownership",
    icon: KeyRound
  },
  [ACCESS.protocolId]: {
    id: ACCESS.protocolId,
    displayName: ACCESS.displayName,
    category: "access",
    icon: Shield
  },
  [ERC4626.protocolId]: {
    id: ERC4626.protocolId,
    displayName: ERC4626.displayName,
    docs: ERC4626.docs,
    category: "vault",
    icon: Vault
  }
};
var DECODE_PROTOCOL_BRANDS = {
  [CHAINLINK_CCIP.protocolId]: {
    id: CHAINLINK_CCIP.protocolId,
    displayName: CHAINLINK_CCIP.displayName,
    docs: CHAINLINK_CCIP.docs,
    category: "bridge",
    icon: Waypoints,
    // Matches the backend `SceneOffchainSpec.label` for the CCIP DON node.
    graphAliases: ["Chainlink CCIP"]
  },
  [WORMHOLE.protocolId]: {
    id: WORMHOLE.protocolId,
    displayName: WORMHOLE.displayName,
    docs: WORMHOLE.docs,
    category: "bridge",
    // Override the bridge default (blue) so Wormhole reads distinctly from CCIP.
    accent: "violet",
    icon: Radio,
    // Matches the backend `SceneOffchainSpec.label` for the Wormhole attestation node.
    graphAliases: ["Wormhole Guardians"]
  },
  [CHAINLINK_AUTOMATION.protocolId]: {
    id: CHAINLINK_AUTOMATION.protocolId,
    displayName: CHAINLINK_AUTOMATION.displayName,
    docs: CHAINLINK_AUTOMATION.docs,
    category: "oracle",
    // Override the oracle default (violet) so it stays distinct from Wormhole.
    accent: "indigo",
    icon: Cog
  },
  [HYPERLANE.protocolId]: {
    id: HYPERLANE.protocolId,
    displayName: HYPERLANE.displayName,
    docs: HYPERLANE.docs,
    category: "bridge",
    // Hyperlane had no entry at all, so it resolved to the NEUTRAL brand — no
    // accent, no glyph, no name — while every other bridge on the same
    // transaction rendered branded. It is also the protocol this package
    // already treats as most confusable with CCIP: it surfaces its messageId
    // under the SHARED `message_id` kind, and `registerHyperlane` exists in the
    // defaults specifically so CCIP's unscoped hint does not claim it. Being
    // the one bridge with no colour, next to the one it is mistaken for, is the
    // worst place to have this gap.
    //
    // Rose, because blue (CCIP), violet (Wormhole), cyan (Mayan) and indigo
    // (LayerZero, below) are taken by the bridges it appears alongside.
    accent: "rose",
    icon: Network,
    // Matches the backend `SceneOffchainSpec.label` for the Hyperlane validator
    // / relayer node, so the off-chain graph node resolves to this brand too.
    graphAliases: ["Hyperlane", "Hyperlane Validators", "Hyperlane Relayer"]
  },
  [OKX_XBRIDGE.protocolId]: {
    id: OKX_XBRIDGE.protocolId,
    displayName: OKX_XBRIDGE.displayName,
    docs: OKX_XBRIDGE.docs,
    category: "aggregator",
    icon: Shuffle
  },
  [MAYAN.protocolId]: {
    id: MAYAN.protocolId,
    displayName: MAYAN.displayName,
    docs: MAYAN.docs,
    category: "bridge",
    // Override the bridge default (blue, CCIP) so Mayan reads as its turquoise
    // brand and stays distinct from CCIP (blue) + Wormhole (violet); the `Waves`
    // glyph nods to the Mayan ocean motif.
    accent: "cyan",
    icon: Waves,
    // Matches the backend `SceneOffchainSpec.label` for the Mayan solver/driver
    // network node, so the off-chain graph node resolves to this brand.
    graphAliases: ["Mayan Drivers", "Mayan"]
  },
  [LIFI.protocolId]: {
    id: LIFI.protocolId,
    displayName: LIFI.displayName,
    docs: LIFI.docs,
    category: "aggregator",
    // Override the aggregator default (teal, shared with OKX) so LI.FI reads as a
    // warm amber — close to its own orange brand and, more importantly, distinct
    // from the bridge it most often wraps (Mayan, cyan) on the same tx. The
    // `Route` glyph nods to LI.FI routing a transfer across bridges.
    accent: "amber",
    icon: Route
  },
  // The DEX / aggregator family. Uniswap owns exchange pink (below), so each of
  // these takes a distinct accent — they routinely appear on the SAME
  // transaction as Uniswap and as each other (an aggregator routing through a
  // pool), and a shared colour would make "who chose this venue" unreadable.
  [COWSWAP.protocolId]: {
    id: COWSWAP.protocolId,
    displayName: COWSWAP.displayName,
    docs: COWSWAP.docs,
    category: "exchange",
    // CoW's own brand green, and distinct from the pink Uniswap family it
    // settles through.
    accent: "emerald",
    icon: FileSignature,
    // Matches the backend `SceneOffchainSpec.label` for the CoW solver node.
    graphAliases: ["CoW Protocol", "CoW Swap"]
  },
  [ONEINCH.protocolId]: {
    id: ONEINCH.protocolId,
    displayName: ONEINCH.displayName,
    docs: ONEINCH.docs,
    category: "aggregator",
    // Distinct from OKX (teal default) and LI.FI (amber), the two aggregators
    // it most often shares a transaction with.
    accent: "indigo",
    icon: Shuffle,
    graphAliases: ["1inch"]
  },
  [KYBERSWAP.protocolId]: {
    id: KYBERSWAP.protocolId,
    displayName: KYBERSWAP.displayName,
    docs: KYBERSWAP.docs,
    category: "aggregator",
    accent: "cyan",
    icon: Route,
    graphAliases: ["KyberSwap"]
  },
  [PANCAKESWAP.protocolId]: {
    id: PANCAKESWAP.protocolId,
    displayName: PANCAKESWAP.displayName,
    docs: PANCAKESWAP.docs,
    category: "exchange",
    // Amber, and for the same reason SushiSwap gets violet: PancakeSwap's V2
    // router is byte-identical to Uniswap's and its pairs emit the identical
    // event, so the two are told apart by address alone. Its own colour is what
    // makes that distinction visible at a glance rather than only in the badge.
    accent: "amber",
    icon: Cake,
    graphAliases: ["PancakeSwap", "Pancake"]
  },
  [SUSHISWAP.protocolId]: {
    id: SUSHISWAP.protocolId,
    displayName: SUSHISWAP.displayName,
    docs: SUSHISWAP.docs,
    category: "exchange",
    // The accent that matters most in this file. SushiSwap's router is
    // byte-identical to Uniswap's, so the two are told apart by address alone —
    // giving Sushi its own colour is what makes that distinction visible at a
    // glance rather than only in the protocol badge's text.
    accent: "violet",
    icon: Waves,
    graphAliases: ["SushiSwap"]
  },
  [TOKEMAK_MESSAGING.protocolId]: {
    id: TOKEMAK_MESSAGING.protocolId,
    displayName: TOKEMAK_MESSAGING.displayName,
    docs: TOKEMAK_MESSAGING.docs,
    category: "messaging",
    icon: MessageSquareText
  },
  // Uniswap family — every member reads the same exchange pink so the DEX is one
  // recognizable colour across versions, with per-member glyphs (swap / approval
  // signature / liquidity droplets) and display names that disambiguate version.
  [UNISWAP_UNIVERSAL_ROUTER.protocolId]: {
    id: UNISWAP_UNIVERSAL_ROUTER.protocolId,
    displayName: UNISWAP_UNIVERSAL_ROUTER.displayName,
    docs: UNISWAP_UNIVERSAL_ROUTER.docs,
    category: "exchange",
    icon: Repeat,
    // Matches the backend Universal Router semantic node's `attributes.protocol`.
    graphAliases: ["Uniswap Universal Router"]
  },
  [UNISWAP_PERMIT2.protocolId]: {
    id: UNISWAP_PERMIT2.protocolId,
    displayName: UNISWAP_PERMIT2.displayName,
    docs: UNISWAP_PERMIT2.docs,
    category: "exchange",
    icon: FileSignature
  },
  [UNISWAP_V1_FACTORY.protocolId]: {
    id: UNISWAP_V1_FACTORY.protocolId,
    displayName: UNISWAP_V1_FACTORY.displayName,
    docs: UNISWAP_V1_FACTORY.docs,
    category: "exchange",
    icon: Repeat
  },
  [UNISWAP_V1_EXCHANGE.protocolId]: {
    id: UNISWAP_V1_EXCHANGE.protocolId,
    displayName: UNISWAP_V1_EXCHANGE.displayName,
    docs: UNISWAP_V1_EXCHANGE.docs,
    category: "exchange",
    icon: Repeat
  },
  [UNISWAP_V2_ROUTER.protocolId]: {
    id: UNISWAP_V2_ROUTER.protocolId,
    displayName: UNISWAP_V2_ROUTER.displayName,
    docs: UNISWAP_V2_ROUTER.docs,
    category: "exchange",
    icon: Repeat
  },
  [UNISWAP_V3_ROUTER.protocolId]: {
    id: UNISWAP_V3_ROUTER.protocolId,
    displayName: UNISWAP_V3_ROUTER.displayName,
    docs: UNISWAP_V3_ROUTER.docs,
    category: "exchange",
    icon: Repeat
  },
  [UNISWAP_V4_POOL_MANAGER.protocolId]: {
    id: UNISWAP_V4_POOL_MANAGER.protocolId,
    displayName: UNISWAP_V4_POOL_MANAGER.displayName,
    docs: UNISWAP_V4_POOL_MANAGER.docs,
    category: "exchange",
    icon: Repeat
  },
  [UNISWAP_V4_POSITION_MANAGER.protocolId]: {
    id: UNISWAP_V4_POSITION_MANAGER.protocolId,
    displayName: UNISWAP_V4_POSITION_MANAGER.displayName,
    docs: UNISWAP_V4_POSITION_MANAGER.docs,
    category: "exchange",
    icon: Droplets
  },
  [UNISWAP_V3_POSITION_MANAGER.protocolId]: {
    id: UNISWAP_V3_POSITION_MANAGER.protocolId,
    displayName: UNISWAP_V3_POSITION_MANAGER.displayName,
    docs: UNISWAP_V3_POSITION_MANAGER.docs,
    category: "exchange",
    icon: Droplets
  },
  // The pool half of the family: the factories that DEPLOY pools and the pool
  // contracts themselves. Registered because a deployment frame brands from
  // whichever of the two identified it, and an unregistered id degrades to the
  // neutral "Protocol" badge — which would put a grey chip on a pool creation the
  // backend named precisely.
  [UNISWAP_V2_FACTORY.protocolId]: {
    id: UNISWAP_V2_FACTORY.protocolId,
    displayName: UNISWAP_V2_FACTORY.displayName,
    docs: UNISWAP_V2_FACTORY.docs,
    category: "exchange",
    icon: Droplets
  },
  [UNISWAP_V3_FACTORY.protocolId]: {
    id: UNISWAP_V3_FACTORY.protocolId,
    displayName: UNISWAP_V3_FACTORY.displayName,
    docs: UNISWAP_V3_FACTORY.docs,
    category: "exchange",
    icon: Droplets
  },
  [UNISWAP_V2_PAIR.protocolId]: {
    id: UNISWAP_V2_PAIR.protocolId,
    displayName: UNISWAP_V2_PAIR.displayName,
    docs: UNISWAP_V2_PAIR.docs,
    category: "exchange",
    icon: Droplets
  },
  [UNISWAP_V3_POOL.protocolId]: {
    id: UNISWAP_V3_POOL.protocolId,
    displayName: UNISWAP_V3_POOL.displayName,
    docs: UNISWAP_V3_POOL.docs,
    category: "exchange",
    icon: Droplets
  },
  // The POOL half of PancakeSwap, branded separately from its routers because
  // it is a separate protocol id — and without an entry of its own it fell back
  // to the neutral grey badge labelled "Pancakeswap v3 pool".
  [PANCAKESWAP_V3_POOL.protocolId]: {
    id: PANCAKESWAP_V3_POOL.protocolId,
    displayName: PANCAKESWAP_V3_POOL.displayName,
    docs: PANCAKESWAP_V3_POOL.docs,
    category: "exchange",
    // Droplets, matching the Uniswap pools: a pool is a pool whichever fork
    // minted it, and the glyph should say "liquidity venue" before it says
    // which brand.
    icon: Droplets
  },
  // Smart accounts. `safe` on purpose — the accent map already reads that
  // category as "Safe & friends", and an ERC-4337 account answers the same
  // question a multisig does: who controls this, and who may act for it.
  [ERC4337.protocolId]: {
    id: ERC4337.protocolId,
    displayName: ERC4337.displayName,
    docs: ERC4337.docs,
    category: "safe",
    icon: UserCog
  },
  // The MESSAGE layer, not the OFT token bridge — `bridge` to match the
  // definition's own category so the taxonomy reads the same on both sides.
  [LAYERZERO_V2.protocolId]: {
    id: LAYERZERO_V2.protocolId,
    displayName: LAYERZERO_V2.displayName,
    docs: LAYERZERO_V2.docs,
    category: "bridge",
    // Override the bridge default (blue) for the same reason Wormhole and Mayan
    // do: that default IS CCIP's colour, and a message bridge sharing it with
    // the bridge it is most often confused for defeats the point of the accent.
    // This entry was the one bridge that never got its override.
    accent: "indigo",
    icon: BadgeCheck
  },
  // An intents bridge whose destination leg is a solver paying out, so its
  // cards land beside the venues the solver used. A distinct accent from the
  // bridge default matters more here than for most: a Relay fill routinely
  // renders next to a KyberSwap or Uniswap swap in the same transaction, and
  // sharing the generic bridge blue would blur which of the two moved the money.
  [RELAY.protocolId]: {
    id: RELAY.protocolId,
    displayName: RELAY.displayName,
    category: "bridge",
    accent: "emerald",
    icon: ArrowLeftRight
  },
  // The lending position: supply, borrow, repay, liquidate. A bank glyph rather
  // than a coin — what distinguishes Aave from a transfer is the OBLIGATION, not
  // the asset that moved.
  [AAVE_V3.protocolId]: {
    id: AAVE_V3.protocolId,
    displayName: AAVE_V3.displayName,
    docs: AAVE_V3.docs,
    category: "lending",
    icon: Landmark
  },
  // One brand for two chains, mirroring the single backend definition — see the
  // labels module for why the name carries both.
  [AERODROME.protocolId]: {
    id: AERODROME.protocolId,
    displayName: AERODROME.displayName,
    docs: AERODROME.docs,
    category: "exchange",
    icon: Wind
  },
  // The chain's own system contract, not an application. ``precompile`` is the
  // nearest existing category and the right reading: a predeploy at a reserved
  // address is low-level infrastructure, and its cool indigo keeps it visually
  // quiet — which matters when it appears on 100% of blocks and must never
  // compete with the transaction a reader actually opened.
  [OP_STACK_L1_BLOCK.protocolId]: {
    id: OP_STACK_L1_BLOCK.protocolId,
    displayName: OP_STACK_L1_BLOCK.displayName,
    docs: OP_STACK_L1_BLOCK.docs,
    category: "precompile",
    icon: Milestone
  },
  [SEAPORT.protocolId]: {
    id: SEAPORT.protocolId,
    displayName: SEAPORT.displayName,
    docs: SEAPORT.docs,
    category: "nft",
    icon: Ship
  },
  [CONDITIONAL_TOKENS.protocolId]: {
    id: CONDITIONAL_TOKENS.protocolId,
    displayName: CONDITIONAL_TOKENS.displayName,
    docs: CONDITIONAL_TOKENS.docs,
    category: "derivatives",
    icon: Scale
  },
  // The venue, beside the standard it trades. A different glyph on purpose:
  // the two appear on the SAME transaction constantly — a settlement that
  // splits collateral to fill an order carries both — and one scale icon over
  // both would leave a reader unable to tell the mint from the trade.
  [POLYMARKET_CTF_EXCHANGE.protocolId]: {
    id: POLYMARKET_CTF_EXCHANGE.protocolId,
    displayName: POLYMARKET_CTF_EXCHANGE.displayName,
    docs: POLYMARKET_CTF_EXCHANGE.docs,
    category: "derivatives",
    icon: CandlestickChart
  }
};
var DEPLOYER_BRANDS = Object.fromEntries(
  DEPLOYER_BRAND_LIST.map((brand) => [brand.id, brand])
);
var PROTOCOL_BRANDS = {
  ...Object.fromEntries(Object.keys(catalog_generated_default).flatMap((id) => {
    const brand = cataloguedBrand(id);
    return brand ? [[id, brand]] : [];
  })),
  ...DECODE_PROTOCOL_BRANDS,
  ...DEPLOYER_BRANDS,
  ...STANDARD_BRANDS
};
var NEUTRAL_BRAND = {
  id: "neutral",
  displayName: "Protocol",
  category: "neutral"
};
function brandForProtocol(protocolId) {
  if (!protocolId) return NEUTRAL_BRAND;
  return ownBrand(PROTOCOL_BRANDS, protocolId) ?? cataloguedBrand(protocolId) ?? NEUTRAL_BRAND;
}
function ownBrand(brands, protocolId) {
  if (!brands || !protocolId) return void 0;
  return Object.prototype.hasOwnProperty.call(brands, protocolId) ? brands[protocolId] : void 0;
}
function protocolName(protocolId) {
  if (!protocolId) return void 0;
  const brand = brandForProtocol(protocolId);
  return brand !== NEUTRAL_BRAND ? brand.displayName : protocolDisplayName(protocolId);
}
function brandForKind(kind) {
  if (!kind) return NEUTRAL_BRAND;
  const dot = kind.indexOf(".");
  if (dot <= 0) return NEUTRAL_BRAND;
  return brandForProtocol(kind.slice(0, dot));
}
function brandForGraphLabel(label) {
  if (!label) return void 0;
  const needle = label.trim().toLowerCase();
  if (!needle) return void 0;
  for (const brand of Object.values(PROTOCOL_BRANDS)) {
    if (brand.displayName.toLowerCase() === needle) return brand;
    if (brand.graphAliases?.some((alias) => alias.toLowerCase() === needle)) return brand;
  }
  return void 0;
}
function accentClasses(brand) {
  return ACCENTS[brand.accent ?? CATEGORY_ACCENT[brand.category]] ?? ACCENTS[CATEGORY_ACCENT[brand.category]] ?? ACCENTS.neutral;
}
function accentForProtocol(protocolId) {
  return accentClasses(brandForProtocol(protocolId));
}
function brandsFromProtocolSummaries(rows) {
  return Object.fromEntries(rows.map((row) => {
    const base = brandForProtocol(row.protocol_id);
    const category = CATALOG_BRAND_CATEGORY[row.category] ?? base.category;
    return [row.protocol_id, {
      ...base,
      id: row.protocol_id,
      displayName: row.display_name || base.displayName,
      category,
      icon: base.icon ?? CATEGORY_ICON[category],
      logoUrl: publicAssetUrl(row.icon?.cdn_key) ?? void 0
    }];
  }));
}
var BRAND_ACCENT_NAMES = Object.freeze(Object.keys(ACCENTS));
var BRAND_LABEL_MAX = 40;
function isEmptyBrandOverride(entry) {
  if (!entry) return true;
  const { perChain, ...base } = entry;
  const blank = (o) => o.accent === void 0 && o.label === void 0 && o.hidden === void 0;
  return blank(base) && Object.values(perChain ?? {}).every(blank);
}
function parseOverride(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return {};
  const raw = value;
  const out = {};
  if (typeof raw.accent === "string" && Object.prototype.hasOwnProperty.call(ACCENTS, raw.accent)) {
    out.accent = raw.accent;
  }
  if (typeof raw.label === "string" && raw.label.trim()) out.label = raw.label.trim().slice(0, BRAND_LABEL_MAX);
  if (typeof raw.hidden === "boolean") out.hidden = raw.hidden;
  return out;
}
function parseBrandOverrides(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return {};
  const out = {};
  for (const [protocolId, raw] of Object.entries(value)) {
    const entry = parseOverride(raw);
    const chains = raw?.perChain;
    if (typeof chains === "object" && chains !== null && !Array.isArray(chains)) {
      const perChain = {};
      for (const [chain, chainRaw] of Object.entries(chains)) {
        const parsed = parseOverride(chainRaw);
        if (!isEmptyBrandOverride(parsed)) perChain[chain] = parsed;
      }
      if (Object.keys(perChain).length > 0) entry.perChain = perChain;
    }
    if (!isEmptyBrandOverride(entry)) out[protocolId] = entry;
  }
  return out;
}
function overrideFor(map, protocolId, chainId) {
  const has = (record, key) => record != null && Object.prototype.hasOwnProperty.call(record, key);
  const entry = has(map, protocolId) ? map[protocolId] : void 0;
  if (!entry) return void 0;
  const { perChain, ...base } = entry;
  const chain = chainId && has(perChain, chainId) ? perChain[chainId] : void 0;
  return chain ? { ...base, ...chain } : base;
}
function resolveBrand(base, layers, chainId, protocolId = base.id) {
  const tenant = overrideFor(layers.tenant, protocolId, chainId);
  const user = overrideFor(layers.user, protocolId, chainId);
  if (!tenant && !user) return base;
  const accent = resolveScalar({
    user: user?.accent,
    tenant: tenant?.accent,
    default: base.accent
  });
  const label = resolveScalar({ user: user?.label, tenant: tenant?.label });
  const hidden = resolveScalar({ user: user?.hidden, tenant: tenant?.hidden });
  return { ...base, accent, displayName: label ?? base.displayName, hidden, ...label ? { relabelled: true } : {} };
}
var BrandContext = React.createContext({});
function BrandProvider({
  tenant,
  user,
  registry,
  chain,
  children
}) {
  const parent = React.useContext(BrandContext);
  const effectiveRegistry = registry ?? parent.registry;
  const value = React.useMemo(
    () => ({ tenant, user, chain, registry: effectiveRegistry }),
    [tenant, user, chain, effectiveRegistry]
  );
  return /* @__PURE__ */ jsx(BrandContext.Provider, { value, children });
}
function useBrandOverrides() {
  return React.useContext(BrandContext);
}
function useBrand(protocolId, chainId) {
  const { tenant, user, chain, registry } = React.useContext(BrandContext);
  const effectiveChain = chainId ?? chain;
  return React.useMemo(
    () => resolveBrand(ownBrand(registry, protocolId) ?? brandForProtocol(protocolId), { tenant, user }, effectiveChain, protocolId),
    [protocolId, tenant, user, effectiveChain, registry]
  );
}
function useBrandAccent(protocolId, chainId) {
  return accentClasses(useBrand(protocolId, chainId));
}

// src/theme/brand/storage.ts
var BRAND_OVERRIDES_KEY = "brand:overrides";
var BrandPrefsStore = class _BrandPrefsStore {
  constructor(storage) {
    this.storage = storage;
  }
  storage;
  /** Derive a store for a different identity (e.g. on tenant/user switch). */
  with(patch) {
    return new _BrandPrefsStore(this.storage.with(patch));
  }
  /**
   * Hierarchical read of the override map (most specific scope wins), checked:
   * storage is cast, not typed, so what comes back is run through
   * {@link parseBrandOverrides} before anything resolves a brand from it.
   */
  getOverrides() {
    return parseBrandOverrides(this.storage.get(BRAND_OVERRIDES_KEY, {}));
  }
  /** Re-read when another tab changes the stored map. Returns the unsubscribe. */
  subscribe(onChange) {
    return this.storage.subscribe(BRAND_OVERRIDES_KEY, onChange);
  }
  /**
   * Set/replace one protocol's override at `level` (default: the storage's).
   * An override that changes nothing removes the entry instead of storing an
   * empty one. `false` when the store refused the write (quota, private mode) —
   * the caller must not show a change that will be gone on reload.
   */
  setOverride(protocolId, override, level = this.storage.defaultLevel) {
    const [entry] = Object.values(parseBrandOverrides({ [protocolId]: override }));
    if (isEmptyBrandOverride(entry)) return this.removeOverride(protocolId, level);
    const current = parseBrandOverrides(this.storage.getAt(BRAND_OVERRIDES_KEY, level, {}));
    return this.storage.set(BRAND_OVERRIDES_KEY, { ...current, [protocolId]: entry }, level);
  }
  /** Remove one protocol's override at `level`. `false` when the store refused. */
  removeOverride(protocolId, level = this.storage.defaultLevel) {
    const current = parseBrandOverrides(this.storage.getAt(BRAND_OVERRIDES_KEY, level, {}));
    if (!Object.prototype.hasOwnProperty.call(current, protocolId)) return true;
    const { [protocolId]: _removed, ...rest } = current;
    return this.storage.set(BRAND_OVERRIDES_KEY, rest, level);
  }
};
function createBrandPrefsStore(storage) {
  return new BrandPrefsStore(storage);
}
var CardFooterContext = React.createContext(null);
function CardFooterProvider({
  footer,
  children
}) {
  return /* @__PURE__ */ jsx(CardFooterContext.Provider, { value: footer, children });
}
function useCardFooter() {
  return React.useContext(CardFooterContext);
}
function SectionCard({
  title,
  meta,
  protocolId,
  children,
  className
}) {
  const accent = useBrandAccent(protocolId);
  const footer = useCardFooter();
  return /* @__PURE__ */ jsx(CardFooterProvider, { footer: null, children: /* @__PURE__ */ jsxs("div", { className: cn(PROTOCOL_CARD, protocolId && ["border-l-2", accent.bar], className), children: [
    /* @__PURE__ */ jsxs("div", { className: PROTOCOL_CARD_HEADER, children: [
      title,
      meta != null && /* @__PURE__ */ jsx("div", { className: "ml-auto flex items-center gap-2", children: meta })
    ] }),
    /* @__PURE__ */ jsx("div", { className: PROTOCOL_CARD_BODY, children }),
    footer != null && /* @__PURE__ */ jsx("div", { className: cn(PROTOCOL_CARD_BODY, "border-t border-border/60"), children: footer })
  ] }) });
}
var MAX_FIELD_DEPTH = 5;
function legField(leg, ...names) {
  const rec = decodedOrRaw(leg);
  if (!isRecord(rec)) return void 0;
  for (const name of names) {
    const target = name.toLowerCase();
    for (const [key, value] of Object.entries(rec)) {
      if (key.toLowerCase() === target) return value;
    }
  }
  return void 0;
}
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function normName(name) {
  return name.toLowerCase().replace(/^_+|_+$/g, "");
}
function fieldAtPath(params, ...paths) {
  for (const path of paths) {
    const [head, ...rest] = path.split(".");
    let value = params.find((p) => normName(p.name ?? "") === normName(head))?.value;
    for (const part of rest) value = legField(value, part);
    if (value != null) return value;
  }
  return void 0;
}
function decodedOrRaw(value) {
  return decodedOf(value) ?? rawOf(value);
}
function container(value) {
  const v = decodedOrRaw(value);
  if (Array.isArray(v)) return v;
  if (isRecord(v)) return v;
  return void 0;
}
function findInValue(value, target, depth) {
  if (kindOf(value) === "call") return void 0;
  const c = container(value);
  if (c === void 0) return void 0;
  if (!Array.isArray(c)) {
    for (const [k, v] of Object.entries(c)) {
      if (normName(k) === target) return v;
    }
    if (depth >= MAX_FIELD_DEPTH) return void 0;
    for (const v of Object.values(c)) {
      const found = findInValue(v, target, depth + 1);
      if (found !== void 0) return found;
    }
    return void 0;
  }
  if (depth >= MAX_FIELD_DEPTH) return void 0;
  for (const v of c) {
    const found = findInValue(v, target, depth + 1);
    if (found !== void 0) return found;
  }
  return void 0;
}
function pickField(params, ...names) {
  const targets = names.map(normName);
  for (const target of targets) {
    for (const p of params) {
      if (p.name && normName(p.name) === target) return p.value;
    }
  }
  for (const target of targets) {
    for (const p of params) {
      const found = findInValue(p.value, target, 0);
      if (found !== void 0) return found;
    }
  }
  return void 0;
}
function pickSemantic(params, role, ...fallbackAliases) {
  for (const p of params) {
    if (p.canonicalName && p.canonicalName.toLowerCase() === role.toLowerCase()) return p.value;
  }
  return pickField(params, role, ...fallbackAliases);
}
function pickFrom(value, ...names) {
  for (const name of names) {
    const found = findInValue(value, normName(name), 0);
    if (found !== void 0) return found;
  }
  return void 0;
}
function hasBytesPayload(value) {
  const v = isEnriched(value) ? rawOf(value) : value;
  if (typeof v !== "string") return v != null;
  const hex = v.startsWith("0x") ? v.slice(2) : v;
  return hex.length > 0;
}
var BADGE_BASE = "inline-flex items-center gap-1 rounded-sm px-1.5 py-px text-2xs font-medium";
function BrandGlyph({ logoUrl, icon: Icon, className }) {
  const [failed, setFailed] = React.useState();
  if (logoUrl && failed !== logoUrl) {
    return /* @__PURE__ */ jsx("img", { src: logoUrl, alt: "", className: cn("rounded-full object-cover", className), onError: () => setFailed(logoUrl) });
  }
  return Icon ? /* @__PURE__ */ jsx(Icon, { className, "aria-hidden": true }) : null;
}
function ProtocolBadge({
  protocolId,
  label,
  hideIcon,
  className
}) {
  const brand = useBrand(protocolId);
  if (brand.hidden && label == null) return null;
  const accent = accentClasses(brand);
  const Icon = brand.icon;
  const text = label ?? // An unbranded protocol reads as its catalogue name — unless somebody has
  // given it one, which is the only name on the neutral brand worth showing.
  (protocolId && brand.id === "neutral" && !brand.relabelled ? protocolName(protocolId) ?? humanizeFieldName(protocolId) : brand.displayName);
  return /* @__PURE__ */ jsxs("span", { className: cn(BADGE_BASE, accent.badge, className), children: [
    !hideIcon ? /* @__PURE__ */ jsx(BrandGlyph, { logoUrl: brand.logoUrl, icon: Icon, className: "size-3 shrink-0" }) : null,
    text
  ] });
}
function isFactVisible(f) {
  return "when" in f ? Boolean(f.when) : f.value != null && f.value !== "";
}
function factKeys(facts) {
  const seen = /* @__PURE__ */ new Map();
  return facts.map((f) => {
    const n = seen.get(f.label) ?? 0;
    seen.set(f.label, n + 1);
    return n === 0 ? f.label : `${f.label}\0${n}`;
  });
}
function FactGrid({ facts, className }) {
  const shown = facts.filter(isFactVisible);
  const keys = factKeys(shown);
  return /* @__PURE__ */ jsx(
    "dl",
    {
      className: ["grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[max-content_1fr]", className].filter(Boolean).join(" "),
      children: shown.map((f, i) => /* @__PURE__ */ jsxs(React.Fragment, { children: [
        /* @__PURE__ */ jsx("dt", { className: FACT_LABEL, children: f.label }),
        /* @__PURE__ */ jsx("dd", { className: FACT_VALUE, children: f.value })
      ] }, keys[i]))
    }
  );
}
function defaultHintsForChain(defaults, chain) {
  const abiDecoded = chainDecodeVocabulary(chain).schemaRegistry;
  if (abiDecoded) return defaults;
  const scoped = (hints) => hints.filter((h) => h.protocolId);
  return {
    valueHints: scoped(defaults.valueHints),
    eventHints: scoped(defaults.eventHints),
    callHints: scoped(defaults.callHints)
  };
}
function valueHintForKind(hints, kind) {
  return valueHintForKindScoped(hints, kind, kindProtocolId(kind));
}
function kindProtocolId(kind) {
  return kind?.includes(".") ? kind.split(".", 1)[0] || void 0 : void 0;
}
function valueHintForKindScoped(hints, kind, protocolId) {
  if (!hints || !kind) return void 0;
  const ofKind = hints.valueHints.filter((h) => h.kind === kind);
  const owner = protocolId ?? kindProtocolId(kind);
  const scoped = owner ? ofKind.find(
    (h) => h.protocolId && h.protocolId.toLowerCase() === owner.toLowerCase()
  ) : void 0;
  return scoped ?? ofKind.find((h) => !h.protocolId);
}
function eventHintFor(hints, protocolIds, event) {
  if (!hints || !event.name) return void 0;
  const scopedIds = event.protocolId ? [event.protocolId] : protocolIds;
  const present = new Set(scopedIds.map((p) => p.toLowerCase()));
  const candidates = hints.eventHints.filter((h) => {
    if (h.component === "ChainlinkEvent" && (!event.protocolId || !event.signature)) return false;
    if (h.event !== event.name) return false;
    if (h.signature && event.signature && h.signature !== event.signature) return false;
    if (h.protocolId && !present.has(h.protocolId.toLowerCase())) return false;
    return true;
  });
  return preferHint(candidates, event.signature, scopedIds, (h) => h.signature, (h) => h.protocolId);
}
function callHintFor(hints, protocolIds, call) {
  if (!hints || !call.name) return void 0;
  if (!call.protocolId && call.name === "transmit" && protocolIds.filter((p) => p.startsWith("chainlink_")).length > 1) return void 0;
  const scopedIds = call.protocolId ? [call.protocolId] : protocolIds;
  const present = new Set(scopedIds.map((p) => p.toLowerCase()));
  const candidates = hints.callHints.filter((h) => {
    if (h.component === "ChainlinkCall" && (!call.protocolId || !call.signature)) return false;
    if (h.call !== call.name) return false;
    if (h.signature && call.signature && h.signature !== call.signature) return false;
    if (h.protocolId && !present.has(h.protocolId.toLowerCase())) return false;
    return true;
  });
  return preferHint(candidates, call.signature, scopedIds, (h) => h.signature, (h) => h.protocolId);
}
function preferHint(candidates, signature, protocolIds, signatureOf, protocolOf) {
  if (candidates.length <= 1) return candidates[0];
  const exact = signature ? candidates.filter((h) => signatureOf(h) === signature) : [];
  const nameOnly = candidates.filter((h) => !signatureOf(h));
  const narrowed = exact.length > 0 ? exact : nameOnly.length > 0 ? nameOnly : candidates;
  const chainlink = new Set(narrowed.map(protocolOf).filter((p) => p?.startsWith("chainlink_")));
  if (chainlink.size > 1) return void 0;
  const [first, ...rest] = narrowed;
  if (first === void 0) return void 0;
  if (rest.length === 0) return first;
  const rank = protocolIds.map((p) => p.toLowerCase());
  const precedence = (h) => {
    const pid = protocolOf(h);
    if (!pid) return Number.MAX_SAFE_INTEGER;
    const at = rank.indexOf(pid.toLowerCase());
    return at === -1 ? Number.MAX_SAFE_INTEGER : at;
  };
  return rest.reduce((best, h) => precedence(h) < precedence(best) ? h : best, first);
}
function resetKeysChanged(previous, next) {
  const a = previous ?? [];
  const b = next ?? [];
  return a.length !== b.length || a.some((item, index) => !Object.is(item, b[index]));
}
var ProtocolErrorBoundary = class extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidUpdate(previous) {
    if (this.state.failed && resetKeysChanged(previous.resetKeys, this.props.resetKeys)) {
      this.setState({ failed: false });
    }
  }
  render() {
    if (this.state.failed) return this.props.fallback;
    return this.props.children;
  }
};
function ProtocolBoundary({ fallback, pending, resetKeys, children }) {
  return /* @__PURE__ */ jsx(ProtocolErrorBoundary, { fallback, resetKeys, children: /* @__PURE__ */ jsx(React.Suspense, { fallback: pending ?? fallback, children }) });
}
function ProtocolDecodedCall({ call, className }) {
  return /* @__PURE__ */ jsx(
    ReadConfidenceBoundary,
    {
      score: call.confidence,
      evidence: call.evidence,
      explanation: call.explanation,
      subject: "Decoded call",
      children: /* @__PURE__ */ jsx(ProtocolCallContent, { call, className })
    }
  );
}
function ProtocolCallContent({ call, className, identity }) {
  const { registry, hints, protocolIds, chain, renderAddress, onAddressDrilldown } = useProtocolUi();
  const renderKind = useDecodedKindRenderer();
  const generic = /* @__PURE__ */ jsx(
    DecodedCall,
    {
      label: call.label,
      signature: call.signature,
      address: call.address,
      source: call.source,
      abiScope: call.abiScope,
      params: call.params,
      returnParams: call.returnParams,
      proxyChain: call.proxyChain,
      chain,
      renderAddress,
      onAddressDrilldown,
      renderKind,
      className
    }
  );
  const hint = callHintFor(hints, protocolIds, { name: call.label, signature: call.signature, protocolId: call.protocolId });
  const Component2 = registry.callComponent(hint?.component);
  const wrap = (content) => /* @__PURE__ */ jsxs(ProtocolArtifactScope, { protocolId: call.protocolId ?? hint?.protocolId, children: [
    content,
    /* @__PURE__ */ jsx(ClaimEvidence, { claims: call.claims, economicReconciliation: call.economicReconciliation })
  ] });
  if (!hint || !Component2) return wrap(generic);
  const showReturns = !registry.callOwnsReturns(hint.component) && call.returnParams.length > 0;
  const footer = showReturns ? /* @__PURE__ */ jsx(ProtocolReturns, { returnParams: call.returnParams }) : null;
  return wrap(
    /* @__PURE__ */ jsx(ProtocolBoundary, { fallback: generic, resetKeys: [identity ?? call, Component2], children: /* @__PURE__ */ jsx(CardFooterProvider, { footer, children: /* @__PURE__ */ jsx(
      Component2,
      {
        call,
        hint,
        chain,
        renderAddress,
        onAddressDrilldown
      }
    ) }) })
  );
}
var EMPTY_HINTS = { valueHints: [], eventHints: [], callHints: [] };
var DEFAULT_CONTEXT = {
  registry: createProtocolUiRegistry(),
  hints: EMPTY_HINTS,
  protocolIds: []
};
var ProtocolUiContext = React.createContext(DEFAULT_CONTEXT);
function mergeHints(body, fallback) {
  return {
    valueHints: [...body.valueHints, ...fallback.valueHints],
    eventHints: [...body.eventHints, ...fallback.eventHints],
    callHints: [...body.callHints, ...fallback.callHints]
  };
}
function ProtocolUiProvider({
  registry,
  hints,
  protocolIds,
  primaryProtocolId,
  chain,
  amountHidden,
  renderAddress,
  onAddressDrilldown,
  children
}) {
  const value = React.useMemo(
    () => ({
      registry,
      bodyHints: hints,
      hints: mergeHints(hints ?? EMPTY_HINTS, defaultHintsForChain(registry.defaultHints(), chain)),
      protocolIds: protocolIds ?? [],
      primaryProtocolId,
      chain,
      amountHidden,
      renderAddress,
      onAddressDrilldown
    }),
    [registry, hints, protocolIds, primaryProtocolId, chain, amountHidden, renderAddress, onAddressDrilldown]
  );
  return /* @__PURE__ */ jsx(ProtocolUiContext.Provider, { value, children });
}
function useProtocolUi() {
  return React.useContext(ProtocolUiContext);
}
function ProtocolArtifactScope({ protocolId, chain, children }) {
  const parent = React.useContext(ProtocolUiContext);
  const value = React.useMemo(() => ({
    ...parent,
    protocolIds: protocolId ? [protocolId] : [],
    primaryProtocolId: protocolId,
    chain: chain ?? parent.chain,
    hints: chain == null || chain === parent.chain ? parent.hints : mergeHints(parent.bodyHints ?? EMPTY_HINTS, defaultHintsForChain(parent.registry.defaultHints(), chain))
  }), [parent, protocolId, chain]);
  return /* @__PURE__ */ jsx(ProtocolUiContext.Provider, { value, children });
}
function useDecodedKindRenderer() {
  const { registry, hints, chain, renderAddress, onAddressDrilldown } = useProtocolUi();
  return React.useCallback(
    (info) => {
      const valueChain = info.chain ?? chain;
      if (info.kind === "call") {
        const call = parseDecodedCallValue(info.decoded);
        if (!call) return void 0;
        return /* @__PURE__ */ jsxs(ProtocolArtifactScope, { protocolId: call.protocolId, chain: valueChain, children: [
          /* @__PURE__ */ jsx(ProtocolCallContent, { call, identity: info.decoded }),
          /* @__PURE__ */ jsx(RawToggle, { raw: info.raw })
        ] });
      }
      const hint = valueHintForKind(hints, info.kind);
      const Component2 = registry.valueComponent(hint?.component, info.kind);
      if (!Component2) return void 0;
      return /* @__PURE__ */ jsx(ProtocolArtifactScope, { protocolId: kindProtocolId(info.kind), chain: valueChain, children: /* @__PURE__ */ jsx(
        ProtocolBoundary,
        {
          fallback: /* @__PURE__ */ jsx(DecodedValueFallback, { info, chain: valueChain }),
          resetKeys: [info.kind, info.raw, info.decoded, Component2],
          children: /* @__PURE__ */ jsx(
            Component2,
            {
              confidence: info.confidence,
              kind: info.kind,
              display: info.display,
              raw: info.raw,
              decoded: info.decoded,
              chain: valueChain,
              hint,
              renderAddress,
              onAddressDrilldown
            }
          )
        }
      ) });
    },
    [registry, hints, chain, renderAddress, onAddressDrilldown]
  );
}
function DecodedValueFallback({ info, chain }) {
  const { renderAddress, onAddressDrilldown } = useProtocolUi();
  const renderKind = useDecodedKindRenderer();
  const value = info.value ?? { "@decoded": { kind: info.kind, raw: info.raw, display: info.display, decoded: info.decoded } };
  return /* @__PURE__ */ jsx(
    DecodedValue,
    {
      value,
      chain,
      renderAddress,
      onAddressDrilldown,
      depth: info.depth,
      renderKind: (child) => child.value === value ? void 0 : renderKind(child)
    }
  );
}
function ProtocolParamList({
  params,
  showTypes,
  emptyLabel,
  className
}) {
  const { chain, renderAddress, onAddressDrilldown } = useProtocolUi();
  const renderKind = useDecodedKindRenderer();
  return /* @__PURE__ */ jsx(
    DecodedParamList,
    {
      params,
      chain,
      renderAddress,
      onAddressDrilldown,
      renderKind,
      showTypes,
      emptyLabel,
      className
    }
  );
}
function ProtocolReturns({ returnParams, className }) {
  if (!returnParams || returnParams.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { className, children: [
    /* @__PURE__ */ jsx("p", { className: "text-3xs font-medium uppercase tracking-wide text-muted-foreground", children: "Returns" }),
    /* @__PURE__ */ jsx("div", { className: "pt-1.5", children: /* @__PURE__ */ jsx(ProtocolParamList, { params: returnParams, emptyLabel: "No return value" }) })
  ] });
}
function ProtocolValue({ value, chain, type, className }) {
  const context = useProtocolUi();
  const { renderAddress, onAddressDrilldown } = context;
  const renderKind = useDecodedKindRenderer();
  return /* @__PURE__ */ jsx(
    DecodedValue,
    {
      value,
      type,
      chain: chain ?? context.chain,
      renderAddress,
      onAddressDrilldown,
      renderKind,
      className
    }
  );
}

export { ACCENTS, ACCENT_TEXT, ACCESS, AUTOMATION_COMPONENTS, AUTOMATION_FIELD_LABELS, BRAND_ACCENT_NAMES, BRAND_LABEL_MAX, BRAND_OVERRIDES_KEY, BrandGlyph, BrandPrefsStore, BrandProvider, CATALOG_BRAND_CATEGORY, CATEGORY_ACCENT, CATEGORY_ICON, CCIP_COMPONENTS, CCIP_CURSE_SUBJECT_KIND, CCIP_FIELD_LABELS, CCIP_MESSAGE_ID_FACT, CCIP_RECEIPT_ROLE_KIND, CCIP_RECEIPT_ROLE_LABELS, CCIP_TOKEN_TRANSFERS_KIND, CCIP_VERIFICATION_KIND, CHAINLINK_AUTOMATION, CHAINLINK_CCIP, CODE_HASH_PARAM, CONDITIONAL_TOKENS, CONDITIONAL_TOKENS_COMPONENTS, CONDITIONAL_TOKENS_EVENTS, CONDITIONAL_TOKENS_FIELDS, CONDITIONAL_TOKENS_LABELS, CONDITIONAL_TOKENS_VERBS, CONTRACT_CREATION_CALL, CONTRACT_TYPE_PARAM, COWSWAP, COWSWAP_CALLS, COWSWAP_COMPONENTS, COWSWAP_EVENTS, COWSWAP_FIELD_LABELS, COWSWAP_KINDS, CardFooterProvider, DEPLOYMENT, ERC1155, ERC20, ERC4337, ERC4337_CALLS, ERC4337_COMPONENTS, ERC4337_EVENTS, ERC4337_FIELDS, ERC4337_LABELS, ERC4626, ERC721, FACT_LABEL, FACT_VALUE, FEE_TIER_PARAM, FUNDED_WITH_PARAM, FactGrid, IDENTIFIED_BY, IDENTIFIED_BY_PARAM, INITIAL_SUPPLY_PARAM, INIT_CODE_PARAM, KYBERSWAP, KYBERSWAP_CALLS, KYBERSWAP_CLIENT_DATA_FIELDS, KYBERSWAP_COMPONENTS, KYBERSWAP_EVENTS, KYBERSWAP_FIELD_LABELS, KYBERSWAP_KINDS, LAYERZERO_V2, LAYERZERO_V2_COMPONENTS, LAYERZERO_V2_EVENTS, LAYERZERO_V2_FIELDS, LAYERZERO_V2_LABELS, LIFI, LIFI_BRIDGE_CALLS, LIFI_COMPONENTS, LIFI_FIELD_LABELS, LOWLEVEL, LOWLEVEL_CALLS, LOWLEVEL_COMPONENT, MAYAN, MAYAN_COMPONENTS, MAYAN_EXECUTOR_CALLS, MAYAN_FIELD_LABELS, MAYAN_FORWARDER_CALLS, MAYAN_ORDER_EVENTS, MAYAN_PACKED_PAYLOAD_KIND, MAYAN_SWIFT_CALLS, MAYAN_SWIFT_MESSAGE_KIND, MINTED_TO_PARAM, NATIVE_TRANSFER, NEUTRAL_BRAND, OKX_COMPONENTS, OKX_FIELD_LABELS, OKX_PACKED_PAYLOAD_KIND, OKX_XBRIDGE, ONEINCH, ONEINCH_CALLS, ONEINCH_COMPONENTS, ONEINCH_EVENTS, ONEINCH_FIELDS, ONEINCH_KINDS, OWNERSHIP, OWNERSHIP_RENOUNCED_PARAM, OWNER_PARAM, PANCAKESWAP, PANCAKESWAP_CALLS, PANCAKESWAP_COMPONENTS, PANCAKESWAP_EVENTS, PANCAKESWAP_FIELDS, PANCAKESWAP_KINDS, PANCAKESWAP_V3_POOL, PANCAKESWAP_V3_POOL_COMPONENTS, PANCAKESWAP_V3_POOL_EVENTS, PANCAKESWAP_V3_POOL_FIELDS, PANCAKESWAP_V3_POOL_LABELS, POLYMARKET_COMPONENTS, POLYMARKET_CTF_EXCHANGE, POLYMARKET_EVENTS, POLYMARKET_FIELDS, POLYMARKET_LABELS, POLYMARKET_SIDE, POLYMARKET_SIDE_VERBS, PRECOMPILE, PRECOMPILES, PRECOMPILE_COMPONENT, PROTOCOL_BRANDS, PROTOCOL_CARD, PROTOCOL_CARD_BODY, PROTOCOL_CARD_HEADER, PROTOCOL_ID_PARAM, PROTOCOL_TAG, PROXY, ProtocolArtifactScope, ProtocolBadge, ProtocolBoundary, ProtocolDecodedCall, ProtocolParamList, ProtocolReturns, ProtocolUiProvider, ProtocolUiRegistry, ProtocolValue, RELAY, RELAY_COMPONENTS, RELAY_EVENTS, RELAY_FIELDS, RELAY_LABELS, STANDARD_BRANDS, SUSHISWAP, SUSHISWAP_CALLS, SUSHISWAP_COMPONENTS, SUSHISWAP_EVENTS, SUSHISWAP_KINDS, SectionCard, TICK_SPACING_PARAM, TOKEMAK_COMPONENTS, TOKEMAK_FIELD_LABELS, TOKEMAK_MESSAGING, TOKEN0_PARAM, TOKEN1_PARAM, UNISWAP_BRAND_LIST, UNISWAP_COMMAND_LABELS, UNISWAP_COMPONENTS, UNISWAP_FIELD_LABELS, UNISWAP_PERMIT2, UNISWAP_SWAP_COMMANDS, UNISWAP_UNIVERSAL_ROUTER, UNISWAP_V1_EXCHANGE, UNISWAP_V1_FACTORY, UNISWAP_V2_FACTORY, UNISWAP_V2_PAIR, UNISWAP_V2_ROUTER, UNISWAP_V3_FACTORY, UNISWAP_V3_POOL, UNISWAP_V3_POSITION_MANAGER, UNISWAP_V3_ROUTER, UNISWAP_V4_POOL_MANAGER, UNISWAP_V4_POSITION_MANAGER, VAULT_COMPONENTS, WORMHOLE, WORMHOLE_COMPONENTS, WORMHOLE_EXECUTOR_FIELD_LABELS, WORMHOLE_EXECUTOR_REQUEST_KIND, WORMHOLE_FIELD_LABELS, WORMHOLE_QUOTE_FIELD_LABELS, WORMHOLE_RELAY_INSTRUCTIONS_KIND, WORMHOLE_REQUEST_PREFIX_LABELS, WORMHOLE_SIGNED_QUOTE_KIND, WORMHOLE_TRANSFER_PAYLOAD_KIND, WORMHOLE_VAA_ID_FACT, WORMHOLE_VAA_KIND, WORMHOLE_VM_FIELD_LABELS, accentClasses, accentForProtocol, brandForGraphLabel, brandForKind, brandForProtocol, brandsFromProtocolSummaries, callHintFor, catalog_generated_default, cataloguedBrand, createBrandPrefsStore, createProtocolUiRegistry, decodedOrRaw, defaultHintsForChain, eventHintFor, fieldAtPath, hasBytesPayload, isEmptyBrandOverride, isFactVisible, isRecord, kindProtocolId, legField, parseBrandOverrides, pickField, pickFrom, pickSemantic, polymarketPrice, polymarketSide, protocolName, resolveBrand, useBrand, useBrandAccent, useBrandOverrides, useCardFooter, useDecodedKindRenderer, useProtocolUi, valueHintForKind, valueHintForKindScoped };
//# sourceMappingURL=chunk-PPWVGY7F.js.map
//# sourceMappingURL=chunk-PPWVGY7F.js.map